import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense } from 'react';
import { flushSync } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useVendorConfig } from '../context/VendorConfigContext';
import { printToLocalPrinter, InvoiceData } from '../services/localPrintService';
import { formatCurrency } from '../config/currency';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useConfirm } from '../context/ConfirmContext';
import { ORDER_EVENTS, VENDOR_EVENTS, TABLE_EVENTS, RESERVATION_EVENTS } from '../constants/socketEvents';
import FancyQrCodeModal from '../components/FancyQrCodeModal';
import api from '../api/api';
import {
  getFloorLayout,
  getFloorLayoutSummary,
  getFloorSections,
  createFloor,
  createSection,
  createTable,
  createTableWithQrCode,
  createVendorQrCode,
  assignQrCodeToTable,
  fetchQrCodeImage,
  updateFloor,
  updateSection,
  updateTable,
  updateTableDisplayNumber,
  logicalDeleteFloor,
  logicalDeleteSection,
  logicalDeleteTable,
  restoreFloor,
  restoreSection,
  restoreTable,
  permanentlyDeleteFloor,
  permanentlyDeleteSection,
  permanentlyDeleteTable,
  getSectionMenuAssignments,
  transferTableOrders,
  getFloorAssets,
  batchUpdateTablePositions,
  createFloorAsset,
  batchUpdateFloorAssetPositions
} from '../api/api';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  PlusCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  TableCellsIcon,
  QrCodeIcon,
  Cog6ToothIcon,
  BanknotesIcon,
  XMarkIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  EllipsisVerticalIcon,
  ScissorsIcon,
  LinkIcon,
  CalendarDaysIcon,
  ReceiptPercentIcon,
  NoSymbolIcon,
  LockOpenIcon,
} from '@heroicons/react/24/outline';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  Paper,
  Chip,
  Collapse,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Select,
  MenuItem,
  Menu,
  FormControl,
  InputLabel,
  styled,
  Backdrop,
  CircularProgress,
  Alert,
  AlertTitle,
  Switch,
  FormControlLabel,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import MapIcon from '@mui/icons-material/Map';
import AddIcon from '@mui/icons-material/Add';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import ImprovedVendorOrderFormModal from '../components/ImprovedVendorOrderFormModal';
import type { HeldOrder } from '../types/heldOrder';
import { getHeldOrders, deleteHeldOrder as deleteHeldOrderFromStore } from '../utils/heldOrderStore';
import InvoiceModal from '../components/InvoiceModal';
import TableOrderHistory from '../components/TableOrderHistory';
import StewardOrderModification from '../components/StewardOrderModification';
import TableTransferDialog from '../components/TableTransferDialog';
// TableMergeDialog removed — merge now uses floor-plan selection mode
import SplitBillDialog from '../components/SplitBillDialog';
import CloseButton from '../components/CloseButton';
import { AppIconButton, TintIcon } from '../components/ui';
import ReservationModal from '../components/reservations/ReservationModal';
import { ReservationProvider } from '../context/ReservationContext';
import { PauseIcon } from '@heroicons/react/24/outline';
import { CURRENCY } from '../config/currency';
import { todayLocalIso , toLocalIsoDate} from '../utils/dateLocal';
import { getPaymentMethods } from '../config/paymentMethods';

const LazyHeldOrdersPicker = React.lazy(() => import('../components/HeldOrdersPicker'));
const FloorPlanEditor = React.lazy(() => import('../components/floorplan/FloorPlanEditor'));

// Custom styled components for ambient design

interface TableReservation {
  id: number;
  reservationCode: string;
  customerName: string;
  customerPhone?: string;
  partySize: number;
  startTime: string;
  endTime: string;
  status: 'PENDING' | 'CONFIRMED' | 'SEATED';
  reservationDate: string;
  specialRequests?: string;
}

interface Table {
  id: string;
  name: string;
  sectionId: string;
  qrCode?: {
    id: string;
    code: string;
  };
  capacity?: number;
  shape?: 'square' | 'round' | 'rectangle';
  status?: 'available' | 'occupied' | 'reserved' | 'maintenance';
  isDeleted?: boolean;
  displayNumber?: string;
}

interface AssignedMenu {
  id: number;
  name: string;
  menuType?: string;
  isDefault?: boolean;
}

interface Section {
  id: string;
  name: string;
  floorId: string;
  tables: Table[];
  gridColumns?: number;
  gridRows?: number;
  isDeleted?: boolean;
  assignedMenus?: AssignedMenu[];
}

interface Floor {
  id: string;
  name: string;
  floorNumber?: number;
  vendorId: string;
  sections: Section[];
  isDeleted?: boolean;
}

// Floor summary for optimized initial load
interface FloorSummary {
  id: number;
  name: string;
  floorNumber: number;
  vendorId: number;
  isDeleted: boolean;
  sectionCount: number;
  tableCount: number;
  activeOrderCount: number;
}

// Floor sections data (loaded on demand)
interface FloorSectionsData {
  sections: Array<{
    section: Section;
    tables: Array<{
      table: Table;
      activeOrderCount?: number;
      activeOrders?: Array<{
        id: number;
        status: string;
        createdAt: string;
        total?: number;
        items?: Array<{
          id: number;
          quantity: number;
          price: number;
          menuItem?: {
            name: string;
          };
        }>;
      }>;
      isOccupied: boolean;
      status: string;
      reservations?: TableReservation[];
      nextReservation?: TableReservation;
    }>;
  }>;
}

interface FloorLayout {
  [key: string]: {
    floor: Floor;
    sections: Array<{
      section: Section;
      tables: Array<{
        table: Table;
        activeOrders: Array<{
          id: number;
          status: string;
          createdAt: string;
          total?: number;
          items?: Array<{
            id: number;
            quantity: number;
            price: number;
            menuItem?: {
              name: string;
            };
          }>;
        }>;
        isOccupied: boolean;
        status: string;
        reservations?: TableReservation[];
        nextReservation?: TableReservation;
      }>;
    }>;
  };
}

interface Visual2DLayoutManagerProps {
  onTableSelect?: (table: {
    id: string; name: string; displayNumber?: string;
    floorName?: string; sectionName?: string;
    qrCode?: string;
    activeOrders?: Array<{ id: number; status: string; total?: number; orderLineId?: string; customOrderId?: string; [key: string]: any }>;
  }) => void;
  selectedTableId?: string;
  embedded?: boolean;
  transferSource?: { id: string; name: string; displayNumber?: string; activeOrderCount?: number } | null;
  onTransferComplete?: () => void;
}

const Visual2DLayoutManager: React.FC<Visual2DLayoutManagerProps> = ({ onTableSelect, selectedTableId, embedded, transferSource, onTransferComplete }) => {
  const { user } = useAuth();
  const { theme, currentThemeName } = useTheme();
  const { configuration } = useVendorConfig();
  const isModernTheme = currentThemeName === 'modernDashboard';
  const navigate = useNavigate();
  const confirm = useConfirm();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isConnected, subscribeToVendor, unsubscribeFromVendor, on, off } = useSocket();

  // State for auto-opening table from query parameter
  const [autoOpenTableQrCode, setAutoOpenTableQrCode] = useState<string | null>(null);

  // Transfer mode state (when navigating from TableTransferDialog)
  const [transferMode, setTransferMode] = useState<{
    active: boolean;
    sourceTableId: string;
    sourceTableName: string;
    activeOrderCount: number;
  } | null>(null);

  // State for transfer instruction popup that fades away
  const [showTransferInstruction, setShowTransferInstruction] = useState(false);

  // Check for transfer mode from navigation state
  useEffect(() => {
    const state = location.state as {
      transferMode?: boolean;
      sourceTableId?: string;
      sourceTableName?: string;
      activeOrderCount?: number;
    } | null;

    if (state?.transferMode && state?.sourceTableId) {
      setTransferMode({
        active: true,
        sourceTableId: state.sourceTableId,
        sourceTableName: state.sourceTableName || 'Unknown',
        activeOrderCount: state.activeOrderCount || 0
      });
      // Show the instruction popup
      setShowTransferInstruction(true);
      // Clear the state from history to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Auto-hide transfer instruction popup after 4 seconds
  useEffect(() => {
    if (showTransferInstruction) {
      const timer = setTimeout(() => {
        setShowTransferInstruction(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showTransferInstruction]);

  // Sync transferSource prop (from EPOS) into internal transferMode state
  useEffect(() => {
    if (transferSource) {
      setTransferMode({
        active: true,
        sourceTableId: transferSource.id,
        sourceTableName: transferSource.displayNumber || transferSource.name,
        activeOrderCount: transferSource.activeOrderCount || 0
      });
      setShowTransferInstruction(true);
    } else if (transferSource === null) {
      // Explicitly cleared — only reset if it was prop-driven
      setTransferMode(prev => prev ? null : prev);
    }
  }, [transferSource]);

  // Derived theme tokens for consistent styling
  const surface = theme.components?.card?.background || '#FFFFFF';
  const borderColor = theme.components?.card?.border || '#e5e7eb';
  const textPrimary = theme.text;
  const textSecondary = theme.gray?.[600] || '#6b7280';
  const primary = theme.primary;
  const primaryLight = theme.primaryLight || `${primary}15`;
  const primaryHover = theme.primaryHover || primary;
  const success = theme.success;
  const danger = theme.danger;
  const warning = theme.warning;
  const neutral = theme.gray?.[500] || '#64748B';
  const neutralLight = theme.gray?.[100] || '#F3F4F6';
  const background = theme.background || '#ffffff';

  // Check if user is a steward (read-only access)
  // Vendors should have full edit permissions, not be treated as stewards
  const isSteward = user?.roles?.includes('ROLE_STEWARD');
  const isVendor = user?.roles?.includes('ROLE_VENDOR');

  // Optimized lazy loading state
  const [floorSummaries, setFloorSummaries] = useState<FloorSummary[]>([]);
  const [cachedFloorData, setCachedFloorData] = useState<Map<string, FloorSectionsData>>(new Map());
  const [loadingFloors, setLoadingFloors] = useState<Set<string>>(new Set());
  const [menuAssignments, setMenuAssignments] = useState<Record<number, AssignedMenu | null>>({});
  const [floorDataVersion, setFloorDataVersion] = useState(0); // Triggers re-render when floor data is cached
  const [floorAssets, setFloorAssets] = useState<any[]>([]);

  // Legacy state for backward compatibility (still used by some functions)
  const [floorLayout, setFloorLayout] = useState<FloorLayout>({});
  const [isManagementMode, setIsManagementMode] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'floorplan'>('grid');
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedFloors, setExpandedFloors] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Section quick-navigation state
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const sectionChipsContainerRef = useRef<HTMLDivElement>(null);

  const [selectedTable, setSelectedTable] = useState<{
    table: Table;
    activeOrders?: Array<{
      id: number;
      status: string;
      createdAt: string;
      total?: number;
      /** The bill this order belongs to. Returned by the API; declared here so
       *  the offer check can be run before a total is shown. */
      orderLineId?: number;
      items?: Array<{
        id: number;
        quantity: number;
        price: number;
        menuItem?: {
          name: string;
        };
      }>;
    }>;
    completedOrders?: Array<{
      id: number;
      status: string;
      createdAt: string;
      total?: number;
      items?: Array<{
        id: number;
        quantity: number;
        price: number;
        menuItem?: {
          name: string;
        };
      }>;
    }>;
    isOccupied: boolean;
    status: string;
    activeOrderCount?: number;
    floorName?: string;
    sectionName?: string;
    reservations?: TableReservation[];
    nextReservation?: TableReservation;
  } | null>(null);
  // Remember which table details to restore after closing invoice
  const [pendingReturnTable, setPendingReturnTable] = useState<{
    table: Table;
    activeOrders?: Array<{
      id: number;
      status: string;
      createdAt: string;
      total?: number;
      items?: Array<{
        id: number;
        quantity: number;
        price: number;
        menuItem?: {
          name: string;
        };
      }>;
    }>;
    completedOrders?: Array<{
      id: number;
      status: string;
      createdAt: string;
      total?: number;
      items?: Array<{
        id: number;
        quantity: number;
        price: number;
        menuItem?: {
          name: string;
        };
      }>;
    }>;
    isOccupied: boolean;
    status: string;
  } | null>(null);

  // Dialog states
  const [openFloorDialog, setOpenFloorDialog] = useState(false);
  const [openSectionDialog, setOpenSectionDialog] = useState(false);
  const [openTableDialog, setOpenTableDialog] = useState(false);
  const [openQrDialog, setOpenQrDialog] = useState(false);

  // Edit/Delete dialog states
  const [openEditFloorDialog, setOpenEditFloorDialog] = useState(false);
  const [openEditSectionDialog, setOpenEditSectionDialog] = useState(false);
  const [openEditTableDialog, setOpenEditTableDialog] = useState(false);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);

  // Edit/Delete entity states
  const [editingFloor, setEditingFloor] = useState<{ id: string, name: string, floorNumber?: number } | null>(null);
  const [editingSection, setEditingSection] = useState<{ id: string, name: string, floorId: string } | null>(null);
  const [editingTable, setEditingTable] = useState<{ id: string, name: string, sectionId: string, displayNumber?: string } | null>(null);
  const [deletingEntity, setDeletingEntity] = useState<{
    type: 'floor' | 'section' | 'table';
    id: string;
    name: string;
    hasPendingOrders?: boolean;
  } | null>(null);

  // Delete confirmation states
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showCascadeOption, setShowCascadeOption] = useState(false);

  // Restore confirmation dialog states
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [restoringEntity, setRestoringEntity] = useState<{
    type: 'floor' | 'section' | 'table';
    id: string;
    name: string;
  } | null>(null);

  // Permanent delete confirmation dialog states
  const [openPermanentDeleteDialog, setOpenPermanentDeleteDialog] = useState(false);
  const [permanentDeletingEntity, setPermanentDeletingEntity] = useState<{
    type: 'floor' | 'section' | 'table';
    id: string;
    name: string;
  } | null>(null);
  const [permanentDeleteConfirmText, setPermanentDeleteConfirmText] = useState('');

  // UI-only Section Settings dialog (no backend change)
  const [openSectionSettings, setOpenSectionSettings] = useState(false);
  const [selectedSectionForSettings, setSelectedSectionForSettings] = useState<{
    id: string;
    name: string;
    gridColumns?: number;
    tablesCount?: number;
    ordersCount?: number;
  } | null>(null);
  // Read-only info panel (no editing state)
  const [selectedFloorForSection, setSelectedFloorForSection] = useState<string>('');
  const [selectedSectionForTable, setSelectedSectionForTable] = useState<string>('');
  const [selectedFloorIdForTable, setSelectedFloorIdForTable] = useState<string>('');
  const [selectedSectionNameForTable, setSelectedSectionNameForTable] = useState<string>('');
  const [selectedTableForQr, setSelectedTableForQr] = useState<Table | null>(null);

  // QR Code states
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [isLoadingQrImage, setIsLoadingQrImage] = useState(false);
  const [showQrInDetails, setShowQrInDetails] = useState(false);
  const [showTableInfo, setShowTableInfo] = useState(false);
  const [showQrMissingDialog, setShowQrMissingDialog] = useState(false);
  const [showFancyQrModal, setShowFancyQrModal] = useState(false);
  const [missingQrDetails, setMissingQrDetails] = useState<{
    code: string;
    table: Table | null;
  } | null>(null);

  // Form states
  const [floorName, setFloorName] = useState('');
  const [floorNumber, setFloorNumber] = useState<number | string>('');
  const [sectionName, setSectionName] = useState('');
  const [sectionColumns, setSectionColumns] = useState(4);
  const [sectionRows, setSectionRows] = useState(4);
  // number | '' so the field can be emptied while typing, exactly like
  // tablesToCreate below. Holding a bare number forced the onChange to invent a
  // value on every keystroke, which is what made Capacity untypeable.
  const [tableCapacity, setTableCapacity] = useState<number | string>(4);
  const [tableShape, setTableShape] = useState<'square' | 'round' | 'rectangle'>('square');
  const [tablesToCreate, setTablesToCreate] = useState<number | string>('');
  const [tablePrefix, setTablePrefix] = useState('T');
  const [tableName, setTableName] = useState('');
  const [tableDisplayNumber, setTableDisplayNumber] = useState('');
  const [qrCodeName, setQrCodeName] = useState('');
  const [qrCodeData, setQrCodeData] = useState('');

  // Order form modal state
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderTableId, setOrderTableId] = useState<string | null>(null);
  const [orderTableName, setOrderTableName] = useState<string | null>(null);
  const [tableDataForOrder, setTableDataForOrder] = useState<any>(null); // Store full table data when opening order form

  // Held orders state
  const [heldOrders, setHeldOrders] = useState<HeldOrder[]>([]);
  const [showHeldOrdersPicker, setShowHeldOrdersPicker] = useState(false);
  const [resumeHeldOrder, setResumeHeldOrder] = useState<HeldOrder | null>(null);

  const refreshHeldOrders = useCallback(() => {
    if (user?.id) {
      setHeldOrders(getHeldOrders(user.id));
    }
  }, [user?.id]);

  // Load held orders on mount
  useEffect(() => {
    refreshHeldOrders();
  }, [refreshHeldOrders]);

  // Build a set of tableIds that have held orders for badge rendering
  const heldOrderTableIds = useMemo(() => {
    const ids = new Set<number>();
    heldOrders.forEach(o => { if (o.tableId !== null) ids.add(o.tableId); });
    return ids;
  }, [heldOrders]);

  // Quick reservation from floor plan
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationTableId, setReservationTableId] = useState<number | undefined>(undefined);

  // Reservation mode — floor plan table selection (like transfer/merge).
  // editingReservationId is set when staff opens the banner via ?editReservation=
  // so submit calls PATCH instead of POST.
  const [reservationMode, setReservationMode] = useState<{
    active: boolean;
    editingReservationId?: number;
    selectedTableIds: Set<number>;
    availableTableIds: Set<number>;
    needsMerge: boolean;
    customerName: string;
    customerPhone: string;
    customerEmail: string;
    partySize: number;
    date: string;
    startTime: string;
    durationMinutes: number;
    specialRequests: string;
    source: string;
  } | null>(null);
  const [reservationAvailableTables, setReservationAvailableTables] = useState<any[]>([]);
  const [reservationSuggestions, setReservationSuggestions] = useState<any[]>([]);
  const [reservationSubmitting, setReservationSubmitting] = useState(false);
  const [reservationCheckLoading, setReservationCheckLoading] = useState(false);
  const [reservationSuccess, setReservationSuccess] = useState<null | {
    updated: boolean; customerName: string; partySize: number; tables: string; date: string; startTime: string;
  }>(null);
  const [reservationSettings, setReservationSettings] = useState<{ maxPartySize: number; maxAdvanceBookingDays: number; openingTime: string; lastReservationTime: string; minAdvanceBookingHours: number } | null>(null);
  const [reservationOccupiedConfirm, setReservationOccupiedConfirm] = useState<{ tableId: number; tableName: string } | null>(null);

  // Waitlist seat mode — staff picks a free table from the live floor plan to
  // notify or seat a walk-in. Same UX shape as transferMode/reservationMode.
  const [waitlistMode, setWaitlistMode] = useState<{
    active: boolean;
    waitlistId: number;
    customerName: string;
    partySize: number;
    action: 'notify' | 'seat';
  } | null>(null);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);

  // Invoice/Settlement states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedOrderForInvoice, setSelectedOrderForInvoice] = useState<number | null>(null);

  // Order tabs state

  // Steward order modification states
  const [selectedOrderForModification, setSelectedOrderForModification] = useState<number | null>(null);
  const [showOrderModification, setShowOrderModification] = useState(false);
  const [cancellingOrderId, setCancellingOrderId] = useState<number | null>(null);
  const [servingOrderId, setServingOrderId] = useState<number | null>(null);
  const [orderHistoryRefreshTrigger, setOrderHistoryRefreshTrigger] = useState<number>(0); // Trigger for TableOrderHistory refresh
  // Confirmation dialog for cancel/serve actions
  const [confirmOrderAction, setConfirmOrderAction] = useState<{ open: boolean; type: 'cancel' | 'serve' | null; orderId: number | null }>({ open: false, type: null, orderId: null });

  // Steward floor filter selection (null => all floors)
  const [selectedFloorKey, setSelectedFloorKey] = useState<string | null>(null);

  const isMobile = window.innerWidth < 600;

  // Global loading for settle flow
  const [isSettlingBill, setIsSettlingBill] = useState<boolean>(false);
  const [isLoadingBill, setIsLoadingBill] = useState<boolean>(false);

  // Settle confirmation dialog state
  const [showSettleConfirmDialog, setShowSettleConfirmDialog] = useState(false);
  const [pendingSettleTable, setPendingSettleTable] = useState<{
    table: Table;
    activeOrders?: Array<any>;
    completedOrders?: Array<any>;
    isOccupied: boolean;
    status: string;
    activeOrderCount?: number;
  } | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [showPaymentMethodError, setShowPaymentMethodError] = useState(false);

  // Payment method options
  // Country-aware (UPI/Wallet are IN-only rails) — see config/paymentMethods
  const PAYMENT_METHODS = getPaymentMethods();

  // Discount dialog state
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountTable, setDiscountTable] = useState<{
    table: Table;
    orderLineId?: number;
    totalAmount?: number;
  } | null>(null);
  const [discountType, setDiscountType] = useState<'PERCENTAGE' | 'FIXED' | 'COUPON'>('PERCENTAGE');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [discountReason, setDiscountReason] = useState<string>('');
  const [couponCode, setCouponCode] = useState<string>('');
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  // What the promotion engine says applies to this bill. Fetched when the
  // discount dialog opens; the server decides, this screen only shows it.
  const [billOffers, setBillOffers] = useState<{
    available: boolean;
    discountAmount: number;
    names: string[];
  } | null>(null);
  const [applyingOffer, setApplyingOffer] = useState(false);

  // How far the guest has got with the offers they earn over visits. Read-only
  // and purely to be said out loud — "one more visit and dessert is on us" is
  // worth something at the table and nothing on a statement afterwards.
  const [billLoyalty, setBillLoyalty] = useState<{
    recognised: boolean;
    /** What a reward would replace. A bill carries one discount, not two. */
    currentDiscount: { amount: number; reason: string } | null;
    standings: Array<{
      promotionId: number;
      name: string;
      /** PROGRESS works towards something, BALANCE is in hand, TIER is standing. */
      kind: 'PROGRESS' | 'BALANCE' | 'TIER';
      unit: 'visits' | 'spend' | 'items' | 'points' | 'credit';
      have: number;
      target: number | null;
      remaining: number | null;
      percent: number;
      claimable: boolean;
      tierHeld: boolean;
      /** What the balance is worth in money. BALANCE only. */
      balanceWorth: number | null;
      minRedeem: number | null;
      /** What the reward is worth on THIS bill, once earned. */
      rewardValue: number | null;
      rewardLabel: string | null;
      /** Why it cannot be given here — "the guest has not ordered it". */
      blockedReason: string | null;
    }>;
  } | null>(null);
  const [claimingReward, setClaimingReward] = useState<number | null>(null);

  // What the server says about the code staff have typed. Looked up rather than
  // guessed: the till never decides what a coupon is worth.
  const [couponCheck, setCouponCheck] = useState<{
    valid: boolean;
    message: string | null;
    discountAmount: number;
    offerName: string | null;
  } | null>(null);
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const [currentDiscount, setCurrentDiscount] = useState<{
    type: string;
    value: number;
    amount: number;
    reason: string;
  } | null>(null);
  // Server-computed preview (single source of truth): discount on the pre-tax
  // subtotal, taxes recomputed on the discounted base — identical to the
  // invoice. Local math here previously used the tax-inclusive total and
  // quoted a different discount than the bill would show.
  interface DiscountPreview {
    subtotal: number;
    sessionDiscountAmount: number;
    taxableAmount: number;
    cgstAmount: number;
    sgstAmount: number;
    igstAmount: number;
    serviceChargeAmount: number;
    roundOff: number;
    vatAmount: number | null;
    vatRate: number | null;
    finalAmount: number;
    grossTotal: number;
  }
  const [discountPreview, setDiscountPreview] = useState<DiscountPreview | null>(null);

  useEffect(() => {
    if (!showDiscountDialog || !discountTable?.orderLineId) { setDiscountPreview(null); return; }
    const value = parseFloat(discountValue);
    if (!value || value <= 0 || (discountType === 'PERCENTAGE' && value > 100)) {
      setDiscountPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await api.get(
          `/api/order-lines/${discountTable.orderLineId}/discount/preview`,
          { params: { discountType: discountType === 'COUPON' ? 'COUPON' : discountType, discountValue: value } }
        );
        setDiscountPreview(res.data);
      } catch {
        setDiscountPreview(null);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [showDiscountDialog, discountTable?.orderLineId, discountType, discountValue]);

  // Discount type options
  const DISCOUNT_TYPES = [
    { value: 'PERCENTAGE', label: 'Percentage', icon: '%' },
    { value: 'FIXED', label: 'Fixed Amount', icon: CURRENCY.symbol },
    { value: 'COUPON', label: 'Coupon Code', icon: '🎟️' }
  ];

  // Table transfer dialog state
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferSourceTable, setTransferSourceTable] = useState<{
    id: string;
    name: string;
    displayNumber?: string;
    activeOrderCount?: number;
  } | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);

  // Transfer confirmation dialog state (when selecting target table in transfer mode)
  const [transferConfirmation, setTransferConfirmation] = useState<{
    show: boolean;
    targetTableId: string;
    targetTableName: string;
  } | null>(null);

  // "More actions" menu anchor
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);

  // Table merge/group state — merge mode works like transfer mode (select on floor plan)
  const [mergeMode, setMergeMode] = useState<{
    active: boolean;
    sourceTableId: string;
    sourceTableName: string;
    selectedTableIds: Set<string>;
  } | null>(null);
  const [isMerging, setIsMerging] = useState(false);
  const [tableGroupMap, setTableGroupMap] = useState<Map<string, { groupId: number; groupName: string; tableNames: string[] }>>(new Map());

  // Split Bill dialog state
  const [showSplitBillDialog, setShowSplitBillDialog] = useState(false);
  const [splitBillData, setSplitBillData] = useState<{
    tableName: string;
    totalAmount: number;
    orderIds: number[];
  } | null>(null);

  // Fetch active table groups and build lookup map
  const fetchTableGroups = useCallback(async () => {
    try {
      const response = await api.get('/api/table-groups');
      const groups = response.data || [];
      const map = new Map<string, { groupId: number; groupName: string; tableNames: string[] }>();
      for (const group of groups) {
        if (group.status !== 'ACTIVE' && group.status !== 'PENDING') continue;
        // API may return members array OR tableIds+tableNames arrays
        const names: string[] = group.tableNames || (group.members || []).map((m: any) => m.tableName || m.tableDisplayNumber || `Table ${m.tableId}`);
        const ids: (string | number)[] = group.tableIds || (group.members || []).map((m: any) => m.tableId);
        for (let i = 0; i < ids.length; i++) {
          map.set(String(ids[i]), {
            groupId: group.id,
            groupName: group.name || `Group #${group.id}`,
            tableNames: names
          });
        }
      }
      setTableGroupMap(map);
    } catch {
      // Table groups are optional, don't break the page
    }
  }, []);

  // Fetch groups on mount and after merge/unmerge
  useEffect(() => { fetchTableGroups(); }, [fetchTableGroups]);

  // Handle unmerge (release table group)
  const handleUnmergeTable = async (tableId: string) => {
    const groupInfo = tableGroupMap.get(String(tableId));
    if (!groupInfo) return;

    const ok = await confirm({
      title: 'Unmerge Tables',
      message: `Unmerge ${groupInfo.tableNames?.join(', ') || 'these tables'}? Each table will become independent again.`,
      confirmText: 'Unmerge',
      confirmColor: 'warning',
    });
    if (!ok) return;

    try {
      await api.post(`/api/table-groups/${groupInfo.groupId}/close`);
      toast.success('Tables unmerged successfully');
      fetchTableGroups();
      setFloorSummaries(prev => [...prev]);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to unmerge tables');
    }
  };

  // Execute merge: create table group from merge mode selections
  const handleExecuteMerge = async () => {
    if (!mergeMode || mergeMode.selectedTableIds.size === 0) return;
    setIsMerging(true);
    try {
      const tableIds = [Number(mergeMode.sourceTableId), ...Array.from(mergeMode.selectedTableIds).map(Number)];
      await api.post('/api/table-groups', {
        tableIds,
        primaryTableId: Number(mergeMode.sourceTableId),
        name: `Merged ${mergeMode.sourceTableName}`
      });
      toast.success(`Merged ${tableIds.length} tables successfully`);
      setMergeMode(null);
      fetchTableGroups();
      cachedFloorData.clear();
      fetchFloorSummaries();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to merge tables');
    } finally {
      setIsMerging(false);
    }
  };

  // Fetch orders from all tables in a merge group (for consolidated bill/settle)
  const fetchMergedGroupOrders = async (tableId: string): Promise<any[]> => {
    const groupInfo = tableGroupMap.get(String(tableId));
    if (!groupInfo) return [];
    // Find all table IDs in this group
    const groupTableIds: string[] = [];
    for (const [tid, info] of tableGroupMap.entries()) {
      if (info.groupId === groupInfo.groupId) groupTableIds.push(tid);
    }
    // Fetch orders from all tables in parallel
    const statuses = ['PENDING', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED'];
    const results = await Promise.all(
      groupTableIds.map(tid =>
        api.get(`/api/tables/${tid}/orders`, { params: { status: statuses } })
          .then(r => r.data || [])
          .catch(() => [])
      )
    );
    return results.flat();
  };

  // Handle opening split bill dialog
  const handleOpenSplitBill = async (tableData: any) => {
    try {
      let orders = tableData.activeOrders || [];
      if (orders.length === 0 && (tableData.activeOrderCount > 0 || tableData.isOccupied)) {
        const response = await api.get(`/api/tables/${tableData.table.id}/orders`, {
          params: { status: ['PENDING', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED'] }
        });
        orders = response.data || [];
      }
      if (orders.length === 0) {
        toast.error('No active orders to split');
        return;
      }
      const orderIds = orders.map((o: any) => o.id);
      // Get total from OrderLine if available, otherwise sum orders
      let totalAmount = orders.reduce((sum: number, o: any) => sum + (o.total || 0), 0);
      // Check for OrderLine discount
      if (orders[0]?.orderLineId) {
        // Put a live offer on BEFORE this total is shown. This screen is the
        // reason the offer must never be applied at settlement instead: a person
        // is about to divide this figure into payments, and discounting it
        // afterwards would leave those parts adding up to more than the bill.
        await ensureOffersOnBill(orders[0].orderLineId);

        try {
          const discountRes = await api.get(`/api/order-lines/${orders[0].orderLineId}/discount`);
          if (discountRes.data?.hasDiscount && typeof discountRes.data.finalAmount === 'number') {
            // Server-computed payable: taxes recomputed on the discounted base
            // (identical to the invoice) — never subtract the discount locally.
            totalAmount = discountRes.data.finalAmount;
          }
        } catch { /* no discount */ }
      }
      setSplitBillData({
        tableName: tableData.table.displayNumber || tableData.table.name,
        totalAmount,
        orderIds
      });
      setSelectedTable(null);
      setShowSplitBillDialog(true);
    } catch (err) {
      toast.error('Failed to load order data for split bill');
    }
  };

  // Optimized: Load only floor summaries initially (lightweight)
  const fetchFloorSummaries = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // Fetch floor summaries and menu assignments in parallel
      const [summaries, menuAssignmentsData] = await Promise.all([
        getFloorLayoutSummary(),
        getSectionMenuAssignments().catch(() => ({})) // Gracefully handle if menu assignments fail
      ]);
      console.log('[OPTIMIZED] Loaded floor summaries:', summaries);
      setFloorSummaries(summaries || []);
      setMenuAssignments(menuAssignmentsData || {});
    } catch (error: unknown) {
      console.error('Failed to fetch floor summaries:', error);
      // Surface the backend's userMessage if present (e.g. orphan steward gets
      // "Your account is not assigned to a vendor. Please contact your admin.")
      // instead of swallowing it as a generic "failed to load" toast.
      const err = error as { response?: { data?: { userMessage?: string; message?: string } }; message?: string };
      const backendMsg = err?.response?.data?.userMessage || err?.response?.data?.message;
      toast.error(backendMsg || 'Failed to load floor summaries');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Fetch floor assets for the floor plan view
  const fetchFloorAssets = useCallback(async (floorId: number) => {
    try {
      const response = await getFloorAssets(floorId);
      setFloorAssets(response.data || []);
    } catch (error) {
      console.error('Failed to fetch floor assets:', error);
      setFloorAssets([]);
    }
  }, []);

  // Save positions and assets from floor plan editor
  const handleSavePositions = useCallback(async (
    positions: Array<{ tableId: number; x: number; y: number; width: number; height: number; rotation: number }>,
    assets: any[]
  ) => {
    try {
      await batchUpdateTablePositions(positions);
      // Save new assets (those without id)
      const newAssets = assets.filter((a: any) => !a.id);
      const existingAssets = assets.filter((a: any) => a.id);
      for (const asset of newAssets) {
        await createFloorAsset(asset.floorId, asset);
      }
      // Update existing asset positions
      if (existingAssets.length > 0) {
        await batchUpdateFloorAssetPositions(
          existingAssets.map((a: any) => ({ id: a.id, x: a.positionX, y: a.positionY, width: a.width, height: a.height, rotation: a.rotation }))
        );
      }
      // Refresh data
      fetchFloorSummaries();
      const activeFloorId = selectedFloorKey ? parseInt(selectedFloorKey.replace('floor_', '')) : null;
      if (activeFloorId) fetchFloorAssets(activeFloorId);
      toast.success('Floor plan saved');
    } catch (error) {
      console.error('Failed to save floor plan:', error);
      toast.error('Failed to save floor plan');
    }
  }, [fetchFloorSummaries, fetchFloorAssets, selectedFloorKey]);

  // Load sections for a specific floor (lazy loading)
  const loadFloorSections = useCallback(async (floorId: string, autoExpand: boolean = false) => {
    // Check if already cached
    if (cachedFloorData.has(floorId)) {
      console.log('[CACHE] Using cached data for floor:', floorId);
      if (autoExpand) {
        const cachedData = cachedFloorData.get(floorId);
        if (cachedData?.sections) {
          setExpandedSections(prev => {
            const newSet = new Set(prev);
            cachedData.sections.forEach(s => newSet.add(s.section.id));
            return newSet;
          });
        }
      }
      return;
    }

    // Check if already loading
    if (loadingFloors.has(floorId)) {
      console.log('[LOADING] Already loading floor:', floorId);
      return;
    }

    setLoadingFloors(prev => new Set(prev).add(floorId));
    try {
      console.log('[LAZY LOAD] Loading sections for floor:', floorId);
      const response = await getFloorSections(floorId);
      const sectionsData: FloorSectionsData = response;

      // Initialize grid settings for sections
      sectionsData.sections.forEach((sectionData) => {
        if (!sectionData.section.gridColumns) {
          sectionData.section.gridColumns = 4;
          sectionData.section.gridRows = Math.ceil((sectionData.tables?.length || 0) / 4) || 4;
        }
      });

      // Cache the loaded data
      setCachedFloorData(prev => new Map(prev).set(floorId, sectionsData));
      setFloorDataVersion(v => v + 1); // Trigger re-render for auto-open table

      // Auto-expand sections if requested
      if (autoExpand) {
        setExpandedSections(prev => {
          const newSet = new Set(prev);
          sectionsData.sections.forEach(s => newSet.add(s.section.id));
          return newSet;
        });
      }

      console.log('[CACHED] Cached sections for floor:', floorId, sectionsData);
    } catch (error) {
      console.error('Failed to load floor sections:', error);
      toast.error(`Failed to load sections for floor ${floorId}`);
    } finally {
      setLoadingFloors(prev => {
        const newSet = new Set(prev);
        newSet.delete(floorId);
        return newSet;
      });
    }
  }, [cachedFloorData, loadingFloors]);

  // Legacy function for backward compatibility (still used by some operations)
  const fetchFloorLayout = useCallback(async (preserveExpanded = false) => {
    if (!user?.id) return null;

    // Only show full page loading for initial fetch or explicit refreshes
    // For background updates (socket events), we don't want to flash the loading screen
    if (!preserveExpanded) {
      setLoading(true);
    }

    try {
      const response = await getFloorLayout();
      const layoutData = response.data || response || {};

      // Initialize grid settings for sections
      Object.keys(layoutData).filter(k => k.startsWith('floor_')).forEach(floorKey => {
        const floorData = layoutData[floorKey];
        floorData.sections.forEach((sectionData: { section: Section; tables: any[] }) => {
          if (!sectionData.section.gridColumns) {
            sectionData.section.gridColumns = 4;
            sectionData.section.gridRows = Math.ceil((sectionData.tables?.length || 0) / 4) || 4;
          }
        });
      });

      setFloorLayout(layoutData);

      // CRITICAL FIX: Sync cachedFloorData with the fresh layout data
      // The renderFloor function prefers cachedFloorData (optimized) over floorLayout (legacy).
      // If we don't update the cache, the UI will show stale data even after floorLayout is updated.
      setCachedFloorData(prev => {
        const newCache = new Map(prev);
        let hasChanges = false;

        Object.keys(layoutData).filter(k => k.startsWith('floor_')).forEach(floorKey => {
          const floorId = floorKey.replace('floor_', '');
          // Update the cache for this floor if it exists (meaning it's been loaded/viewed)
          if (newCache.has(floorId)) {
            const floorData = layoutData[floorKey];
            // The structure of floorData.sections matches FloorSectionsData.sections
            newCache.set(floorId, { sections: floorData.sections });
            hasChanges = true;
          }
        });

        return hasChanges ? newCache : prev;
      });

      // Don't auto-expand any floors - let user expand manually
      // This keeps the mobile interface clean with only "Add Floor" button visible initially
      // If preserveExpanded is true, keep the current expandedFloors and expandedSections state

      return layoutData; // Return the fetched data
    } catch (error) {
      // Only log to console, don't show toast as data may load on retry
      console.error('Floor layout fetch issue:', error);
      return null;
    } finally {
      if (!preserveExpanded) {
        setLoading(false);
      }
    }
  }, [user?.id]);

  // Use optimized loading on mount
  useEffect(() => {
    fetchFloorSummaries();
  }, [fetchFloorSummaries]);

  // Background load all floor data for stats (Available/Occupied counts)
  // This runs once after floor summaries are loaded to populate cachedFloorData
  const [statsDataLoaded, setStatsDataLoaded] = useState(false);
  useEffect(() => {
    if (floorSummaries.length > 0 && !loading && !statsDataLoaded) {
      // Load all floors in background to populate stats
      const loadAllFloorsForStats = async () => {
        console.log('[STATS] Loading all floor data in background for stats...');
        setStatsDataLoaded(true); // Mark as started to prevent re-runs
        for (const floor of floorSummaries) {
          // Use just the ID (not "floor_123" format) - matches how loadFloorSections expects it
          const floorId = String(floor.id);
          await loadFloorSections(floorId, false); // false = don't auto-expand
        }
        console.log('[STATS] All floor data loaded for stats');
      };
      loadAllFloorsForStats();
    }
  }, [floorSummaries, loading, statsDataLoaded, loadFloorSections]);

  // Check for table query parameter on mount
  useEffect(() => {
    const tableQrCode = searchParams.get('table');
    if (tableQrCode) {
      setAutoOpenTableQrCode(tableQrCode);
      // Clear the query parameter from URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // When we have a table to auto-open, load all floor sections to find it
  useEffect(() => {
    if (autoOpenTableQrCode && floorSummaries.length > 0 && !loading) {
      // Load all floor sections to find the table
      floorSummaries.forEach(floor => {
        if (!cachedFloorData.has(String(floor.id))) {
          loadFloorSections(String(floor.id), true);
        }
      });
    }
  }, [autoOpenTableQrCode, floorSummaries, loading, cachedFloorData, loadFloorSections]);

  // Auto-open table modal when cached floor data loads and we have a table to open
  useEffect(() => {
    if (autoOpenTableQrCode && cachedFloorData.size > 0 && !loading) {
      // Find the table with matching qrCode across all cached floor data
      for (const [floorId, floorData] of cachedFloorData.entries()) {
        for (const sectionData of floorData.sections) {
          // Find matching table - data structure is { table: {..., qrCode}, isOccupied, status, ... }
          const matchingTableData = sectionData.tables.find(
            (tableData: any) => tableData?.table?.qrCode?.code === autoOpenTableQrCode
          );
          if (matchingTableData) {
            // Expand the floor and section that contains this table
            // Note: expandedFloors uses "floor_123" format, expandedSections uses section.id directly
            setExpandedFloors(prev => new Set(prev).add(`floor_${floorId}`));
            setExpandedSections(prev => new Set(prev).add(sectionData.section.id));
            // Get floor name from summaries
            const floor = floorSummaries.find(f => String(f.id) === floorId);
            const floorName = floor?.name || `Floor ${floor?.floorNumber || floorId}`;
            // Open the table modal - matchingTableData already has the correct structure
            setSelectedTable({
              ...matchingTableData,
              activeOrders: matchingTableData.activeOrders || [],
              floorName,
              sectionName: sectionData.section.name,
            });
            setAutoOpenTableQrCode(null); // Clear to prevent re-opening
            return;
          }
        }
      }
      // Table not found in cached data, will try again when more data is cached
    }
  }, [autoOpenTableQrCode, floorDataVersion, loading, floorSummaries]);

  // Socket.IO real-time updates for cross-steward synchronization
  useEffect(() => {
    console.log('[Visual2DLayoutManager] Socket.IO useEffect running:', {
      isConnected,
      userId: user?.id,
      willRegisterListeners: !!(isConnected && user?.id)
    });

    if (!isConnected || !user?.id) {
      console.log('[Visual2DLayoutManager] Skipping listener registration - isConnected:', isConnected, 'user?.id:', user?.id);
      return;
    }

    const currentVendorId = user.id;

    // Subscribe to vendor-wide events
    subscribeToVendor(currentVendorId);

    // Handler for new orders placed by other stewards
    const handleNewOrder = (data: unknown) => {
      console.log('[Visual2DLayoutManager] order:new received:', data);

      // Refresh the floor layout to show the new order
      // Use preserve expanded state to maintain user's view
      fetchFloorLayout(true);

      // Show notification - use default position (top-right) from global Toaster
      console.log('[Visual2DLayoutManager] Showing toast notification');
      toast.success('New order placed on floor', {
        duration: 3000,
        icon: '📋'
      });
    };

    // Handler for order status updates
    const handleOrderStatusUpdate = (data: unknown) => {
      console.log('[Visual2DLayoutManager] order:status received:', data);

      // Type-safe extraction of order status data
      const orderData = data as { orderId?: string | number; status?: string; tableId?: number; orderNumber?: string };

      // Update order status in selectedTable if we have an open table modal
      if (orderData.orderId && orderData.status) {
        const completedStatuses = ['COMPLETED', 'CANCELLED', 'SETTLED'];
        const isCompleted = completedStatuses.includes(orderData.status.toUpperCase());

        setSelectedTable(prev => {
          if (prev && prev.activeOrders && prev.activeOrders.length > 0) {
            if (isCompleted) {
              // Filter out the completed/cancelled order
              const updatedOrders = prev.activeOrders.filter(
                order => String(order.id) !== String(orderData.orderId)
              );
              const wasFiltered = updatedOrders.length < prev.activeOrders.length;

              if (wasFiltered) {
                console.log('[Visual2DLayoutManager] Removed completed order from selectedTable:', orderData.orderId);
                const newActiveOrderCount = updatedOrders.length;
                return {
                  ...prev,
                  activeOrders: updatedOrders,
                  activeOrderCount: newActiveOrderCount,
                  isOccupied: newActiveOrderCount > 0,
                  status: newActiveOrderCount > 0 ? 'OCCUPIED' : 'AVAILABLE'
                };
              }
            } else {
              // Update the order status in place for non-completed statuses (READY, PREPARING, etc.)
              // Match by id, customOrderId, or orderNumber since different sources use different identifiers
              const orderIdStr = String(orderData.orderId);
              const orderNumber = orderData.orderNumber;

              let matchFound = false;
              const updatedOrders = prev.activeOrders.map(order => {
                const orderIdMatch = String(order.id) === orderIdStr;
                const customOrderIdMatch = order.customOrderId && orderNumber && order.customOrderId === orderNumber;

                if (orderIdMatch || customOrderIdMatch) {
                  console.log('[Visual2DLayoutManager] Order status updated:', order.customOrderId || order.id, '→', orderData.status);
                  matchFound = true;
                  return { ...order, status: orderData.status };
                }
                return order;
              });

              if (matchFound) {
                return {
                  ...prev,
                  activeOrders: updatedOrders
                };
              }
            }
          }
          return prev;
        });
      }

      // Trigger TableOrderHistory refresh to show updated order status
      setOrderHistoryRefreshTrigger(prev => prev + 1);

      // Refresh the floor layout to show updated status
      fetchFloorLayout(true);
    };

    // Handler for vendor updates
    const handleVendorUpdate = (data: unknown) => {
      console.log('[Visual2DLayoutManager] vendor:update received:', data);

      // Refresh the floor layout to show updated data
      fetchFloorLayout(true);
    };

    // Handler for table status changes (e.g., when a table becomes occupied/available after order)
    const handleTableStatusChanged = (data: unknown) => {
      console.log('[Visual2DLayoutManager] table:status_changed received:', data);

      // Type-safe extraction of table status data
      const statusData = data as { tableId?: number; activeOrderCount?: number; status?: string };

      // If the currently selected table matches the updated table, update its state immediately
      if (statusData.tableId) {
        setSelectedTable(prev => {
          if (prev && String(prev.table.id) === String(statusData.tableId)) {
            console.log('[Visual2DLayoutManager] Updating selectedTable with new status:', statusData);
            const newActiveOrderCount = statusData.activeOrderCount ?? 0;
            const newStatus = statusData.status || (newActiveOrderCount > 0 ? 'OCCUPIED' : 'AVAILABLE');
            const isOccupied = newActiveOrderCount > 0 || newStatus.toUpperCase() === 'OCCUPIED';

            return {
              ...prev,
              activeOrderCount: newActiveOrderCount,
              isOccupied: isOccupied,
              status: newStatus,
              // Clear activeOrders if the count is now 0 (orders were settled/completed)
              activeOrders: newActiveOrderCount === 0 ? [] : prev.activeOrders
            };
          }
          return prev;
        });
      }

      // Refresh the floor layout to show updated table statuses
      fetchFloorLayout(true);
    };

    // Handle table transfer event
    const handleTableTransfer = (data: any) => {
      console.log('[Visual2DLayoutManager] Table transfer event received:', data);
      toast.success(`Orders transferred: ${data.sourceTableName} → ${data.targetTableName}`);
      // Refresh floor layout to show updated table statuses
      fetchFloorLayout(true);
    };

    // Handle reservation events - refresh layout when reservations change
    const handleReservationChange = () => {
      fetchFloorLayout(true);
    };

    // Register event listeners using specific event constants (not SOCKET_EVENTS which has key collisions)
    console.log('[Visual2DLayoutManager] Registering listeners for:', ORDER_EVENTS.NEW, ORDER_EVENTS.STATUS_UPDATE, VENDOR_EVENTS.UPDATE, TABLE_EVENTS.STATUS_CHANGED, TABLE_EVENTS.TRANSFER);
    on(ORDER_EVENTS.NEW, handleNewOrder);
    on(ORDER_EVENTS.STATUS_UPDATE, handleOrderStatusUpdate);
    on(VENDOR_EVENTS.UPDATE, handleVendorUpdate);
    on(TABLE_EVENTS.STATUS_CHANGED, handleTableStatusChanged);
    on(TABLE_EVENTS.TRANSFER, handleTableTransfer);
    on(RESERVATION_EVENTS.NEW, handleReservationChange);
    on(RESERVATION_EVENTS.UPDATED, handleReservationChange);
    on(RESERVATION_EVENTS.SEATED, handleReservationChange);
    on(RESERVATION_EVENTS.CANCELLED, handleReservationChange);

    console.log('[Visual2DLayoutManager] Socket.IO subscribed to vendor:', currentVendorId, '- listeners registered');

    // Cleanup on unmount or when dependencies change
    return () => {
      console.log('[Visual2DLayoutManager] Socket.IO unsubscribing from vendor:', currentVendorId);
      off(ORDER_EVENTS.NEW, handleNewOrder);
      off(ORDER_EVENTS.STATUS_UPDATE, handleOrderStatusUpdate);
      off(VENDOR_EVENTS.UPDATE, handleVendorUpdate);
      off(TABLE_EVENTS.STATUS_CHANGED, handleTableStatusChanged);
      off(TABLE_EVENTS.TRANSFER, handleTableTransfer);
      off(RESERVATION_EVENTS.NEW, handleReservationChange);
      off(RESERVATION_EVENTS.UPDATED, handleReservationChange);
      off(RESERVATION_EVENTS.SEATED, handleReservationChange);
      off(RESERVATION_EVENTS.CANCELLED, handleReservationChange);
      unsubscribeFromVendor(currentVendorId);
    };
  }, [isConnected, user?.id, subscribeToVendor, unsubscribeFromVendor, on, off, fetchFloorLayout]);



  const toggleFloorExpansion = (floorKey: string) => {
    const newExpanded = new Set(expandedFloors);
    const isExpanding = !newExpanded.has(floorKey);

    if (newExpanded.has(floorKey)) {
      newExpanded.delete(floorKey);
    } else {
      newExpanded.add(floorKey);
    }
    setExpandedFloors(newExpanded);

    // Lazy load sections when expanding floor
    if (isExpanding) {
      // Extract floor ID from floorKey (format: "floor_123")
      const floorId = floorKey.replace('floor_', '');
      loadFloorSections(floorId);
    }
  };

  const toggleSectionExpansion = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  // --- Section quick-navigation helpers ---

  const scrollToSection = useCallback((sectionId: string) => {
    const doScroll = () => {
      const el = sectionRefs.current.get(sectionId);
      if (!el) return;
      // Account for sticky header + pills row (~100px)
      const stickyOffset = transferMode?.active ? 160 : 100;
      const top = el.getBoundingClientRect().top + window.scrollY - stickyOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    };

    // If section is collapsed, expand first then scroll after animation
    if (!expandedSections.has(sectionId)) {
      setExpandedSections(prev => {
        const next = new Set(prev);
        next.add(sectionId);
        return next;
      });
      setTimeout(doScroll, 350);
    } else {
      doScroll();
    }
  }, [expandedSections, transferMode?.active]);

  // Reset active section when floor changes
  useEffect(() => {
    setActiveSectionId(null);
    sectionRefs.current.clear();
  }, [selectedFloorKey]);

  // Load floor assets and section data when floor plan view is active
  useEffect(() => {
    if (viewMode !== 'floorplan') return;
    const activeFloorKey = selectedFloorKey || (floorSummaries[0] ? `floor_${floorSummaries[0].id}` : null);
    if (!activeFloorKey) return;
    const floorId = activeFloorKey.replace('floor_', '');
    // Ensure section data is loaded for this floor
    if (!cachedFloorData.has(floorId)) {
      loadFloorSections(floorId, true);
    }
    fetchFloorAssets(parseInt(floorId));
  }, [viewMode, selectedFloorKey, floorSummaries, cachedFloorData, loadFloorSections, fetchFloorAssets]);

  // Auto-scroll active chip into view
  useEffect(() => {
    if (!activeSectionId || !sectionChipsContainerRef.current) return;
    const chip = sectionChipsContainerRef.current.querySelector(`[data-section-chip="${activeSectionId}"]`);
    if (chip) {
      (chip as HTMLElement).scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, [activeSectionId]);

  // Compute visible sections for section navigation (must be before hooks that reference them)
  const visibleSections = (() => {
    if (!selectedFloorKey) return [];
    const floorId = selectedFloorKey.replace('floor_', '');
    const cached = cachedFloorData.get(floorId);
    if (cached) {
      return cached.sections.filter(s => !s.section.isDeleted);
    }
    const legacyFloor = floorLayout[selectedFloorKey];
    if (legacyFloor?.sections) {
      return legacyFloor.sections
        .filter((s: any) => !s.section?.isDeleted)
        .map((s: any) => ({ section: s.section || s, tables: s.tables || [] }));
    }
    return [];
  })();
  const showSectionNav = !!selectedFloorKey && visibleSections.length > 1;
  const selectedFloorInfo = selectedFloorKey
    ? (floorSummaries.length > 0
        ? floorSummaries.find(f => `floor_${f.id}` === selectedFloorKey)
        : floorLayout[selectedFloorKey]?.floor)
    : null;

  // IntersectionObserver for active section tracking
  useEffect(() => {
    if (!showSectionNav) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost visible section
        let topEntry: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!topEntry || entry.boundingClientRect.top < topEntry.boundingClientRect.top) {
              topEntry = entry;
            }
          }
        }
        if (topEntry) {
          const sectionId = topEntry.target.id.replace('section-', '');
          setActiveSectionId(sectionId);
        }
      },
      {
        rootMargin: '-100px 0px -40% 0px',
        threshold: 0,
      }
    );

    // Observe all section refs
    sectionRefs.current.forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [showSectionNav, visibleSections.length, floorDataVersion]);

  const handleCreateFloor = async (): Promise<void> => {
    if (!floorName.trim()) {
      toast.error('Please enter a floor name');
      return;
    }

    // Validate floor number is provided
    if (floorNumber === '' || floorNumber === null) {
      toast.error('Please enter a floor number');
      return;
    }

    // Convert to number and validate range
    const floorNum = typeof floorNumber === 'string' ? parseInt(floorNumber) : floorNumber;
    if (isNaN(floorNum)) {
      toast.error('Floor number must be a valid number');
      return;
    }

    if (floorNum < 0 || floorNum > 100) {
      toast.error('Floor number must be between 0 and 100');
      return;
    }

    // Client-side duplicate validation for floor name
    const isDuplicateFloor = Object.values(floorLayout).some(
      ({ floor }) => floor.name.toLowerCase() === floorName.trim().toLowerCase()
    );

    if (isDuplicateFloor) {
      toast.error('A floor with this name already exists');
      return;
    }

    // Client-side duplicate validation for floor number
    const existingFloorNumbers = Object.values(floorLayout).map(({ floor }) => floor.floorNumber);
    const isDuplicateFloorNumber = existingFloorNumbers.includes(floorNum);

    if (isDuplicateFloorNumber) {
      const existingFloorWithNumber = Object.values(floorLayout).find(
        ({ floor }) => floor.floorNumber === floorNum
      );
      toast.error(
        `Floor number ${floorNum} is already used by "${existingFloorWithNumber?.floor.name}". Please use a different floor number.`
      );
      return;
    }

    try {
      await createFloor({ name: floorName, floorNumber: floorNum });
      toast.success('Floor created successfully');
      setFloorName('');
      setFloorNumber('');
      setOpenFloorDialog(false);
      // Refresh floor summaries to show new floor
      fetchFloorSummaries();
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      const errorMessage = err.response?.data?.message || 'Failed to create floor';
      toast.error(errorMessage);
    }
  };

  const handleCreateSection = async (): Promise<void> => {
    if (!sectionName.trim() || !selectedFloorForSection) {
      toast.error('Please enter a section name and select a floor');
      return;
    }

    // Client-side duplicate validation
    const floorData = floorLayout[selectedFloorForSection];
    if (floorData) {
      const isDuplicateSection = floorData.sections.some(
        ({ section }) => section.name.toLowerCase() === sectionName.trim().toLowerCase()
      );

      if (isDuplicateSection) {
        toast.error(`A section with this name already exists in ${floorData.floor.name}`);
        return;
      }
    }

    try {
      const floorId = selectedFloorForSection.replace('floor_', '');
      await createSection({ name: sectionName, floorId });
      toast.success('Section created successfully');
      setSectionName('');
      setSectionColumns(4);
      setSectionRows(4);
      setOpenSectionDialog(false);
      fetchFloorLayout();
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      const errorMessage = err.response?.data?.message || 'Failed to create section';
      toast.error(errorMessage);
    }
  };

  const handleCreateTables = async () => {
    if (!tablePrefix.trim() || !selectedSectionForTable || !selectedFloorIdForTable || !selectedSectionNameForTable) {
      toast.error('Please fill all fields correctly');
      return;
    }

    const numTables = typeof tablesToCreate === 'string' ? parseInt(tablesToCreate) : tablesToCreate;
    if (isNaN(numTables) || numTables < 1 || numTables > 100) {
      toast.error('Number of tables must be between 1 and 100');
      return;
    }

    // Capacity is number | '' while the field is being edited; resolve it the
    // same way. onBlur normally clamps first, but submitting straight from a
    // cleared box must not post '' as the seat count.
    const capacity = typeof tableCapacity === 'string' ? parseInt(tableCapacity) : tableCapacity;
    if (isNaN(capacity) || capacity < 1 || capacity > 20) {
      toast.error('Capacity must be between 1 and 20');
      return;
    }

    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    try {
      // Get floor name from floorSummaries or floorLayout
      let floorName = '';
      const floorSummary = floorSummaries.find(f => f.id === parseInt(selectedFloorIdForTable));
      if (floorSummary) {
        floorName = floorSummary.name;
      } else {
        // Fallback to floorLayout
        const floorKey = Object.keys(floorLayout).filter(k => k.startsWith('floor_')).find(key =>
          floorLayout[key].floor.id === selectedFloorIdForTable
        );
        if (floorKey) {
          floorName = floorLayout[floorKey].floor.name;
        }
      }

      if (!floorName) {
        toast.error('Floor information not found');
        return;
      }

      // Get existing table count and names from cached data or floorLayout
      let existingTables = 0;
      let existingTableNames: string[] = [];

      const sectionsCache = cachedFloorData.get(selectedFloorIdForTable);
      if (sectionsCache) {
        const cachedSection = sectionsCache.sections.find(s => s.section.id === selectedSectionForTable);
        existingTables = cachedSection?.tables.length || 0;
        existingTableNames = cachedSection?.tables.map(t => t.table.name.toLowerCase()) || [];
      } else {
        // Fallback to floorLayout
        const floorKey = `floor_${selectedFloorIdForTable}`;
        const floorData = floorLayout[floorKey];
        if (floorData) {
          const sectionData = floorData.sections.find(s => s.section.id === selectedSectionForTable);
          existingTables = sectionData?.tables.length || 0;
          existingTableNames = sectionData?.tables.map(t => t.table.name.toLowerCase()) || [];
        }
      }

      const floorAbbr = floorName.substring(0, 2).toUpperCase();
      const sectionAbbr = selectedSectionNameForTable.substring(0, 2).toUpperCase();

      // Create multiple tables
      const tablesToCreateList = [];
      for (let i = 0; i < numTables; i++) {
        const tableNumber = existingTables + i + 1;
        const tableName = `${tablePrefix}${tableNumber}`;
        const tableId = `${floorAbbr}-${sectionAbbr}-${tableName}`;

        // Check for duplicate table names
        if (existingTableNames.includes(tableId.toLowerCase())) {
          toast.error(`Table ${tableId} already exists in this section`);
          return;
        }

        tablesToCreateList.push({
          name: tableId,
          sectionId: selectedSectionForTable,
          capacity,
          shape: tableShape
        });
      }

      // Show progress toast
      const progressToast = toast.loading(`Creating ${numTables} table(s) and generating QR codes...`);

      try {
        // Create all tables and generate QR codes
        const createdTables = [];
        let successCount = 0;
        let qrSuccessCount = 0;

        for (const tableData of tablesToCreateList) {
          try {
            // Create table with QR code in one operation
            const response = await createTableWithQrCode(tableData);

            // Response contains both table and qrCode
            createdTables.push(response.table);
            successCount++;

            if (response.qrCode) {
              qrSuccessCount++;
            }

          } catch (error) {
            console.error(`Failed to create table ${tableData.name}:`, error);
            // Continue with other tables if one fails
          }
        }

        // Dismiss progress toast
        toast.dismiss(progressToast);

        // Show success message
        if (successCount === numTables) {
          if (qrSuccessCount === numTables) {
            toast.success(`🎉 ${numTables} table(s) created successfully with QR codes!`, {
              duration: 4000,
              icon: '✅',
            });
          } else if (qrSuccessCount > 0) {
            toast.success(`${numTables} table(s) created! ${qrSuccessCount} QR codes generated successfully.`, {
              duration: 4000,
            });
            toast.warning(`${numTables - qrSuccessCount} QR codes failed to generate. You can generate them manually.`, {
              duration: 5000,
            });
          } else {
            toast.success(`${numTables} table(s) created successfully!`);
            toast.warning('QR code generation failed. You can generate them manually from the table options.', {
              duration: 5000,
            });
          }
        }

        // Reset form and close dialog
        setTablePrefix('T');
        setTablesToCreate('');
        setTableCapacity(4);
        setTableShape('square');
        setOpenTableDialog(false);

        // Refresh the layout to show new tables with QR codes
        await fetchFloorLayout();

      } catch (error) {
        toast.dismiss(progressToast);
        throw error;
      }

    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      const errorMessage = err.response?.data?.message || 'Failed to create tables';
      toast.error(errorMessage);
    }
  };

  const handleOpenQrDialog = (table: Table) => {
    setSelectedTableForQr(table);

    // Find the section information for this table
    const floorInfo = Object.values(floorLayout).find(floor =>
      floor.sections.some(section =>
        section.tables.some(t => t.table.id === table.id)
      )
    );

    const sectionInfo = floorInfo?.sections.find(section =>
      section.tables.some(t => t.table.id === table.id)
    );

    // Pre-fill QR code data compatible with existing system
    const qrCodeIdentifier = `TABLE-${table.name.replace(/[^A-Z0-9]/g, '-')}-${Date.now()}`;
    const baseUrl = window.location.origin;
    const qrCodeData = `${baseUrl}/menu?qr=${qrCodeIdentifier}`;

    setQrCodeName(`${table.name} QR Code`);
    setQrCodeData(qrCodeData);
    setOpenQrDialog(true);
  };

  const handleGenerateQrCode = async () => {
    if (!user?.id || !selectedTableForQr || !qrCodeData.trim()) {
      toast.error('Missing required information');
      return;
    }

    try {
      const response = await createVendorQrCode(String(user.id), {
        location: selectedTableForQr.name, // Use table name as location
        metadata: {
          tableId: String(selectedTableForQr.id),
          tableName: selectedTableForQr.name,
          customName: qrCodeName.trim() || `${selectedTableForQr.name} QR Code`,
          customData: qrCodeData.trim() || '',
          type: 'table_qr'
        },
        logoUrl: null
      });
      const qrCode = response.data;
      await assignQrCodeToTable(selectedTableForQr.id, qrCode.id);
      toast.success(`QR code generated for ${selectedTableForQr.name}`);
      setOpenQrDialog(false);
      setQrCodeName('');
      setQrCodeData('');

      // Fetch the layout first to update the table data, preserve expanded state
      await fetchFloorLayout(true);

      // Automatically show the newly generated QR code
      if (qrCode.code) {
        try {
          const imageUrl = await fetchQrCodeImage(String(user.id), qrCode.code);
          setQrImageUrl(imageUrl);
          setShowQrInDetails(true);
          toast.success('QR code is ready to view and download!', { duration: 3000 });
        } catch (error) {
          console.error('Failed to fetch newly generated QR code:', error);
        }
      }

      setSelectedTableForQr(null);
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };


  const handleViewQrCode = async (qrCode: { id: string; code: string }) => {
    if (!user?.id || !qrCode?.code) return;

    setIsLoadingQrImage(true);
    try {
      const imageUrl = await fetchQrCodeImage(String(user.id), qrCode.code);
      setQrImageUrl(imageUrl);
      setShowQrInDetails(true);
    } catch (error: any) {
      console.error('QR code fetch error:', error);
      if (error.response?.status === 404) {
        // Show helpful message with action button
        setShowQrMissingDialog(true);
        setMissingQrDetails({
          code: qrCode.code,
          table: selectedTable?.table || null
        });
      } else {
        toast.error('Failed to load QR code image');
      }
    } finally {
      setIsLoadingQrImage(false);
    }
  };

  const handleDownloadQr = () => {
    if (!qrImageUrl || !selectedTable) return;

    const link = document.createElement('a');
    link.href = qrImageUrl;
    link.download = `${selectedTable.table.name || 'table'}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintQr = () => {
    if (!qrImageUrl || !selectedTable) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print QR Code - ${selectedTable.table.name}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
            }
            img { max-width: 400px; }
            h2 { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <h2>${selectedTable.table.name} - QR Code</h2>
          <img src="${qrImageUrl}" alt="QR Code" />
          <script>window.onload = () => window.print();</script>
        </body>
      </html>
    `;

    printWindow.document.write(content);
    printWindow.document.close();
  };

  // Handle opening discount dialog
  const handleOpenDiscountDialog = async (tableData: {
    table: Table;
    activeOrders?: Array<any>;
    activeOrderCount?: number;
    isOccupied?: boolean;
  }) => {
    try {
      // Find the OrderLine ID from the first active order
      let firstOrder = tableData.activeOrders?.[0];
      let orders = tableData.activeOrders || [];

      // If no activeOrders but table has orders (optimized format), fetch from API
      if (!firstOrder && (tableData.activeOrderCount && tableData.activeOrderCount > 0 || tableData.isOccupied)) {
        console.log('[Discount] No activeOrders in tableData but table has orders, fetching from API...');
        try {
          const ordersResponse = await api.get(`/api/tables/${tableData.table.id}/orders`, {
            params: { status: ['PENDING', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED'] }
          });
          orders = ordersResponse.data || [];
          console.log('[Discount] Fetched orders from API:', orders.length);
          if (orders.length > 0) {
            firstOrder = orders[0];
          }
        } catch (fetchError) {
          console.error('[Discount] Failed to fetch orders:', fetchError);
        }
      }

      if (!firstOrder) {
        toast.error('No active orders to apply discount');
        return;
      }

      // Try to get order line info from the order
      let orderLineId = firstOrder.orderLineId || firstOrder.order_line_id;
      let totalAmount = orders.reduce((sum: number, o: any) => sum + (o.total || 0), 0) || 0;

      // If no orderLineId, try to fetch it from the API using QR code
      if (!orderLineId && tableData.table.qrCode) {
        try {
          const qrCode = typeof tableData.table.qrCode === 'object'
            ? tableData.table.qrCode.code
            : tableData.table.qrCode;
          const response = await api.get(`/api/order-lines/qr/${qrCode}`);
          if (response.data?.sessionId) {
            orderLineId = response.data.sessionId;
            // Also get the total from the order line response
            if (response.data.totalAmount) {
              totalAmount = response.data.totalAmount;
            }
          }
        } catch (err) {
          console.error('Failed to fetch order line:', err);
        }
      }

      // Check for existing discount
      if (orderLineId) {
        // Before anything is shown: if the vendor has asked for automatic
        // offers, put the live one on now. Idempotent, and it does nothing when
        // the setting is off or a discount is already there — so this reads as
        // "make sure the bill is right" rather than "apply something".
        //
        // Deliberately here and not at settlement: split settlement takes
        // payment amounts a person worked out against a total they were shown,
        // and discounting underneath those would leave them adding up to more
        // than the bill.
        await ensureOffersOnBill(orderLineId);

        try {
          const discountResponse = await api.get(`/api/order-lines/${orderLineId}/discount`);
          if (discountResponse.data?.hasDiscount) {
            setCurrentDiscount({
              type: discountResponse.data.discountType,
              value: discountResponse.data.discountValue,
              amount: discountResponse.data.discountAmount,
              reason: discountResponse.data.discountReason
            });
            totalAmount = discountResponse.data.totalAmount || totalAmount;
          } else {
            setCurrentDiscount(null);
          }
        } catch (err) {
          setCurrentDiscount(null);
        }

        // What is on offer for this bill. Read-only, so a failure here must not
        // stop the discount dialog opening — staff can still key one in.
        try {
          const offersResponse = await api.get(`/api/order-lines/${orderLineId}/offers`);
          setBillOffers({
            available: !!offersResponse.data?.available,
            discountAmount: Number(offersResponse.data?.discountAmount || 0),
            names: (offersResponse.data?.offers || []).map((o: { name: string }) => o.name),
          });
        } catch {
          setBillOffers(null);
        }

        // Where this guest stands on the offers they earn over time. Read-only,
        // and a failure must not stop the dialog opening — it is something to
        // mention, not something the bill depends on.
        try {
          const loyaltyResponse = await api.get(`/api/order-lines/${orderLineId}/loyalty`);
          setBillLoyalty({
            recognised: !!loyaltyResponse.data?.recognised,
            currentDiscount: loyaltyResponse.data?.currentDiscount || null,
            standings: loyaltyResponse.data?.standings || [],
          });
        } catch {
          setBillLoyalty(null);
        }
      }

      setDiscountTable({
        table: tableData.table,
        orderLineId,
        totalAmount
      });
      setDiscountType('PERCENTAGE');
      setDiscountValue('');
      setDiscountReason('');
      setCouponCode('');
      setShowDiscountDialog(true);
    } catch (error) {
      console.error('Error opening discount dialog:', error);
      toast.error('Failed to open discount dialog');
    }
  };

  // Handle applying discount
  const handleApplyDiscount = async () => {
    if (!discountTable?.orderLineId) {
      toast.error('Order line not found');
      return;
    }

    if (!discountValue || parseFloat(discountValue) <= 0) {
      toast.error('Please enter a valid discount value');
      return;
    }

    if (!discountReason.trim()) {
      toast.error('Discount reason is mandatory');
      return;
    }

    if (discountType === 'COUPON' && !couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    if (discountType === 'PERCENTAGE' && parseFloat(discountValue) > 100) {
      toast.error('Percentage cannot exceed 100%');
      return;
    }

    setIsApplyingDiscount(true);
    try {
      const response = await api.post(`/api/order-lines/${discountTable.orderLineId}/discount`, {
        discountType,
        discountValue: parseFloat(discountValue),
        reason: discountReason.trim(),
        couponCode: discountType === 'COUPON' ? couponCode.trim() : undefined
      });

      if (response.data) {
        toast.success(`Discount applied: ${CURRENCY.symbol}${response.data.discountAmount?.toFixed(2) || '0'} off`);
        setShowDiscountDialog(false);

        // Update the current discount state
        setCurrentDiscount({
          type: discountType,
          value: parseFloat(discountValue),
          amount: response.data.discountAmount,
          reason: discountReason
        });

        // Refresh table data
        await fetchFloorLayout(true);
      }
    } catch (error: any) {
      console.error('Failed to apply discount:', error);
      toast.error(error.response?.data?.error || 'Failed to apply discount');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // Handle removing discount
  const handleRemoveDiscount = async () => {
    if (!discountTable?.orderLineId) {
      toast.error('Order line not found');
      return;
    }

    setIsApplyingDiscount(true);
    try {
      await api.delete(`/api/order-lines/${discountTable.orderLineId}/discount`);
      toast.success('Discount removed');
      setCurrentDiscount(null);
      setShowDiscountDialog(false);

      // Refresh table data
      await fetchFloorLayout(true);
    } catch (error: any) {
      console.error('Failed to remove discount:', error);
      toast.error(error.response?.data?.error || 'Failed to remove discount');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  /**
   * Ask the server what a typed coupon code is worth on this bill.
   *
   * Read-only and debounced, so it is safe to run while staff are still typing.
   * The answer can go stale — another till may take the last use a moment later
   * — which is why applying re-proves everything rather than trusting this.
   */
  useEffect(() => {
    if (!showDiscountDialog || discountType !== 'COUPON' || !discountTable?.orderLineId) {
      setCouponCheck(null);
      return;
    }
    const code = couponCode.trim();
    if (code.length < 3) { setCouponCheck(null); return; }

    let cancelled = false;
    setCheckingCoupon(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/api/order-lines/${discountTable.orderLineId}/coupon/check`,
          { params: { code } },
        );
        if (!cancelled) {
          setCouponCheck({
            valid: !!data.valid,
            message: data.message ?? null,
            discountAmount: Number(data.discountAmount || 0),
            offerName: data.offerName ?? null,
          });
        }
      } catch {
        if (!cancelled) setCouponCheck(null);
      } finally {
        if (!cancelled) setCheckingCoupon(false);
      }
    }, 400);

    return () => { cancelled = true; clearTimeout(timer); setCheckingCoupon(false); };
  }, [showDiscountDialog, discountType, couponCode, discountTable?.orderLineId]);

  /**
   * What is still missing before a manual discount can be applied.
   *
   * The Apply button is disabled until the required fields are filled, and a
   * disabled button explains nothing — you press it, nothing happens, and there
   * is no way to tell why. Saying it plainly is the whole point.
   */
  const manualDiscountBlockers = (): string | null => {
    const missing: string[] = [];
    if (!discountValue || parseFloat(discountValue) <= 0) {
      missing.push(discountType === 'PERCENTAGE' ? 'a percentage' : 'an amount');
    }
    if (discountType === 'COUPON' && !couponCode.trim()) missing.push('a coupon code');
    if (!discountReason.trim()) missing.push('a reason');
    if (missing.length === 0) return null;
    const list = missing.length === 1
      ? missing[0]
      : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`;
    return `Enter ${list} to apply this discount`;
  };

  /**
   * Put a recognised coupon on the bill.
   *
   * Sends the CODE and nothing else. A till that could name its own figure would
   * make the coupon's use limits, expiry and minimum spend advisory.
   */
  const handleApplyCoupon = async () => {
    if (!discountTable?.orderLineId) return;
    setApplyingCoupon(true);
    try {
      const { data } = await api.post(
        `/api/order-lines/${discountTable.orderLineId}/coupon`,
        { code: couponCode.trim() },
      );
      toast.success(`${data.offerName} applied`);
      setShowDiscountDialog(false);
      await fetchFloorLayout(true);
    } catch (error) {
      // The API answers with a sentence a guest can be told.
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || 'Could not apply that code');
    } finally {
      setApplyingCoupon(false);
    }
  };

  /**
   * Make sure a live offer is on this bill before a total is shown.
   *
   * Idempotent: it does nothing unless the vendor has asked for automatic
   * offers AND the bill has no discount yet. Called from every place that is
   * about to show what the guest owes, so the figure is right the first time it
   * appears rather than being corrected later.
   *
   * Never throws — a bill that cannot be checked for offers must still open.
   */
  const ensureOffersOnBill = async (orderLineId?: number | null) => {
    if (!orderLineId) return;
    try {
      await api.post(`/api/order-lines/${orderLineId}/offers/ensure`, {});
    } catch {
      // Intentionally silent: this is a best-effort improvement to the bill,
      // never a reason to stop staff seeing it.
    }
  };

  /**
   * Put the offer the server found onto this bill.
   *
   * Sends no figure. The server works out what applies again — the happy hour
   * may have ended since the dialog opened, or another till may have taken a
   * coupon's last use — so anything this screen quoted is only a preview.
   */
  const handleApplyOffer = async () => {
    if (!discountTable?.orderLineId) {
      toast.error('Order line not found');
      return;
    }

    setApplyingOffer(true);
    try {
      const { data } = await api.post(`/api/order-lines/${discountTable.orderLineId}/offers/apply`, {});
      toast.success(`${(data.offers || []).join(' + ')} applied`);
      setShowDiscountDialog(false);
      await fetchFloorLayout(true);
    } catch (error) {
      // The API answers with a sentence — "That offer has just run out" — which
      // is more use than anything this screen could invent.
      const message = (error as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      toast.error(message || 'Could not apply the offer');
    } finally {
      setApplyingOffer(false);
    }
  };

  /**
   * Give the guest a reward they have earned.
   *
   * Sends no figure — the server prices the reward from the offer and the bill,
   * so a till cannot name its own amount.
   *
   * `replaceExisting` is the guest's answer, not a convenience. A bill holds one
   * discount, so somebody with a full card who came in during happy hour has to
   * choose. Without it the server refuses and says so; with it, the server has
   * read both figures out at the table and the guest asked for the reward.
   * Either way it can be undone: taking the discount off puts the visits back.
   */
  /**
   * Spend a guest's points or cashback against this bill.
   *
   * Sends no amount, so the server spends as much as the bill can absorb and
   * the guest keeps the rest. Partial is the point of a balance: 200 of their
   * 500 today, the other 300 next time is a reason to come back.
   */
  const handleSpendBalance = async (promotionId: number, replaceExisting: boolean) => {
    if (!discountTable?.orderLineId) {
      toast.error('Order line not found');
      return;
    }

    setClaimingReward(promotionId);
    try {
      const { data } = await api.post(
        `/api/order-lines/${discountTable.orderLineId}/loyalty/${promotionId}/spend`,
        { replaceExisting },
      );
      toast.success(`${data.spent} spent — ${CURRENCY.symbol}${Number(data.discountAmount).toFixed(2)} off`);
      setShowDiscountDialog(false);
      await fetchFloorLayout(true);
    } catch (error) {
      const message = (error as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      toast.error(message || 'Could not spend the balance');
    } finally {
      setClaimingReward(null);
    }
  };

  const handleGiveReward = async (promotionId: number, replaceExisting: boolean) => {
    if (!discountTable?.orderLineId) {
      toast.error('Order line not found');
      return;
    }

    setClaimingReward(promotionId);
    try {
      const { data } = await api.post(
        `/api/order-lines/${discountTable.orderLineId}/loyalty/${promotionId}/claim`,
        { replaceExisting },
      );
      toast.success(`${data.rewardLabel} — ${CURRENCY.symbol}${Number(data.discountAmount).toFixed(2)} off`);
      setShowDiscountDialog(false);
      await fetchFloorLayout(true);
    } catch (error) {
      // The API answers with a sentence a guest can be told — "the guest has
      // not ordered the item this reward gives" — which beats anything this
      // screen could invent.
      const message = (error as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      toast.error(message || 'Could not give the reward');
    } finally {
      setClaimingReward(null);
    }
  };

  // Handle table transfer
  const handleTransferOrders = async (targetTableId: string, reason?: string) => {
    if (!transferSourceTable) {
      toast.error('Source table not found');
      return;
    }

    setIsTransferring(true);
    try {
      const response = await transferTableOrders(
        transferSourceTable.id,
        targetTableId,
        reason
      );

      if (response.success) {
        toast.success(response.message || `Orders transferred successfully`);
        setShowTransferDialog(false);
        setTransferSourceTable(null);

        // Refresh floor data to update both tables
        await fetchFloorLayout(true);
      } else {
        toast.error(response.message || 'Failed to transfer orders');
      }
    } catch (error: any) {
      console.error('Transfer failed:', error);
      const errorMessage = error.response?.data?.message || 'Failed to transfer orders';
      toast.error(errorMessage);
    } finally {
      setIsTransferring(false);
    }
  };

  // Handle transfer from navigation mode (when user clicks a table after navigating from transfer dialog)
  const handleTransferFromNavigation = async (targetTableId: string, targetTableName: string) => {
    if (!transferMode || isTransferring) return;

    setIsTransferring(true);
    try {
      const response = await transferTableOrders(
        transferMode.sourceTableId,
        targetTableId
      );

      if (response.success) {
        toast.success(`Orders transferred from ${transferMode.sourceTableName} to ${targetTableName}`);
        setTransferMode(null);
        onTransferComplete?.();
        // Refresh floor data to update both tables
        await fetchFloorLayout(true);
      } else {
        toast.error(response.message || 'Failed to transfer orders');
      }
    } catch (error: any) {
      console.error('Transfer failed:', error);
      const errorMessage = error.response?.data?.message || 'Failed to transfer orders';
      toast.error(errorMessage);
    } finally {
      setIsTransferring(false);
    }
  };

  // Check if table has upcoming reservation before allowing order
  const confirmReservationOrder = async (tableData: any): Promise<boolean> => {
    const nextRes = tableData.nextReservation;
    if (nextRes && (nextRes.status === 'PENDING' || nextRes.status === 'CONFIRMED')) {
      const startTime = new Date(`2000-01-01T${nextRes.startTime}`);
      const timeStr = startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      return await confirm({
        title: 'Table Has Upcoming Reservation',
        description: `This table is reserved for ${nextRes.customerName} (${nextRes.partySize} guests) at ${timeStr}. Do you want to proceed with a new order?`,
        confirmText: 'Proceed with Order',
        cancelText: 'Cancel'
      });
    }
    return true;
  };

  // EPOS table selection mode
  const isEposSelectMode = false; // epos removed — floor-plan tap uses default table behaviour

  // Handle table click - check if we're in transfer or EPOS select mode
  const handleTableClick = (tableData: any) => {
    if (tableData.table?.isDeleted) return;

    // EPOS select mode — all tables select directly (actions live in EPOS context bar)
    if (isEposSelectMode) {
      (async () => {
        if (!(await confirmReservationOrder(tableData))) return;
        const tableId = tableData.table?.id;
        const tableName = tableData.table?.displayNumber || tableData.table?.name || `Table ${tableId}`;
        const floorName = tableData.floorName || '';
        const sectionName = tableData.sectionName || '';
        const qrCode = tableData.table?.qrCode?.code;
        const activeOrders = (tableData.activeOrders || []).filter(
          (o: { status: string }) => o.status !== 'COMPLETED' && o.status !== 'CANCELLED'
        );
        if (onTableSelect) {
          onTableSelect({ id: tableId, name: tableName, displayNumber: tableName, floorName, sectionName, qrCode, activeOrders });
        } else {
          navigate(`/vendor/epos?tableId=${tableId}&tableName=${encodeURIComponent(tableName)}&floorName=${encodeURIComponent(floorName)}&sectionName=${encodeURIComponent(sectionName)}`);
        }
      })();
      return;
    }

    // Merge mode — multi-select tables on the floor plan
    if (mergeMode?.active) {
      const tableId = String(tableData.table?.id);
      if (tableId === mergeMode.sourceTableId) {
        toast.error('This is the source table');
        return;
      }
      // Only allow selecting unoccupied, ungrouped tables
      if (tableData.isOccupied) {
        toast.error('Cannot merge with an occupied table');
        return;
      }
      if (tableGroupMap.has(tableId)) {
        toast.error('This table is already in a merge group');
        return;
      }
      // Toggle selection
      setMergeMode(prev => {
        if (!prev) return prev;
        const next = new Set(prev.selectedTableIds);
        if (next.has(tableId)) next.delete(tableId);
        else next.add(tableId);
        return { ...prev, selectedTableIds: next };
      });
      return;
    }

    // Helper to toggle reservation table selection
    const selectReservationTable = (id: any) => {
      const tableId = Number(id);
      setReservationMode(prev => {
        if (!prev) return prev;
        const next = new Set(prev.selectedTableIds);
        // Check both number and original type
        const has = next.has(tableId) || next.has(id);
        if (has) { next.delete(tableId); next.delete(id); }
        else if (!prev.needsMerge && next.size > 0) {
          next.clear();
          next.add(tableId);
        } else {
          next.add(tableId);
        }
        return { ...prev, selectedTableIds: next };
      });
    };

    // Waitlist seat mode — single-click a free table to assign + (optionally) seat
    if (waitlistMode?.active) {
      const tableId = Number(tableData.table?.id);
      const tableName = tableData.table?.displayNumber || tableData.table?.name || `Table ${tableId}`;
      const isOccupied = tableData.isOccupied || (tableData.activeOrderCount && tableData.activeOrderCount > 0);
      if (isOccupied) {
        toast.error(`${tableName} is occupied`);
        return;
      }
      if (tableData.status === 'reserved') {
        toast.error(`${tableName} is reserved for an upcoming booking`);
        return;
      }
      const { waitlistId, action, customerName, partySize } = waitlistMode;
      (async () => {
        if (waitlistSubmitting) return;
        if (!(await confirm({
          title: action === 'seat' ? `Seat ${customerName} at ${tableName}?` : `Assign ${tableName} to ${customerName}?`,
          description: `Party of ${partySize}. ${action === 'seat' ? 'A new session will be opened at this table.' : 'Customer will be notified that the table is ready.'}`,
          confirmText: action === 'seat' ? 'Seat Customer' : 'Notify Customer',
          cancelText: 'Cancel',
        }))) return;
        setWaitlistSubmitting(true);
        try {
          const { waitlistApi } = await import('../api/reservationApi');
          await waitlistApi.notify(waitlistId, tableId);
          if (action === 'seat') {
            await waitlistApi.seat(waitlistId);
            toast.success(`${customerName} seated at ${tableName}`);
          } else {
            toast.success(`${customerName} notified — ${tableName} is ready`);
          }
          setWaitlistMode(null);
          fetchFloorLayout(true);
          navigate('/vendor/reservations');
        } catch (err: any) {
          toast.error(err?.response?.data?.error || err?.message || 'Failed to assign table');
        } finally {
          setWaitlistSubmitting(false);
        }
      })();
      return;
    }

    // Reservation mode — select/deselect tables for reservation
    if (reservationMode?.active) {
      const rawId = tableData.table?.id;
      const numId = Number(rawId);
      // Normalize: check both number and string variants since IDs may be either type
      const checkSet = (set: Set<any>, id: any) => set.has(id) || set.has(Number(id)) || set.has(String(id));
      const isAvailable = checkSet(reservationMode.availableTableIds, rawId);
      // If availability hasn't loaded yet, allow selection optimistically
      const availabilityNotLoaded = reservationMode.availableTableIds.size === 0;
      if (!isAvailable && !availabilityNotLoaded) {
        toast.error(`Table has a conflicting reservation on ${reservationMode.date} at ${reservationMode.startTime}`);
        return;
      }

      // Check if table is currently occupied — confirm before selecting
      const isAlreadySelected = checkSet(reservationMode.selectedTableIds, rawId);
      if (tableData.isOccupied && !isAlreadySelected) {
        const tableName = tableData.table?.displayNumber || tableData.table?.name || `Table ${tableId}`;
        setReservationOccupiedConfirm({ tableId: numId, tableName });
        return;
      }

      selectReservationTable(rawId);
      return;
    }

    if (transferMode?.active && tableData.table?.id !== transferMode.sourceTableId) {
      // In transfer mode - show confirmation dialog before transferring
      const tableName = tableData.table?.displayNumber || tableData.table?.name || 'Unknown';
      setTransferConfirmation({
        show: true,
        targetTableId: tableData.table.id,
        targetTableName: tableName
      });
    } else if (transferMode?.active && tableData.table?.id === transferMode.sourceTableId) {
      // Clicked on source table - show message
      toast.error('Cannot transfer to the same table');
    } else {
      // Normal mode - open table details
      setSelectedTable(tableData);
    }
  };

  // Handle confirmed transfer from confirmation dialog
  const handleConfirmedTransfer = async () => {
    if (!transferConfirmation) return;
    setTransferConfirmation(null);
    await handleTransferFromNavigation(transferConfirmation.targetTableId, transferConfirmation.targetTableName);
  };

  // Enter reservation mode — overlay banner over the user's current /vendor/tables
  // view (Grid by default). Don't force a viewMode change so layout stays familiar.
  const enterReservationMode = useCallback((preselectedTableId?: number) => {
    const today = todayLocalIso();
    setReservationMode({
      active: true,
      selectedTableIds: preselectedTableId ? new Set([preselectedTableId]) : new Set(),
      availableTableIds: new Set(),
      needsMerge: false,
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      partySize: 2,
      date: today,
      startTime: '12:00',
      durationMinutes: 90,
      specialRequests: '',
      source: 'DASHBOARD',
    });
    setSelectedTable(null);
  }, []);

  // Auto-enter waitlist seat mode if navigated with ?waitlistSeat=ID&action=seat|notify
  useEffect(() => {
    const waitlistId = searchParams.get('waitlistSeat');
    if (waitlistId && !waitlistMode?.active) {
      const action = (searchParams.get('action') === 'notify' ? 'notify' : 'seat') as 'notify' | 'seat';
      setSearchParams({}, { replace: true });
      (async () => {
        try {
          const { waitlistApi } = await import('../api/reservationApi');
          const entry = await waitlistApi.getById(Number(waitlistId));
          setWaitlistMode({
            active: true,
            waitlistId: Number(waitlistId),
            customerName: entry.customerName,
            partySize: entry.partySize,
            action,
          });
          // Keep the user's current viewMode (default Grid). Banner overlays the layout.
          setSelectedTable(null);
        } catch (err: any) {
          toast.error('Could not load waitlist entry');
        }
      })();
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-enter reservation EDIT mode if navigated with ?editReservation=ID.
  // Loads the existing reservation and populates the same banner used for create.
  useEffect(() => {
    const editId = searchParams.get('editReservation');
    if (editId && !reservationMode?.active) {
      setSearchParams({}, { replace: true });
      (async () => {
        try {
          const { reservationApi } = await import('../api/reservationApi');
          const r = await reservationApi.getById(Number(editId));
          const tableIds = new Set<number>();
          if (r.tableId) tableIds.add(r.tableId);
          if (r.tableIds && Array.isArray(r.tableIds)) r.tableIds.forEach((id: number) => tableIds.add(id));
          setReservationMode({
            active: true,
            editingReservationId: r.id,
            selectedTableIds: tableIds,
            availableTableIds: new Set(),
            needsMerge: tableIds.size > 1,
            customerName: r.customerName || '',
            customerPhone: r.customerPhone || '',
            customerEmail: r.customerEmail || '',
            partySize: r.partySize || 2,
            date: r.reservationDate || todayLocalIso(),
            startTime: (r.startTime || '12:00').slice(0, 5),
            durationMinutes: r.durationMinutes || 90,
            specialRequests: r.specialRequests || '',
            source: r.source || 'DASHBOARD',
          });
          setSelectedTable(null);
        } catch (err: any) {
          toast.error('Could not load reservation');
        }
      })();
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-enter reservation mode if navigated with ?reservationMode=true
  useEffect(() => {
    if (searchParams.get('reservationMode') === 'true' && !reservationMode?.active) {
      const date = searchParams.get('date') || undefined;
      const time = searchParams.get('time') || undefined;
      // Clear query params
      setSearchParams({}, { replace: true });
      // Enter reservation mode with optional preselected date/time. Keep current viewMode.
      const today = todayLocalIso();
      setReservationMode({
        active: true,
        selectedTableIds: new Set(),
        availableTableIds: new Set(),
        needsMerge: false,
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        partySize: 2,
        date: date || today,
        startTime: time || '12:00',
        durationMinutes: 90,
        specialRequests: '',
        source: 'DASHBOARD',
      });
      setSelectedTable(null);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch reservation settings once when entering reservation mode
  useEffect(() => {
    if (!reservationMode?.active) return;
    if (reservationSettings) return; // Already fetched
    (async () => {
      try {
        const { reservationSettingsApi } = await import('../api/reservationApi');
        const settings = await reservationSettingsApi.get();
        setReservationSettings({
          maxPartySize: settings.maxPartySize || 20,
          maxAdvanceBookingDays: settings.maxAdvanceBookingDays || 30,
          openingTime: settings.openingTime || '10:00',
          lastReservationTime: settings.lastReservationTime || '22:00',
          minAdvanceBookingHours: settings.minAdvanceBookingHours ?? 1,
        });
      } catch { /* ignore — use defaults */ }
    })();
  }, [reservationMode?.active, reservationSettings]);

  // Check availability when reservation mode params change
  useEffect(() => {
    if (!reservationMode?.active) return;
    const { date, startTime, durationMinutes, partySize } = reservationMode;
    if (!date || !startTime) return;

    const checkAvail = async () => {
      setReservationCheckLoading(true);
      try {
        const [startH, startM] = startTime.split(':').map(Number);
        const totalMins = startH * 60 + startM + durationMinutes;
        const endH = Math.floor(totalMins / 60) % 24;
        const endM = totalMins % 60;
        const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;

        const { availabilityApi } = await import('../api/reservationApi');
        const [tables, suggestions] = await Promise.all([
          availabilityApi.getTables(date, startTime, endTime),
          availabilityApi.suggestTables(date, startTime, endTime, partySize),
        ]);

        setReservationAvailableTables(tables);
        setReservationSuggestions(suggestions);

        const availableIds = new Set(tables.map((t: any) => t.id));
        const largestCap = tables.reduce((max: number, t: any) => Math.max(max, t.seatingCapacity), 0);

        setReservationMode(prev => prev ? {
          ...prev,
          availableTableIds: availableIds,
          needsMerge: partySize > largestCap && tables.length > 0,
        } : null);
      } catch (err) {
        console.error('Error checking reservation availability:', err);
      } finally {
        setReservationCheckLoading(false);
      }
    };

    const debounce = setTimeout(checkAvail, 500);
    return () => clearTimeout(debounce);
  }, [reservationMode?.active, reservationMode?.date, reservationMode?.startTime, reservationMode?.durationMinutes, reservationMode?.partySize]);

  // Submit reservation from floor plan mode
  const handleSubmitReservation = async () => {
    if (!reservationMode) return;
    const { customerName, customerPhone, customerEmail, partySize, date, startTime, durationMinutes, selectedTableIds, specialRequests, source } = reservationMode;

    if (!customerName.trim()) { toast.error('Customer name is required'); return; }
    if (!customerPhone.trim()) { toast.error('Phone number is required'); return; }
    if (selectedTableIds.size === 0) { toast.error('Please select at least one table'); return; }

    setReservationSubmitting(true);
    try {
      const { reservationApi } = await import('../api/reservationApi');
      const tableIdArray = Array.from(selectedTableIds);
      const payload = {
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        customerEmail: customerEmail.trim() || undefined,
        partySize,
        date,
        startTime,
        durationMinutes,
        tableId: tableIdArray.length === 1 ? tableIdArray[0] : undefined,
        tableIds: tableIdArray.length > 1 ? tableIdArray : undefined,
        specialRequests: specialRequests.trim() || undefined,
        source: source as any,
      };
      const isUpdate = !!reservationMode?.editingReservationId;
      if (isUpdate) {
        await reservationApi.update(reservationMode.editingReservationId!, payload);
      } else {
        await reservationApi.create(payload);
      }
      // Capture a summary for the success dialog before we clear the mode.
      const tableNames = tableIdArray
        .map(id => reservationAvailableTables.find((t: any) => t.id === id)?.name || `Table ${id}`)
        .join(' + ');
      setReservationSuccess({
        updated: isUpdate, customerName: customerName.trim(), partySize, tables: tableNames, date, startTime,
      });
      setReservationMode(null);
      setReservationAvailableTables([]);
      setReservationSuggestions([]);
      fetchFloorLayout(true);
    } catch (err: any) {
      const data = err.response?.data;
      // Show specific field errors if available (e.g., "date: Reservation date must be today or in the future")
      if (data?.errors && typeof data.errors === 'object') {
        const fieldErrors = Object.entries(data.errors).map(([, msg]) => `${msg}`).join('. ');
        toast.error(fieldErrors, { duration: 7000 });
      } else {
        // ReservationController returns { error: <message> } on 400 — include it.
        toast.error(data?.error || data?.userMessage || data?.message || err.message || 'Failed to create reservation', { duration: 7000 });
      }
    } finally {
      setReservationSubmitting(false);
    }
  };

  // Get floors data for transfer dialog
  const getFloorsForTransfer = useCallback((): Floor[] => {
    // If using cached floor data
    const floorsData: Floor[] = [];

    for (const summary of floorSummaries) {
      const floorKey = `floor_${summary.id}`;
      const cachedData = cachedFloorData.get(floorKey);

      if (cachedData) {
        const sections: Section[] = cachedData.sections.map(s => ({
          id: s.section.id,
          name: s.section.name,
          floorId: String(summary.id),
          tables: s.tables.map(t => ({
            id: t.table.id,
            name: t.table.name,
            displayNumber: t.table.displayNumber,
            sectionId: s.section.id,
            qrCode: t.table.qrCode,
            isDeleted: t.table.isDeleted
          })),
          isDeleted: s.section.isDeleted
        }));

        floorsData.push({
          id: String(summary.id),
          name: summary.name,
          floorNumber: summary.floorNumber,
          vendorId: String(summary.vendorId),
          sections,
          isDeleted: summary.isDeleted
        });
      }
    }

    // If no cached data, fall back to floorLayout
    if (floorsData.length === 0 && Object.keys(floorLayout).length > 0) {
      for (const key of Object.keys(floorLayout).filter(k => k.startsWith('floor_'))) {
        const floorData = floorLayout[key];
        if (floorData.floor) {
          floorsData.push({
            ...floorData.floor,
            sections: floorData.sections.map(s => ({
              ...s.section,
              tables: s.tables.map(t => t.table)
            }))
          });
        }
      }
    }

    return floorsData;
  }, [floorSummaries, cachedFloorData, floorLayout, floorDataVersion]);

  const handleSettleBill = async (tableData: {
    table: Table;
    activeOrders?: Array<any>;
    completedOrders?: Array<any>;
    isOccupied: boolean;
    status: string;
    activeOrderCount?: number;
  }, paymentMethod?: string) => {
    try {
      setIsSettlingBill(true);

      // OPTIMISTIC UPDATE: Update table status immediately in cached data
      // This gives immediate visual feedback to the user who performs the action
      // Socket.IO will sync with other users
      const tableId = tableData.table.id;
      setCachedFloorData(prev => {
        const newCache = new Map(prev);
        // Find and update the table in cached floor data
        for (const [floorId, floorData] of newCache) {
          const updatedSections = floorData.sections.map(sectionData => {
            const updatedTables = sectionData.tables.map(tableEntry => {
              // Null check for tableEntry.table before accessing id
              if (tableEntry.table && tableEntry.table.id === tableId) {
                return {
                  ...tableEntry,
                  activeOrderCount: 0,
                  isOccupied: false,
                  status: 'available'
                };
              }
              return tableEntry;
            });
            return { ...sectionData, tables: updatedTables };
          });
          newCache.set(floorId, { ...floorData, sections: updatedSections });
        }
        return newCache;
      });

      // Close table details immediately so the user only sees a loader until invoice
      setSelectedTable(null);
      setShowQrInDetails(false);

      // Get all orders eligible for settlement (include SERVED so steward flow can settle)
      let activeOrders = (tableData.activeOrders || [])
        .filter((order) => ['PENDING', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED'].includes(order.status));

      const isInMergeGroup = tableGroupMap.has(String(tableId));

      // If activeOrders is empty but we know there are orders, fetch from API
      // For merged tables, fetch orders from ALL tables in the group
      if (activeOrders.length === 0 && (tableData.activeOrderCount && tableData.activeOrderCount > 0 || tableData.isOccupied)) {
        console.log('[handleSettleBill] Fetching orders from API...');
        try {
          if (isInMergeGroup) {
            activeOrders = await fetchMergedGroupOrders(String(tableId));
            console.log(`[handleSettleBill] Fetched ${activeOrders.length} orders from merged group`);
          } else {
            const ordersResponse = await api.get(`/api/tables/${tableData.table.id}/orders`, {
              params: { status: ['PENDING', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED'] }
            });
            activeOrders = ordersResponse.data || [];
            console.log('[handleSettleBill] Fetched orders from API:', activeOrders.length);
          }
        } catch (fetchError) {
          console.error('[handleSettleBill] Failed to fetch orders:', fetchError);
          toast.error('Failed to fetch table orders');
          return;
        }
      } else if (isInMergeGroup && activeOrders.length > 0) {
        // Even if this table has orders, also fetch from other merged tables
        try {
          const allGroupOrders = await fetchMergedGroupOrders(String(tableId));
          // Merge with local orders, dedup by ID
          const orderIds = new Set(activeOrders.map((o: any) => o.id));
          for (const order of allGroupOrders) {
            if (!orderIds.has(order.id)) {
              activeOrders.push(order);
              orderIds.add(order.id);
            }
          }
          console.log(`[handleSettleBill] Total orders after merge group consolidation: ${activeOrders.length}`);
        } catch {
          // Continue with just this table's orders if group fetch fails
        }
      }

      // If nothing to settle but there are completed orders, just show invoice
      if (activeOrders.length === 0) {
        const completedId = tableData.completedOrders?.[0]?.id;
        if (completedId) {
          setSelectedOrderForInvoice(completedId);
          setShowInvoiceModal(true);
        } else {
          toast.error('No active orders to settle');
        }
        return;
      }

      // Unified visual feedback handled by Backdrop; avoid duplicate toasts

      // Use the new settle-bill endpoint to complete all orders at once
      const orderIds = activeOrders.map((order) => order.id);

      let settleResponse;
      try {
        settleResponse = await api.post('/orders/settle-bill', { orderIds, paymentMethod: paymentMethod || 'CASH' });
      } catch (err) {
        console.error('Failed to settle bill:', err);
        toast.error('Failed to settle bill. Please try again.');
        return;
      }

      // Check if any orders were successfully completed
      const settledCount = settleResponse.data.settledCount || 0;

      if (settledCount === 0) {
        toast.error('Failed to complete any orders');
        return;
      }

      // Success toast suppressed to keep UX minimal; invoice will appear shortly

      // Update the active tables to remove settled orders
      const settledOrderIds = settleResponse.data.settledOrders?.map((o: { id: number }) => o.id) || [];

      // Show the invoice immediately - prefer consolidated invoice if available
      const primaryOrderId = settleResponse.data.primaryOrderId;
      if (primaryOrderId) {
        // Use the consolidated invoice's primary order
        setSelectedOrderForInvoice(primaryOrderId);
        setShowInvoiceModal(true);
      } else if (settledOrderIds.length > 0) {
        // Fallback to first settled order
        setSelectedOrderForInvoice(settledOrderIds[0]);
        setShowInvoiceModal(true);
      }

      // Auto-print invoice if setting is enabled
      if (configuration?.autoPrintInvoiceOnSettle) {
        const invoiceOrderId = primaryOrderId || settledOrderIds[0];
        if (invoiceOrderId) {
          // Fetch and print in background - don't block UI
          api.get(`/invoices/by-order/${invoiceOrderId}`)
            .then((invoiceResponse) => {
              const invoiceData = invoiceResponse.data as InvoiceData;
              if (invoiceData) {
                printToLocalPrinter(invoiceData)
                  .then((result) => {
                    if (result.success) {
                      console.log('[AutoPrint] Invoice printed successfully');
                    } else {
                      console.warn('[AutoPrint] Invoice print failed:', result.error);
                    }
                  })
                  .catch((err) => console.error('[AutoPrint] Invoice print error:', err));
              }
            })
            .catch((err) => console.error('[AutoPrint] Failed to fetch invoice:', err));
        }
      }

      // Close the selected table details
      setSelectedTable(null);
      setShowQrInDetails(false);

      // Auto-unmerge: fire-and-forget — don't block UI for this
      const groupInfo = tableGroupMap.get(String(tableId));
      if (groupInfo) {
        api.post(`/api/table-groups/${groupInfo.groupId}/close`)
          .then(() => {
            console.log(`[AutoUnmerge] Released group #${groupInfo.groupId} after settle`);
            fetchTableGroups();
          })
          .catch(err => console.warn('[AutoUnmerge] Failed to release group:', err));
      }

      // Refresh floor layout in background (non-blocking)
      fetchFloorLayout(true).catch(err => console.error('Background floor refresh failed:', err));
    } catch (error) {
      console.error('Error settling bill:', error);
      toast.error('Failed to settle bill');
    }
    finally {
      setIsSettlingBill(false);
    }
  };

  const closeTableDetails = () => {
    setSelectedTable(null);
    setShowQrInDetails(false);
    setShowTableInfo(false);
    if (qrImageUrl) {
      URL.revokeObjectURL(qrImageUrl);
      setQrImageUrl(null);
    }
  };

  const handleCancelOrder = (orderId: number) => {
    setConfirmOrderAction({ open: true, type: 'cancel', orderId });
  };

  const handleServeOrder = (orderId: number) => {
    setConfirmOrderAction({ open: true, type: 'serve', orderId });
  };

  // Edit handlers
  const handleEditFloor = (floor: { id: string, name: string, floorNumber?: number }) => {
    setEditingFloor(floor);
    setFloorName(floor.name);
    setFloorNumber(floor.floorNumber || 0);
    setOpenEditFloorDialog(true);
  };

  const handleEditSection = (section: { id: string, name: string }, floorId: string) => {
    setEditingSection({ ...section, floorId });
    setSectionName(section.name);
    setOpenEditSectionDialog(true);
  };

  const handleEditTable = (table: { id: string, name: string, displayNumber?: string }, sectionId: string) => {
    setEditingTable({ ...table, sectionId });
    setTableName(table.name);
    setTableDisplayNumber(table.displayNumber || '');
    setOpenEditTableDialog(true);
  };

  const handleUpdateTable = async () => {
    if (!editingTable || !tableName.trim()) {
      toast.error('Please enter a table name');
      return;
    }

    try {
      // Update table name
      await updateTable(editingTable.id, {
        name: tableName.trim(),
        sectionId: editingTable.sectionId
      });

      // Update display number if provided
      if (tableDisplayNumber.trim()) {
        await updateTableDisplayNumber(editingTable.id, tableDisplayNumber.trim());
      }

      toast.success('Table updated successfully');
      setOpenEditTableDialog(false);
      setEditingTable(null);
      setTableName('');
      setTableDisplayNumber('');
      fetchFloorLayout(true);
    } catch (error) {
      console.error('Failed to update table:', error);
      toast.error('Failed to update table');
    }
  };

  // Delete handlers
  const handleDeleteFloor = async (floorId: string, floorName: string) => {
    // Always show confirmation dialog before deletion
    setDeletingEntity({
      type: 'floor',
      id: floorId,
      name: floorName,
      hasPendingOrders: false
    });
    setShowCascadeOption(false);
    setOpenDeleteConfirmDialog(true);
  };

  const handleDeleteSection = async (sectionId: string, sectionName: string) => {
    // Always show confirmation dialog before deletion
    setDeletingEntity({
      type: 'section',
      id: sectionId,
      name: sectionName,
      hasPendingOrders: false
    });
    setShowCascadeOption(false);
    setOpenDeleteConfirmDialog(true);
  };

  const handleDeleteTable = async (tableId: string, tableName: string) => {
    // Always show confirmation dialog before deletion
    setDeletingEntity({
      type: 'table',
      id: tableId,
      name: tableName,
      hasPendingOrders: false
    });
    setShowCascadeOption(false); // Tables don't have cascade delete
    setOpenDeleteConfirmDialog(true);
  };

  // Update handlers
  const handleUpdateFloor = async () => {
    if (!editingFloor || !floorName.trim()) {
      toast.error('Please enter a floor name');
      return;
    }

    try {
      await updateFloor(editingFloor.id, {
        name: floorName.trim(),
        floorNumber: floorNumber || undefined
      });
      toast.success('Floor updated successfully');
      setOpenEditFloorDialog(false);
      setEditingFloor(null);
      setFloorName('');
      setFloorNumber(0);
      fetchFloorLayout(true);
    } catch (error) {
      console.error('Failed to update floor:', error);
      toast.error('Failed to update floor');
    }
  };

  const handleUpdateSection = async () => {
    if (!editingSection || !sectionName.trim()) {
      toast.error('Please enter a section name');
      return;
    }

    try {
      await updateSection(editingSection.id, {
        name: sectionName.trim(),
        floorId: editingSection.floorId
      });
      toast.success('Section updated successfully');
      setOpenEditSectionDialog(false);
      setEditingSection(null);
      setSectionName('');
      fetchFloorLayout(true);
    } catch (error) {
      console.error('Failed to update section:', error);
      toast.error('Failed to update section');
    }
  };

  // Delete confirmation handler
  const handleConfirmDelete = async () => {
    if (!deletingEntity) return;

    const needsTextConfirmation = deletingEntity.hasPendingOrders && deleteConfirmText.toLowerCase() !== 'delete';

    if (needsTextConfirmation) {
      toast.error('Please type "delete" to confirm cascade deletion');
      return;
    }

    try {
      // First try with forceCascade = false to check for pending orders
      let response;

      switch (deletingEntity.type) {
        case 'floor':
          response = await logicalDeleteFloor(deletingEntity.id, false);
          break;
        case 'section':
          response = await logicalDeleteSection(deletingEntity.id, false);
          break;
        case 'table':
          response = await logicalDeleteTable(deletingEntity.id, false);
          break;
      }

      // If there are pending orders and we haven't already shown the cascade option
      if (response?.hasPendingOrders && !deletingEntity.hasPendingOrders) {
        setDeletingEntity({
          ...deletingEntity,
          hasPendingOrders: true
        });
        setShowCascadeOption(deletingEntity.type !== 'table');
        // Don't close dialog, let user decide on cascade delete
        return;
      }

      // If we're here and have pending orders, do cascade delete
      if (deletingEntity.hasPendingOrders) {
        const forceCascade = true;
        switch (deletingEntity.type) {
          case 'floor':
            await logicalDeleteFloor(deletingEntity.id, forceCascade);
            break;
          case 'section':
            await logicalDeleteSection(deletingEntity.id, forceCascade);
            break;
          case 'table':
            await logicalDeleteTable(deletingEntity.id, forceCascade);
            break;
        }
      }

      toast.success(`${deletingEntity.type.charAt(0).toUpperCase() + deletingEntity.type.slice(1)} deleted successfully`);
      setOpenDeleteConfirmDialog(false);
      setDeletingEntity(null);
      setDeleteConfirmText('');
      setShowCascadeOption(false);
      fetchFloorLayout(true);
    } catch (error) {
      console.error('Failed to delete entity:', error);
      toast.error(`Failed to delete ${deletingEntity.type}`);
    }
  };

  // Restore handlers
  const handleRestoreEntity = (type: 'floor' | 'section' | 'table', id: string, name: string) => {
    setRestoringEntity({ type, id, name });
    setOpenRestoreDialog(true);
  };

  const handleConfirmRestore = async () => {
    if (!restoringEntity) return;

    try {
      switch (restoringEntity.type) {
        case 'floor':
          await restoreFloor(restoringEntity.id);
          break;
        case 'section':
          await restoreSection(restoringEntity.id);
          break;
        case 'table':
          await restoreTable(restoringEntity.id);
          break;
      }

      toast.success(`${restoringEntity.type.charAt(0).toUpperCase() + restoringEntity.type.slice(1)} restored successfully`);
      setOpenRestoreDialog(false);
      setRestoringEntity(null);
      fetchFloorLayout(true);
    } catch (error: any) {
      console.error('Failed to restore entity:', error);
      toast.error(error?.response?.data?.error || `Failed to restore ${restoringEntity.type}`);
    }
  };

  // Permanent delete handlers
  const handlePermanentDeleteEntity = (type: 'floor' | 'section' | 'table', id: string, name: string) => {
    setPermanentDeletingEntity({ type, id, name });
    setPermanentDeleteConfirmText('');
    setOpenPermanentDeleteDialog(true);
  };

  const handleConfirmPermanentDelete = async () => {
    if (!permanentDeletingEntity) return;

    if (permanentDeleteConfirmText.toLowerCase() !== 'delete') {
      toast.error('Please type "delete" to confirm permanent deletion');
      return;
    }

    try {
      switch (permanentDeletingEntity.type) {
        case 'floor':
          await permanentlyDeleteFloor(permanentDeletingEntity.id, permanentDeleteConfirmText);
          break;
        case 'section':
          await permanentlyDeleteSection(permanentDeletingEntity.id, permanentDeleteConfirmText);
          break;
        case 'table':
          await permanentlyDeleteTable(permanentDeletingEntity.id, permanentDeleteConfirmText);
          break;
      }

      toast.success(`${permanentDeletingEntity.type.charAt(0).toUpperCase() + permanentDeletingEntity.type.slice(1)} permanently deleted`);
      setOpenPermanentDeleteDialog(false);
      setPermanentDeletingEntity(null);
      setPermanentDeleteConfirmText('');
      fetchFloorLayout(true);
    } catch (error: any) {
      console.error('Failed to permanently delete entity:', error);
      toast.error(error?.response?.data?.error || `Failed to permanently delete ${permanentDeletingEntity.type}`);
    }
  };

  const executeConfirmedOrderAction = async () => {
    if (!confirmOrderAction.orderId || !confirmOrderAction.type) return;
    const orderId = confirmOrderAction.orderId;
    const action = confirmOrderAction.type;
    setConfirmOrderAction({ open: false, type: null, orderId: null });

    if (action === 'cancel') {
      setCancellingOrderId(orderId);
      try {
        const reason = window.prompt('Please provide a reason for cancellation:');
        if (!reason) {
          setCancellingOrderId(null);
          return;
        }
        await api.post(`/api/steward/orders/${orderId}/cancel`, { reason });
        toast.success('Order cancelled successfully');

        // Update the order status locally instead of fetching entire layout
        if (selectedTable) {
          flushSync(() => {
            const updatedTable = {
              ...selectedTable,
              activeOrders: selectedTable.activeOrders?.map(order =>
                order.id === orderId ? { ...order, status: 'CANCELLED' } : order
              )
            };

            // Update floor layout
            setFloorLayout(prevLayout => {
              const newLayout = JSON.parse(JSON.stringify(prevLayout));
              Object.keys(newLayout).forEach(floorKey => {
                const floorData = newLayout[floorKey];
                floorData.sections?.forEach((sectionData: any) => {
                  const tableIndex = sectionData.tables?.findIndex((t: any) => t.table.id === selectedTable.table.id);
                  if (tableIndex !== undefined && tableIndex !== -1) {
                    sectionData.tables[tableIndex] = updatedTable;
                  }
                });
              });
              return newLayout;
            });

            // Update selected table to reflect the change
            setSelectedTable(updatedTable);
            setOrderHistoryRefreshTrigger(prev => prev + 1); // Trigger TableOrderHistory refresh
          });
        } else {
          // If no table details open, refresh the layout
          fetchFloorLayout(true);
        }
      } catch (error) {
        console.error('Failed to cancel order:', error);
        toast.error('Failed to cancel order');
      } finally {
        setCancellingOrderId(null);
      }
      return;
    }

    if (action === 'serve') {
      setServingOrderId(orderId);
      try {
        const response = await api.post(`/api/steward/orders/${orderId}/serve`, {});
        if (response.status === 200) {
          toast.success('Order marked as served successfully');

          // Update the order status locally instead of fetching entire layout
          if (selectedTable) {
            flushSync(() => {
              const updatedTable = {
                ...selectedTable,
                activeOrders: selectedTable.activeOrders?.map(order =>
                  order.id === orderId ? { ...order, status: 'SERVED' } : order
                )
              };

              // Update floor layout
              setFloorLayout(prevLayout => {
                const newLayout = JSON.parse(JSON.stringify(prevLayout));
                Object.keys(newLayout).forEach(floorKey => {
                  const floorData = newLayout[floorKey];
                  floorData.sections?.forEach((sectionData: any) => {
                    const tableIndex = sectionData.tables?.findIndex((t: any) => t.table.id === selectedTable.table.id);
                    if (tableIndex !== undefined && tableIndex !== -1) {
                      sectionData.tables[tableIndex] = updatedTable;
                    }
                  });
                });
                return newLayout;
              });

              // Update selected table to reflect the change
              setSelectedTable(updatedTable);
              setOrderHistoryRefreshTrigger(prev => prev + 1); // Trigger TableOrderHistory refresh
            });
          } else {
            // If no table details open, refresh the layout
            fetchFloorLayout(true);
          }
        }
      } catch (error: any) {
        console.error('Error marking order as served:', error);
        toast.error(error.response?.data?.message || 'Failed to mark order as served');
      } finally {
        setServingOrderId(null);
      }
    }
  };
  const getTableStatusColor = (tableData: {
    isOccupied: boolean;
    status: string;
  }) => {
    if (isModernTheme) {
      // Modern Dashboard: Use theme status colors with fallbacks
      if (tableData.isOccupied) return theme.components?.status?.occupied?.text || '#EF4444';
      switch (tableData.status) {
        case 'reserved': return theme.components?.status?.reserved?.text || '#F59E0B';
        case 'maintenance': return theme.components?.status?.cleaning?.text || '#6B7280';
        default: return theme.components?.status?.available?.text || '#10B981';
      }
    }
    // Classic themes
    if (tableData.isOccupied) return theme.components?.status?.occupied?.text || danger;
    switch (tableData.status) {
      case 'reserved': return theme.components?.status?.reserved?.text || warning;
      case 'maintenance': return theme.components?.status?.cleaning?.text || textSecondary;
      default: return theme.components?.status?.available?.text || success;
    }
  };

  const getTableStatusText = (tableData: {
    isOccupied: boolean;
    status?: string;
  }) => {
    if (tableData.isOccupied) return 'Occupied';
    if (tableData.status === 'reserved') return 'Reserved';
    return tableData.status || 'Available';
  };

  // Check if table has active orders, unpaid orders, or is occupied
  // Used to determine if View Bill and Settle buttons should be shown
  const hasActiveOrders = (tableData: {
    isOccupied: boolean;
    activeOrders?: Array<any>;
    completedOrders?: Array<{ id: number; status: string }>;
    activeOrderCount?: number;
  }) => {
    const hasActive = tableData.activeOrders && tableData.activeOrders.length > 0;
    const hasUnpaidOrders = tableData.completedOrders?.some(order =>
      order.status && ['PENDING', 'PREPARING', 'READY', 'SERVED'].includes(order.status)
    );
    // If table is marked as occupied or has activeOrderCount, assume there are orders
    // This handles the optimized format where activeOrders is [] but activeOrderCount is set
    const hasOrderCount = tableData.activeOrderCount && tableData.activeOrderCount > 0;
    const isOccupied = tableData.isOccupied;
    return hasActive || hasUnpaidOrders || isOccupied || hasOrderCount;
  };

  // Get the primary order ID for billing - checks activeOrders first, then completedOrders
  const getPrimaryOrderId = (tableData: {
    activeOrders?: Array<{ id: number }>;
    completedOrders?: Array<{ id: number; status: string }>;
  }): number | undefined => {
    // First check activeOrders
    if (tableData.activeOrders && tableData.activeOrders.length > 0) {
      return tableData.activeOrders[0].id;
    }
    // Fall back to completedOrders with active statuses
    const unpaidOrder = tableData.completedOrders?.find(order =>
      order.status && ['PENDING', 'PREPARING', 'READY', 'SERVED'].includes(order.status)
    );
    return unpaidOrder?.id;
  };

  const renderTable = (tableData: {
    table: Table;
    activeOrders?: Array<any>;
    isOccupied: boolean;
    status: string;
  }, sectionGridColumns: number, sectionIsDeleted: boolean = false, floorName?: string, sectionName?: string) => {
    const table = tableData.table;
    const isDeleted = table.isDeleted === true || sectionIsDeleted;
    const statusColor = isDeleted ? '#9e9e9e' : getTableStatusColor(tableData);
    const statusText = isDeleted ? 'Deleted' : getTableStatusText(tableData);

    // Compute running total and time elapsed for occupied tables
    const tableTotal = tableData.activeOrders?.reduce((sum, o) => sum + (o.finalTotal ?? o.total ?? 0), 0) || 0;
    const oldestOrderTime = tableData.activeOrders?.length
      ? tableData.activeOrders.reduce((oldest, o) => {
          const t = new Date(o.createdAt).getTime();
          return t < oldest ? t : oldest;
        }, Date.now())
      : 0;
    const minutesElapsed = oldestOrderTime ? Math.floor((Date.now() - oldestOrderTime) / 60000) : 0;
    const timeLabel = minutesElapsed >= 60 ? `${Math.floor(minutesElapsed / 60)}h ${minutesElapsed % 60}m` : `${minutesElapsed}m`;

    // Create table data with floor/section names for modal
    const tableDataWithContext = {
      ...tableData,
      floorName,
      sectionName
    };

    // Responsive grid columns
    // When embedded (EPOS split panel ~420px), MUI breakpoints use viewport width,
    // so we cap columns to avoid cramped cards in the narrow container.
    const effectiveGridColumns = embedded
      ? Math.min(sectionGridColumns, 4)
      : !isManagementMode ? Math.max(sectionGridColumns, 6) : sectionGridColumns;

    const responsiveColumns = embedded
      ? { xs: 3, sm: 3, md: Math.min(4, effectiveGridColumns) }
      : {
          xs: isManagementMode ? 2 : 3,
          sm: Math.min(isManagementMode ? 3 : 4, effectiveGridColumns),
          md: effectiveGridColumns
        };

    return (
      <Grid
        item
        xs={12 / responsiveColumns.xs}
        sm={12 / responsiveColumns.sm}
        md={12 / responsiveColumns.md}
        key={table.id}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: isDeleted ? 0.5 : 1, scale: 1 }}
          transition={{ duration: 0.2 }}
        >
          <Card
            data-table-id={table.id}
            data-table-name={table.name}
            data-table-status={isDeleted ? 'deleted' : tableData.isOccupied ? 'busy' : tableData.status === 'reserved' ? 'reserved' : 'free'}
            role={isDeleted ? undefined : 'button'}
            tabIndex={isDeleted ? -1 : 0}
            aria-disabled={isDeleted || undefined}
            aria-label={`Table ${table.displayNumber || table.name}${tableData.isOccupied ? `, busy, ${tableData.activeOrders?.length || (tableData as any).activeOrderCount || 0} active orders, total ${formatCurrency(tableTotal)}` : tableData.status === 'reserved' ? ', reserved' : ', free'}`}
            onKeyDown={(e) => {
              if (!isDeleted && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleTableClick(tableDataWithContext);
              }
            }}
            sx={{
              background: isDeleted
                ? neutralLight
                : `linear-gradient(135deg, ${statusColor}06 0%, ${surface} 100%)`,
              border: isDeleted
                ? `1px solid ${borderColor}`
                : selectedTableId && table.id.toString() === selectedTableId
                  ? `2px solid ${primary}`
                  : `1.5px solid ${statusColor}30`,
              borderRadius: { xs: '8px', sm: '12px' },
              cursor: isDeleted ? 'not-allowed' : 'pointer',
              height: '100%',
              minHeight: { xs: isManagementMode ? 90 : 56, sm: isManagementMode ? 100 : 72 },
              position: 'relative',
              overflow: 'hidden',
              boxShadow: isDeleted
                ? 'none'
                : selectedTableId && table.id.toString() === selectedTableId
                  ? `0 0 0 2px ${primary}30`
                  : '0 1px 3px 0 rgba(0, 0, 0, 0.04)',
              transition: 'all 0.2s ease',
              opacity: isDeleted ? 0.6 : 1,
              '&:focus-visible': !isDeleted ? {
                outline: `2px solid ${primary}`,
                outlineOffset: '2px',
              } : {},
              '@media (hover: hover)': {
                '&:hover': !isDeleted ? {
                  boxShadow: `0 4px 12px 0 ${statusColor}20`,
                  borderColor: statusColor,
                  transform: 'translateY(-1px)'
                } : {}
              }
            }}
            onClick={() => !isDeleted && handleTableClick(tableDataWithContext)}
          >
            {/* Merge mode selection overlay */}
            {mergeMode?.active && mergeMode.selectedTableIds.has(String(table.id)) && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 3,
                background: 'rgba(245, 158, 11, 0.15)',
                border: '2.5px solid var(--status-warn)',
                borderRadius: 'inherit',
                pointerEvents: 'none',
              }} />
            )}
            {/* Merge mode source highlight */}
            {mergeMode?.active && String(table.id) === mergeMode.sourceTableId && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 3,
                background: 'rgba(245, 158, 11, 0.25)',
                border: '2.5px solid var(--status-warn-text)',
                borderRadius: 'inherit',
                pointerEvents: 'none',
              }} />
            )}
            {/* Reservation mode selection overlay */}
            {reservationMode?.active && (reservationMode.selectedTableIds.has(table.id) || reservationMode.selectedTableIds.has(Number(table.id))) && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 3,
                background: 'rgba(20, 184, 166, 0.15)',
                border: '2.5px solid var(--action)',
                borderRadius: 'inherit',
                pointerEvents: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ color: 'var(--action)', fontSize: '1.5rem', fontWeight: 700 }}>✓</Typography>
              </Box>
            )}
            {/* Merged group: translucent cover so the card visually reads as part
                of a chain. Header strip carries the group name + primary marker. */}
            {(tableData as any).group && (
              <>
                <Box sx={{
                  position: 'absolute', inset: 0, zIndex: 1,
                  background: (tableData as any).group.status === 'ACTIVE'
                    ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.10) 0%, rgba(124, 58, 237, 0.04) 100%)'
                    : 'linear-gradient(135deg, rgba(245, 158, 11, 0.10) 0%, rgba(245, 158, 11, 0.04) 100%)',
                  border: (tableData as any).group.status === 'ACTIVE'
                    ? '2px dashed rgba(124, 58, 237, 0.6)'
                    : '2px dashed rgba(245, 158, 11, 0.6)',
                  borderRadius: 'inherit',
                  pointerEvents: 'none',
                }} />
                <Box sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  backgroundColor: (tableData as any).group.status === 'ACTIVE' ? '#EDE9FE' : '#FEF3C7',
                  border: `1px solid ${(tableData as any).group.status === 'ACTIVE' ? '#A78BFA' : '#FBBF24'}`,
                  borderRadius: '4px',
                  px: 0.5,
                  py: 0.125,
                }}>
                  <LinkIcon style={{ width: 10, height: 10, color: (tableData as any).group.status === 'ACTIVE' ? '#7C3AED' : '#D97706' }} />
                  <span style={{ fontSize: '0.55rem', fontWeight: 700, color: (tableData as any).group.status === 'ACTIVE' ? '#5B21B6' : '#92400E' }}>
                    {(tableData as any).group.isPrimary ? 'PRIMARY' : 'MERGED'}
                  </span>
                </Box>
              </>
            )}
            {/* Legacy merge group badge — only when API didn't include group info */}
            {!(tableData as any).group && tableGroupMap.has(String(table.id)) && (
              <Box sx={{
                position: 'absolute',
                bottom: 4,
                left: 4,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                backgroundColor: 'var(--status-warn-bg)',
                border: '1px solid var(--status-warn)',
                borderRadius: '4px',
                px: 0.5,
                py: 0.125,
              }}>
                <LinkIcon style={{ width: 10, height: 10, color: 'var(--status-warn-text)' }} />
                <span style={{ fontSize: '0.55rem', fontWeight: 700, color: 'var(--status-warn-text)' }}>
                  Merged
                </span>
              </Box>
            )}
            {/* Held order amber dot badge */}
            {heldOrderTableIds.has(table.id) && (
              <Box sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: 'var(--status-warn-text)',
                border: '2px solid var(--border-subtle)',
                zIndex: 2,
              }} />
            )}
            <CardContent sx={{
              p: { xs: isManagementMode ? 0.75 : 0.5, sm: isManagementMode ? 1.25 : 0.75 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              '&:last-child': { pb: { xs: isManagementMode ? 0.75 : 0.5, sm: isManagementMode ? 1.25 : 0.75 } }
            }}>
              {/* Modern Theme: Self-Explanatory Design */}
              {isModernTheme ? (
                <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} sx={{ width: '100%', pb: 1, minHeight: 60 }}>
                  {/* Table Label with Icon */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.9rem', flexShrink: 0 }}>🍽️</Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: textPrimary,
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {table.displayNumber || table.name}
                    </Typography>
                  </Box>
                  
                  {/* Order info — total, count, time */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, minHeight: 20 }}>
                    {tableData.isOccupied ? (
                      <>
                        {tableTotal > 0 && (
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: textPrimary }}>
                            {formatCurrency(tableTotal)}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.65rem', color: textSecondary, fontWeight: 500 }}>
                            {(tableData as any).activeOrderCount || tableData.activeOrders?.length || 1} orders
                          </Typography>
                          {minutesElapsed > 0 && (
                            <Typography sx={{
                              fontSize: '0.6rem',
                              fontWeight: 600,
                              color: minutesElapsed > 45 ? danger : neutral,
                            }}>
                              · {timeLabel}
                            </Typography>
                          )}
                        </Box>
                      </>
                    ) : tableData.status === 'reserved' && (tableData as any).nextReservation ? (
                      <Typography sx={{ fontSize: '0.65rem', color: warning, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                        {(tableData as any).nextReservation.customerName} · {new Date(`2000-01-01T${(tableData as any).nextReservation.startTime}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: '0.7rem', color: neutral, fontWeight: 500 }}>
                        No orders
                      </Typography>
                    )}
                  </Box>

                  {/* Status Badge - Explicit */}
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: '12px',
                    backgroundColor: tableData.isOccupied
                      ? (theme.components?.status?.occupied?.bg || '#FEE2E2')
                      : tableData.status === 'reserved'
                        ? (theme.components?.status?.reserved?.bg || '#FEF3C7')
                        : (theme.components?.status?.available?.bg || '#D1FAE5'),
                    border: `1.5px solid ${tableData.isOccupied
                      ? (theme.components?.status?.occupied?.border || theme.components?.status?.occupied?.text || '#EF4444')
                      : tableData.status === 'reserved'
                        ? (theme.components?.status?.reserved?.border || theme.components?.status?.reserved?.text || '#F59E0B')
                        : (theme.components?.status?.available?.border || theme.components?.status?.available?.text || '#10B981')}`
                  }}>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: tableData.isOccupied
                        ? (theme.components?.status?.occupied?.text || '#EF4444')
                        : tableData.status === 'reserved'
                          ? (theme.components?.status?.reserved?.text || '#F59E0B')
                          : (theme.components?.status?.available?.text || '#10B981'),
                      flexShrink: 0
                    }} />
                    <Typography sx={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      color: tableData.isOccupied
                        ? (theme.components?.status?.occupied?.text || '#DC2626')
                        : tableData.status === 'reserved'
                          ? (theme.components?.status?.reserved?.text || '#92400E')
                          : (theme.components?.status?.available?.text || '#059669'),
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap'
                    }}>
                      {tableData.isOccupied ? 'Busy' : tableData.status === 'reserved' ? 'Reserved' : 'Free'}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                /* Original Theme: Standard Card Layout */
                <Box display="flex" flexDirection="column" alignItems="center" gap={{ xs: 0.25, sm: 0.5 }}>
                  <Box
                    sx={{
                      width: { xs: isManagementMode ? 28 : 24, sm: isManagementMode ? 36 : 28 },
                      height: { xs: isManagementMode ? 28 : 24, sm: isManagementMode ? 36 : 28 },
                      borderRadius: { xs: '6px', sm: '8px' },
                      background: `linear-gradient(135deg, ${statusColor}10 0%, ${statusColor}05 100%)`,
                      border: `1px solid ${statusColor}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 0.25, sm: 0.5 }
                    }}
                  >
                    <TableCellsIcon style={{ width: isManagementMode ? 16 : 14, height: isManagementMode ? 16 : 14, color: statusColor }} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: textPrimary,
                      fontSize: { xs: isManagementMode ? '0.7rem' : '0.65rem', sm: isManagementMode ? '0.85rem' : '0.75rem' },
                      textAlign: 'center',
                      lineHeight: 1
                    }}
                  >
                    {table.displayNumber || table.name.replace(/[^0-9]/g, '') || table.name}
                  </Typography>
                  <Chip
                    label={statusText}
                    size="small"
                    sx={{
                      backgroundColor: `${statusColor}15`,
                      color: statusColor,
                      fontWeight: 600,
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      height: { xs: 18, sm: 20 },
                      mb: 0.25,
                      '& .MuiChip-label': {
                        px: { xs: 0.5, sm: 1 },
                        py: 0
                      }
                    }}
                  />
                  {/* Running total + elapsed time for occupied tables */}
                  {tableData.isOccupied && tableTotal > 0 && (
                    <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: { xs: isManagementMode ? 0.5 : 0, sm: isManagementMode ? 0.75 : 0 } }}>
                      <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, fontWeight: 700, color: textPrimary }}>
                        {formatCurrency(tableTotal)}
                      </Typography>
                      {minutesElapsed > 0 && (
                        <Typography sx={{
                          fontSize: { xs: '0.55rem', sm: '0.6rem' },
                          fontWeight: 500,
                          color: minutesElapsed > 45 ? danger : textSecondary,
                        }}>
                          · {timeLabel}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Action buttons - anchored at bottom via flex auto spacing */}
              <Box
                sx={{
                  mt: 'auto',
                  display: 'flex',
                  gap: 0.5,
                  justifyContent: 'center',
                  width: '100%',
                  flexWrap: 'wrap'
                }}
              >
                {/* Restore and Permanent Delete buttons for deleted tables */}
                {isDeleted && !isSteward && isManagementMode && (
                  <>
                    <AppIconButton
                      variant="success"
                      tooltip="Restore Table"
                      icon={<ArrowPathIcon style={{ width: 14, height: 14 }} />}
                      size="small"
                      sx={{ padding: { xs: 0.25, sm: 0.5 } }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestoreEntity('table', table.id, table.name);
                      }}
                    />
                    <AppIconButton
                      variant="delete"
                      tooltip="Delete Permanently"
                      icon={<TrashIcon style={{ width: 14, height: 14 }} />}
                      size="small"
                      sx={{
                        padding: { xs: 0.25, sm: 0.5 },
                        backgroundColor: `${danger}20`,
                        '&:hover': { backgroundColor: `${danger}30` }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePermanentDeleteEntity('table', table.id, table.name);
                      }}
                    />
                  </>
                )}

                {/* Regular action buttons for non-deleted tables */}
                {!isDeleted && !table.qrCode && !isSteward && isManagementMode && (
                  <AppIconButton
                    variant="edit"
                    tooltip="Generate QR Code"
                    icon={<QrCodeIcon style={{ width: 16, height: 16 }} />}
                    size="small"
                    sx={{
                      padding: { xs: 0.5, sm: 0.75 },
                      backgroundColor: `${theme.button?.secondary?.transparentBg || 'rgba(29, 78, 216, 0.1)'} !important`,
                      color: `${theme.button?.secondary?.text || '#1D4ED8'} !important`,
                      border: 'none !important',
                      '&:hover': {
                        backgroundColor: `${theme.button?.secondary?.hover || '#DBEAFE'} !important`,
                        boxShadow: 'none !important'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenQrDialog(table);
                    }}
                  />
                )}
                {!isDeleted && !isSteward && isManagementMode && (
                  <AppIconButton
                    variant="edit"
                    tooltip="Edit Table"
                    icon={<PencilIcon style={{ width: 14, height: 14 }} />}
                    size="small"
                    sx={{ borderRadius: '6px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const sectionId = Object.values(floorLayout)
                        .flatMap(floor => floor.sections)
                        .find(section => section.tables.some(t => t.table.id === table.id))
                        ?.section.id || '';
                      handleEditTable(table, sectionId);
                    }}
                  />
                )}
                {!isDeleted && !isSteward && isManagementMode && (
                  <AppIconButton
                    variant="delete"
                    tooltip="Delete Table"
                    icon={<TrashIcon style={{ width: 14, height: 14 }} />}
                    size="small"
                    sx={{ borderRadius: '6px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTable(table.id, table.name);
                    }}
                  />
                )}
                {!isDeleted && !isEposSelectMode && (
                  <AppIconButton
                    variant="ghost"
                    tooltip="Order"
                    icon={<ShoppingCartIcon style={{ width: 22, height: 22 }} />}
                    size="small"
                    sx={{
                      padding: '3px !important',
                      backgroundColor: `${neutral}10 !important`,
                      border: 'none !important',
                      color: `${neutral} !important`,
                      width: 30,
                      height: 30,
                      minWidth: 0,
                      '&:hover': {
                        backgroundColor: `${neutral}20 !important`,
                        color: `${textSecondary} !important`,
                        boxShadow: 'none !important'
                      }
                    }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!(await confirmReservationOrder(tableData))) return;
                      setOrderTableId(table.id);
                      setOrderTableName(table.name);
                      setTableDataForOrder(tableData); // Save full table data for reopening after order
                      setShowOrderForm(true);
                    }}
                  />
                )}
              </Box>

              {/* QR Code indicator */}
              {table.qrCode && (
                <Box
                  position="absolute"
                  top={{ xs: 4, sm: 6 }}
                  right={{ xs: 4, sm: 6 }}
                  sx={{
                    backgroundColor: success,
                    borderRadius: { xs: '4px', sm: '6px' },
                    width: { xs: 20, sm: 24 },
                    height: { xs: 20, sm: 24 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.12)'
                  }}
                >
                  <QrCodeIcon style={{ width: 12, height: 12, color: 'white' }} />
                </Box>
              )}

              {/* Order count badge */}
              {((tableData.activeOrders && tableData.activeOrders.length > 0) || (tableData as any).activeOrderCount > 0) && (
                <Box
                  position="absolute"
                  top={{ xs: 4, sm: 6 }}
                  left={{ xs: 4, sm: 6 }}
                  sx={{
                    backgroundColor: danger,
                    borderRadius: { xs: '4px', sm: '6px' },
                    minWidth: { xs: 24, sm: 24 },
                    height: { xs: 24, sm: 24 },
                    px: { xs: 0.25, sm: 0.5 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.12)'
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                    {tableData.activeOrders?.length || (tableData as any).activeOrderCount || 0}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </Grid>
    );
  };

  const renderTableListItem = (tableData: {
    table: Table;
    activeOrders?: Array<any>;
    isOccupied: boolean;
    status: string;
  }, sectionIsDeleted: boolean = false, floorName?: string, sectionName?: string) => {
    const table = tableData.table;
    const isDeleted = table.isDeleted === true || sectionIsDeleted;
    const statusColor = isDeleted ? '#9e9e9e' : getTableStatusColor(tableData);
    const statusText = isDeleted ? 'Deleted' : getTableStatusText(tableData);
    const activeOrderCount = tableData.activeOrders?.length || (tableData as any).activeOrderCount || 0;

    // Create table data with floor/section names for modal
    const tableDataWithContext = {
      ...tableData,
      floorName,
      sectionName
    };

    return (
      <Paper
        key={table.id}
        data-table-id={table.id}
        data-table-name={table.name}
        data-table-status={isDeleted ? 'deleted' : tableData.isOccupied ? 'busy' : tableData.status === 'reserved' ? 'reserved' : 'free'}
        role={isDeleted ? undefined : 'button'}
        tabIndex={isDeleted ? -1 : 0}
        aria-disabled={isDeleted || undefined}
        aria-label={`Table ${table.displayNumber || table.name}, ${statusText}${activeOrderCount > 0 ? `, ${activeOrderCount} active orders` : ''}`}
        onKeyDown={(e) => {
          if (!isDeleted && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleTableClick(tableDataWithContext);
          }
        }}
        elevation={0}
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: isDeleted ? 'not-allowed' : 'pointer',
          border: reservationMode?.active && (reservationMode.selectedTableIds.has(table.id) || reservationMode.selectedTableIds.has(Number(table.id)))
            ? '2px solid var(--action)'
            : selectedTableId && table.id.toString() === selectedTableId
            ? `2px solid ${primary}`
            : `1px solid ${borderColor}`,
          borderRadius: '8px',
          backgroundColor: reservationMode?.active && (reservationMode.selectedTableIds.has(table.id) || reservationMode.selectedTableIds.has(Number(table.id)))
            ? 'color-mix(in srgb, var(--action) 8%, transparent)'
            : isDeleted ? neutralLight : selectedTableId && table.id.toString() === selectedTableId ? `${primary}08` : surface,
          opacity: isDeleted ? 0.6 : 1,
          transition: 'all 0.2s ease',
          '&:focus-visible': !isDeleted ? {
            outline: `2px solid ${primary}`,
            outlineOffset: '2px',
          } : {},
          '&:hover': !isDeleted ? {
            borderColor: statusColor,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          } : {}
        }}
        onClick={() => !isDeleted && handleTableClick(tableDataWithContext)}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '6px',
              background: `linear-gradient(135deg, ${statusColor}10 0%, ${statusColor}05 100%)`,
              border: `1px solid ${statusColor}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <TableCellsIcon style={{ width: 18, height: 18, color: statusColor }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textPrimary }}>
              {table.displayNumber || table.name}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={statusText}
                size="small"
                sx={{
                  backgroundColor: `${statusColor}15`,
                  color: statusColor,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 22,
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
              {activeOrderCount > 0 && (
                <Chip
                  label={`${activeOrderCount} orders`}
                  size="small"
                  sx={{
                    backgroundColor: `${danger}15`,
                    color: danger,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 22,
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={0.5}>
          {/* Restore and Permanent Delete buttons for deleted tables */}
          {isDeleted && !isSteward && isManagementMode && (
            <>
              <AppIconButton
                variant="success"
                tooltip="Restore Table"
                icon={<ArrowPathIcon style={{ width: 16, height: 16 }} />}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRestoreEntity('table', table.id, table.name);
                }}
              />
              <AppIconButton
                variant="delete"
                tooltip="Delete Permanently"
                icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                size="small"
                sx={{ color: danger }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePermanentDeleteEntity('table', table.id, table.name);
                }}
              />
            </>
          )}

          {/* Regular action buttons */}
          {!isDeleted && !table.qrCode && !isSteward && isManagementMode && (
            <AppIconButton
              variant="ghost"
              tooltip="Generate QR Code"
              icon={<QrCodeIcon style={{ width: 16, height: 16 }} />}
              size="small"
              sx={{ color: textSecondary }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenQrDialog(table);
              }}
            />
          )}
          {!isDeleted && !isSteward && isManagementMode && (
            <AppIconButton
              variant="edit"
              tooltip="Edit Table"
              icon={<PencilIcon style={{ width: 16, height: 16 }} />}
              size="small"
              sx={{ borderRadius: '6px' }}
              onClick={(e) => {
                e.stopPropagation();
                const sectionId = Object.values(floorLayout)
                  .flatMap(floor => floor.sections)
                  .find(section => section.tables.some(t => t.table.id === table.id))
                  ?.section.id || '';
                handleEditTable(table, sectionId);
              }}
            />
          )}
          {!isDeleted && !isSteward && isManagementMode && (
            <AppIconButton
              variant="delete"
              tooltip="Delete Table"
              icon={<TrashIcon style={{ width: 16, height: 16 }} />}
              size="small"
              sx={{ borderRadius: '6px' }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTable(table.id, table.name);
              }}
            />
          )}
          {!isDeleted && !isEposSelectMode && (
            <AppIconButton
              variant="primary"
              tooltip="Order"
              icon={<ShoppingCartIcon style={{ width: 20, height: 20 }} />}
              size="small"
              sx={{ width: 36, height: 36 }}
              onClick={async (e) => {
                e.stopPropagation();
                if (!(await confirmReservationOrder(tableData))) return;
                setOrderTableId(table.id);
                setOrderTableName(table.name);
                setTableDataForOrder(tableData);
                setShowOrderForm(true);
              }}
            />
          )}
        </Box>
      </Paper>
    );
  };

  // Subtle, professional colors following best practices
  const sectionColors = Array(8).fill({ bg: surface, border: borderColor, hover: neutralLight });

  const renderSection = (sectionData: {
    section: Section;
    tables: Array<{
      table: Table;
      activeOrders?: Array<{
        id: number;
        status: string;
        createdAt: string;
        total?: number;
        items?: Array<{
          id: number;
          quantity: number;
          price: number;
          menuItem?: {
            name: string;
          };
        }>;
      }>;
      isOccupied: boolean;
      status: string;
    }>;
  }, sectionIndex: number, floorId: string, floorName: string) => {
    const section = sectionData.section;
    const isDeleted = section.isDeleted === true;
    const isExpanded = expandedSections.has(section.id);
    const gridColumns = section.gridColumns || 4;
    const colorScheme = isDeleted
      ? { bg: '#f5f5f5', border: 'var(--border-default)', hover: '#eeeeee', icon: '#9e9e9e' }
      : sectionColors[sectionIndex % sectionColors.length];

    // Transform optimized table data to expected format if needed
    const tables = sectionData.tables.map((t: any) => {
      // Check if table data is in optimized format (flat structure)
      if (t.id && !t.table) {
        // Transform optimized format to expected nested format
        return {
          table: {
            id: t.id,
            name: t.name,
            sectionId: t.sectionId,
            isDeleted: t.isDeleted || false,
            qrCode: t.qrCode
          },
          activeOrders: [], // Optimized data doesn't include full orders
          completedOrders: [],
          isOccupied: (t.activeOrderCount || 0) > 0,
          status: (t.activeOrderCount || 0) > 0 ? 'OCCUPIED' : 'AVAILABLE',
          activeOrderCount: t.activeOrderCount || 0
        };
      }
      // Already in expected format
      return t;
    });

    const ordersInSection = tables.reduce((sum: number, t: any) =>
      sum + (t.activeOrders?.length || t.activeOrderCount || 0), 0
    );

    const reservedInSection = tables.filter((t: any) => t.status === 'reserved').length;

    return (
      <Box
        key={section.id}
        id={`section-${section.id}`}
        ref={(el: HTMLElement | null) => {
          if (el) sectionRefs.current.set(section.id, el);
          else sectionRefs.current.delete(section.id);
        }}
      >
        <Paper
          elevation={0}
          sx={{
            background: isDeleted ? '#f5f5f5' : surface,
            border: `1px solid ${isDeleted ? '#e0e0e0' : borderColor}`,
            borderRadius: { xs: '6px', sm: '10px' },
            overflow: 'hidden',
            boxShadow: isDeleted ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            opacity: isDeleted ? 0.6 : 1,
            transition: 'all 0.2s ease',
            '&:hover': !isDeleted ? {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            } : {}
          }}
        >
          <Box
            data-section-id={section.id}
            sx={{
              p: { xs: 1.5, sm: 2 },
              background: colorScheme.bg,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'row',
              gap: { xs: 1, sm: 0 },
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: isExpanded ? `1px solid ${borderColor}` : 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: colorScheme.hover
              }
            }}
            onClick={() => toggleSectionExpansion(section.id)}
          >
            <Box display="flex" alignItems="center" gap={{ xs: 0.75, sm: 1.5 }} sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
              {isExpanded ? (
                <ChevronDownIcon style={{ width: 16, height: 16, color: textSecondary, flexShrink: 0 }} />
              ) : (
                <ChevronRightIcon style={{ width: 16, height: 16, color: textSecondary, flexShrink: 0 }} />
              )}
              <TintIcon
                icon={<TableCellsIcon style={{ width: 16, height: 16, color: primary }} />}
                size={{ xs: 28, sm: 32 }}
                borderOpacity="20"
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box display="flex" alignItems="center" gap={0.75} sx={{ flexWrap: 'nowrap', overflow: 'hidden' }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight: 600,
                      color: textPrimary,
                      fontSize: { xs: '0.813rem', sm: '0.875rem', md: '0.95rem' },
                      lineHeight: 1.2,
                      flexShrink: 1,
                      minWidth: 0,
                    }}
                  >
                    {section.name}
                  </Typography>
                  {/* Menu assignment badge */}
                  {menuAssignments[parseInt(section.id, 10)] && (
                    <Box display="flex" flexDirection="column" gap={0.25}>
                      <Chip
                        label={menuAssignments[parseInt(section.id, 10)].name}
                        size="small"
                        sx={{
                          backgroundColor: `${primary}15`,
                          color: primary,
                          fontWeight: 500,
                          height: { xs: 18, sm: 20 },
                          fontSize: { xs: '0.7rem', sm: '0.7rem' },
                          maxWidth: { xs: 80, sm: 120 },
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            px: { xs: 0.5, sm: 0.75 }
                          }
                        }}
                        title={menuAssignments[parseInt(section.id, 10)].name}
                      />
                      {menuAssignments[parseInt(section.id, 10)].schedule &&
                       menuAssignments[parseInt(section.id, 10)].schedule !== 'Available anytime' && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: menuAssignments[parseInt(section.id, 10)].isActiveNow ? success : warning,
                            fontSize: { xs: '0.65rem', sm: '0.65rem' },
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: { xs: 80, sm: 120 }
                          }}
                          title={menuAssignments[parseInt(section.id, 10)].schedule}
                        >
                          {menuAssignments[parseInt(section.id, 10)].schedule}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
                <Box display="flex" alignItems="center" gap={0.5} mt={0.25} sx={{ flexWrap: 'nowrap', overflow: 'hidden' }}>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      color: textSecondary,
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      flexShrink: 0,
                    }}
                  >
                    {tables.length} tables
                  </Typography>
                  <Typography variant="caption" sx={{ color: textSecondary, display: { xs: 'none', sm: 'inline' }, flexShrink: 0 }}>
                    •
                  </Typography>
                  <Typography variant="caption" noWrap sx={{ color: textSecondary, fontWeight: 500, fontSize: '0.7rem', display: { xs: 'none', sm: 'inline' }, flexShrink: 0 }}>
                    {gridColumns} col
                  </Typography>
                  {ordersInSection > 0 && (
                    <Chip
                      label={`${ordersInSection} ord`}
                      size="small"
                      sx={{
                        backgroundColor: theme.components?.status?.occupied?.bg || `${warning}18`,
                        color: theme.components?.status?.occupied?.text || warning,
                        fontWeight: 600,
                        height: 18,
                        fontSize: '0.65rem',
                        border: `1px solid ${theme.components?.status?.occupied?.border || `${warning}35`}`,
                        flexShrink: 0,
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                  )}
                  {reservedInSection > 0 && (
                    <Chip
                      label={`${reservedInSection} rsv`}
                      size="small"
                      sx={{
                        backgroundColor: theme.components?.status?.reserved?.bg || '#FEF3C7',
                        color: theme.components?.status?.reserved?.text || '#92400E',
                        fontWeight: 600,
                        height: 18,
                        fontSize: '0.65rem',
                        border: `1px solid ${theme.components?.status?.reserved?.border || '#F59E0B35'}`,
                        flexShrink: 0,
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                  )}
                </Box>
                
                {/* Progress Bar - Occupancy (modernDashboard only) */}
                {isModernTheme && (
                  <Box sx={{ mt: 1, width: '100%', maxWidth: { xs: '100%', sm: 200 }, display: { xs: 'none', sm: 'block' } }}>
                    {(() => {
                      const occupiedCount = tables.filter((t: any) => 
                        t.isOccupied || (t.activeOrderCount && t.activeOrderCount > 0)
                      ).length;
                      const totalCount = tables.length;
                      const percentage = totalCount > 0 ? (occupiedCount / totalCount) * 100 : 0;
                      
                      return (
                        <Box>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            mb: 0.5 
                          }}>
                            <Typography sx={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              color: textSecondary
                            }}>
                              Occupancy
                            </Typography>
                            <Typography sx={{
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              color: occupiedCount > 0 ? danger : success
                            }}>
                              {occupiedCount}/{totalCount}
                            </Typography>
                          </Box>
                          <Box sx={{
                            width: '100%',
                            height: 8,
                            backgroundColor: `${neutral}20`,
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <Box sx={{
                              width: `${percentage}%`,
                              height: '100%',
                              backgroundColor: percentage > 75 ? danger : percentage > 50 ? warning : success,
                              borderRadius: '4px',
                              transition: 'width 0.3s ease'
                            }} />
                          </Box>
                        </Box>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            </Box>

            <Box display="flex" gap={0.5} sx={{ flexShrink: 0 }}>
              {/* Restore and Permanent Delete buttons for deleted sections */}
              {isDeleted && !isSteward && isManagementMode && (
                <>
                  <AppIconButton
                    variant="success"
                    tooltip="Restore Section"
                    icon={<ArrowPathIcon style={{ width: 16, height: 16 }} />}
                    size="small"
                    sx={{ padding: '5px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRestoreEntity('section', section.id, section.name);
                    }}
                  />
                  <AppIconButton
                    variant="delete"
                    tooltip="Delete Permanently"
                    icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                    size="small"
                    sx={{
                      padding: '5px',
                      backgroundColor: `${danger}20`,
                      '&:hover': { backgroundColor: `${danger}30` }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePermanentDeleteEntity('section', section.id, section.name);
                    }}
                  />
                </>
              )}

              {/* Regular action buttons for non-deleted sections */}
              {!isDeleted && !isSteward && isManagementMode && (
                <>
                  {/* Add Tables — icon-only on mobile, text on desktop */}
                  <AppIconButton
                    variant="success"
                    tooltip="Add Table"
                    icon={<PlusCircleIcon style={{ width: 16, height: 16 }} />}
                    size="small"
                    sx={{
                      display: { xs: isExpanded ? 'flex' : 'none', sm: 'none' },
                      padding: '5px',
                      backgroundColor: `${theme.button?.success?.transparentBg || 'rgba(16, 185, 129, 0.13)'} !important`,
                      color: `${theme.success || '#059669'} !important`,
                      '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.2) !important' }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSectionForTable(section.id);
                      setSelectedFloorIdForTable(floorId);
                      setSelectedSectionNameForTable(section.name);
                      setOpenTableDialog(true);
                    }}
                  />
                  <Button
                    size="small"
                    startIcon={<PlusCircleIcon style={{ width: 12, height: 12 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSectionForTable(section.id);
                      setSelectedFloorIdForTable(floorId);
                      setSelectedSectionNameForTable(section.name);
                      setOpenTableDialog(true);
                    }}
                    sx={{
                      display: { xs: 'none', sm: 'inline-flex' },
                      backgroundColor: `${theme.button?.success?.transparentBg || 'rgba(16, 185, 129, 0.13)'} !important`,
                      color: `${theme.success || '#059669'} !important`,
                      px: 2,
                      py: 0.75,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      minWidth: '100px',
                      border: 'none !important',
                      boxShadow: 'none !important',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.2) !important',
                        boxShadow: 'none !important'
                      }
                    }}
                  >
                    Add Tables
                  </Button>
                </>
              )}
              {!isDeleted && !isSteward && isManagementMode && (
                <AppIconButton
                  variant="utility"
                  tooltip="Section Settings"
                  icon={<Cog6ToothIcon style={{ width: 15, height: 15 }} />}
                  size="small"
                  sx={{
                    display: { xs: isExpanded ? 'flex' : 'none', md: 'flex' },
                    padding: '5px',
                    border: 'none !important',
                    color: `${textSecondary} !important`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSectionForSettings({
                      id: section.id,
                      name: section.name,
                      gridColumns,
                      tablesCount: sectionData.tables.length,
                      ordersCount: ordersInSection
                    });
                    setOpenSectionSettings(true);
                  }}
                />
              )}
              {!isSteward && !isDeleted && isManagementMode && (
                <AppIconButton
                  variant="utility"
                  tooltip="Edit Section"
                  icon={<PencilIcon style={{ width: 14, height: 14 }} />}
                  size="small"
                  sx={{
                    display: { xs: isExpanded ? 'flex' : 'none', md: 'flex' },
                    padding: '5px',
                    border: 'none !important',
                    color: `${textSecondary} !important`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const currentFloorKey = Object.keys(floorLayout).filter(k => k.startsWith('floor_')).find(key =>
                      floorLayout[key].sections.some(s => s.section.id === section.id)
                    );
                    const floorId = currentFloorKey ? floorLayout[currentFloorKey].floor.id : '';
                    handleEditSection(section, floorId);
                  }}
                />
              )}
              {!isSteward && !isDeleted && isManagementMode && (
                <AppIconButton
                  variant="delete"
                  tooltip="Delete Section"
                  icon={<TrashIcon style={{ width: 14, height: 14 }} />}
                  size="small"
                  // Colour comes from variant="delete" (--status-bad-bg tint with a
                  // --status-bad-text glyph). It used to be overridden here with
                  // theme.button.danger.bg for the fill and theme.danger for the
                  // icon — both resolve to #DC2626 on the saffron preset, so the
                  // trash icon was painted the same colour as the button under it
                  // and rendered as a blank red square. Only layout stays.
                  sx={{
                    display: { xs: isExpanded ? 'flex' : 'none', md: 'flex' },
                    padding: '5px',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSection(section.id, section.name);
                  }}
                />
              )}
            </Box>
          </Box>

          <Collapse in={isExpanded}>
            <Box sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: surface }}>
              {tables.length > 0 ? (
                viewMode === 'grid' ? (
                  <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                    {tables.map((tableData) =>
                      renderTable(tableData, gridColumns, isDeleted, floorName, section.name)
                    )}
                  </Grid>
                ) : (
                  <Box display="flex" flexDirection="column" gap={1}>
                    {tables.map((tableData) =>
                      renderTableListItem(tableData, isDeleted, floorName, section.name)
                    )}
                  </Box>
                )
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: { xs: 3, sm: 4 },
                    px: { xs: 2, sm: 3 },
                    backgroundColor: surface,
                    borderRadius: '6px',
                    border: `1px dashed ${borderColor}`
                  }}
                >
                  <TableCellsIcon style={{ width: 32, height: 32, color: theme.gray?.[300] || textSecondary }} />
                  <Typography variant="caption" sx={{ color: theme.gray?.[500] || textSecondary, mt: 1, fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.813rem' } }}>
                    No tables in this section
                  </Typography>
                  {!isSteward && isManagementMode && (
                    <Button
                      size="small"
                      startIcon={<PlusCircleIcon style={{ width: 12, height: 12 }} />}
                      onClick={() => {
                        setSelectedSectionForTable(section.id);
                        setSelectedFloorIdForTable(floorId);
                        setSelectedSectionNameForTable(section.name);
                        setOpenTableDialog(true);
                      }}
                      sx={{
                        display: { xs: isExpanded ? 'flex' : 'none', md: 'flex' }, // Show on mobile only when section expanded, always show on desktop
                        mt: 1.5,
                        backgroundColor: `${theme.button?.success?.transparentBg || 'rgba(16, 185, 129, 0.13)'} !important`,
                        border: `1.5px solid ${theme.success || '#10B981'} !important`,
                        color: `${theme.success || '#059669'} !important`,
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 0.5, sm: 0.75 },
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        fontWeight: 600,
                        boxShadow: 'none !important',
                        '&:hover': {
                          backgroundColor: 'rgba(16, 185, 129, 0.2) !important',
                          border: `1.5px solid ${theme.success || '#10B981'} !important`,
                          boxShadow: '0 2px 4px rgba(16, 185, 129, 0.15) !important'
                        }
                      }}
                    >
                      Add Tables
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Box>
    );
  };

  const renderFloor = (floorKey: string) => {
    const isExpanded = expandedFloors.has(floorKey);
    const floorId = floorKey.replace('floor_', '');
    const isLoadingThisFloor = loadingFloors.has(floorId);

    // Try to get data from optimized sources first
    const floorSummary = floorSummaries.find(f => f.id === parseInt(floorId));
    const cachedSections = cachedFloorData.get(floorId);

    // Fallback to legacy floorLayout if optimized data not available
    const floorData = floorLayout[floorKey];

    // Determine which data to use
    let floor, isDeleted, totalActiveOrders, totalTablesInFloor, sectionCount;

    if (floorSummary) {
      // Use optimized data
      floor = {
        id: floorSummary.id.toString(),
        name: floorSummary.name,
        floorNumber: floorSummary.floorNumber,
        vendorId: floorSummary.vendorId.toString(),
        sections: [],
        isDeleted: floorSummary.isDeleted
      };
      isDeleted = floorSummary.isDeleted;
      totalActiveOrders = floorSummary.activeOrderCount;
      totalTablesInFloor = floorSummary.tableCount;
      sectionCount = floorSummary.sectionCount;
    } else if (floorData) {
      // Use legacy data
      floor = floorData.floor;
      isDeleted = floor.isDeleted === true;
      totalActiveOrders = floorData.sections.reduce((sum, s) => sum + s.tables.reduce((acc, t) => acc + (t.activeOrders?.length || 0), 0), 0);
      totalTablesInFloor = floorData.sections.reduce((acc, s) => acc + s.tables.length, 0);
      sectionCount = floorData.sections.length;
    } else {
      // No data available
      return null;
    }

    return (
      <Box key={floorKey} mb={{ xs: 1.5, sm: 2 }} data-testid={`floor-card-${floor.id}`}>
        <Paper
          elevation={0}
          sx={{
            background: isDeleted ? '#f5f5f5' : surface,
            border: `1px solid ${isDeleted ? '#e0e0e0' : borderColor}`,
            borderRadius: { xs: '8px', sm: '10px' },
            overflow: 'hidden',
            boxShadow: isDeleted ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            opacity: isDeleted ? 0.6 : 1,
            transition: 'all 0.2s ease',
            '&:hover': !isDeleted ? {
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
            } : {}
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              background: surface,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'row',
              gap: { xs: 1, sm: 0 },
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: isExpanded ? `1px solid ${borderColor}` : 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: neutralLight
              }
            }}
            onClick={() => toggleFloorExpansion(floorKey)}
          >
            <Box display="flex" alignItems="center" gap={{ xs: 0.75, sm: 1.5 }} sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
              {isExpanded ? (
                <ChevronDownIcon style={{ width: 16, height: 16, color: textSecondary, flexShrink: 0 }} />
              ) : (
                <ChevronRightIcon style={{ width: 16, height: 16, color: textSecondary, flexShrink: 0 }} />
              )}
              <TintIcon
                icon={<BuildingOfficeIcon style={{ width: 18, height: 18, color: primary }} />}
                size={{ xs: 28, sm: 36 }}
                borderOpacity="25"
              />
              <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <Typography
                  noWrap
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: textPrimary,
                    fontSize: { xs: '0.813rem', sm: '0.938rem', md: '1rem' },
                    lineHeight: 1.2
                  }}
                >
                  {floor.floorNumber !== undefined && floor.floorNumber !== null
                    ? `Floor ${floor.floorNumber}: ${floor.name}`
                    : floor.name}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5} mt={0.25} sx={{ flexWrap: 'nowrap', overflow: 'hidden' }}>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      color: primary,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      flexShrink: 0,
                    }}
                  >
                    {sectionCount}s
                  </Typography>
                  <Typography variant="caption" sx={{ color: borderColor, flexShrink: 0, fontSize: '0.6rem' }}>|</Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      color: textSecondary,
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      flexShrink: 0,
                    }}
                  >
                    {totalTablesInFloor}t
                  </Typography>
                  <Typography variant="caption" sx={{ color: borderColor, flexShrink: 0, fontSize: '0.6rem' }}>|</Typography>
                  {totalActiveOrders > 0 ? (
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        color: theme.components?.status?.occupied?.text || theme.warning || '#D97706',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        flexShrink: 0,
                      }}
                    >
                      {totalActiveOrders} ord
                    </Typography>
                  ) : (
                    <Typography variant="caption" noWrap sx={{ color: textSecondary, fontWeight: 500, fontSize: '0.7rem', flexShrink: 0 }}>
                      0 ord
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {!isSteward && isManagementMode && (
              <Box display="flex" gap={0.5} sx={{ flexShrink: 0 }}>
                {/* Restore and Permanent Delete buttons for deleted floors */}
                {isDeleted ? (
                  <>
                    <AppIconButton
                      variant="success"
                      tooltip="Restore Floor"
                      icon={<ArrowPathIcon style={{ width: 16, height: 16 }} />}
                      size="small"
                      sx={{
                        display: { xs: isExpanded ? 'flex' : 'none', md: 'flex' },
                        padding: '5px',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRestoringEntity({
                          type: 'floor',
                          id: floor.id,
                          name: floor.name
                        });
                        setOpenRestoreDialog(true);
                      }}
                    />
                    <AppIconButton
                      variant="delete"
                      tooltip="Permanently Delete Floor"
                      icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                      size="small"
                      sx={{
                        display: { xs: isExpanded ? 'flex' : 'none', md: 'flex' },
                        padding: '5px',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingEntity({
                          type: 'floor',
                          id: floor.id,
                          name: floor.name,
                          isPermanent: true
                        });
                        setOpenPermanentDeleteDialog(true);
                      }}
                    />
                  </>
                ) : (
                  <>
                    {/* Add Section — icon-only on mobile, text on desktop */}
                    <AppIconButton
                      variant="edit"
                      tooltip="Add Section"
                      icon={<PlusCircleIcon style={{ width: 16, height: 16 }} />}
                      size="small"
                      sx={{
                        display: { xs: isExpanded ? 'flex' : 'none', sm: 'none' },
                        padding: '5px',
                        backgroundColor: `${theme.button?.secondary?.transparentBg || 'rgba(29, 78, 216, 0.1)'} !important`,
                        color: `${theme.button?.secondary?.text || '#1D4ED8'} !important`,
                        '&:hover': { backgroundColor: `${theme.button?.secondary?.hover || '#DBEAFE'} !important` }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFloorForSection(floorKey);
                        setOpenSectionDialog(true);
                      }}
                    />
                    <Button
                      size="small"
                      startIcon={<PlusCircleIcon style={{ width: 14, height: 14 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFloorForSection(floorKey);
                        setOpenSectionDialog(true);
                      }}
                      sx={{
                        display: { xs: 'none', sm: 'inline-flex' },
                        backgroundColor: `${theme.button?.secondary?.transparentBg || 'rgba(29, 78, 216, 0.1)'} !important`,
                        color: `${theme.button?.secondary?.text || '#1D4ED8'} !important`,
                        fontWeight: 600,
                        fontSize: '0.813rem',
                        px: 2.5,
                        py: 1,
                        minWidth: '120px',
                        borderRadius: '10px !important',
                        border: 'none !important',
                        boxShadow: 'none !important',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          backgroundColor: `${theme.button?.secondary?.hover || '#DBEAFE'} !important`,
                          boxShadow: 'none !important'
                        }
                      }}
                    >
                      Add Section
                    </Button>
                    <AppIconButton
                      variant="utility"
                      tooltip="Edit Floor"
                      icon={<PencilIcon style={{ width: 16, height: 16 }} />}
                      size="small"
                      sx={{
                        display: { xs: isExpanded ? 'flex' : 'none', md: 'flex' },
                        padding: '5px',
                        border: 'none !important',
                        color: `${textSecondary} !important`,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditFloor({
                          id: floor.id,
                          name: floor.name,
                          floorNumber: floor.floorNumber
                        });
                      }}
                    />
                    <AppIconButton
                      variant="delete"
                      tooltip="Delete Floor"
                      icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                      size="small"
                      // Same as Delete Section above: let variant="delete" paint it.
                      // The override made the icon and the fill both #DC2626.
                      sx={{
                        display: { xs: isExpanded ? 'flex' : 'none', md: 'flex' },
                        padding: '5px',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFloor(floor.id, floor.name);
                      }}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>

          <Collapse in={isExpanded}>
            <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: surface }}>
              {isLoadingThisFloor ? (
                // Show loading indicator while fetching sections
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2 }}>
                  <CircularProgress size={24} sx={{ color: primary }} />
                  <Typography variant="body2" sx={{ color: textSecondary }}>
                    Loading sections...
                  </Typography>
                </Box>
              ) : cachedSections && cachedSections.sections.length > 0 ? (
                // Use cached/optimized data
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                  {cachedSections.sections.map((sectionData, index) => renderSection(sectionData, index, floor.id, floor.name))}
                </Box>
              ) : floorData && floorData.sections.length > 0 ? (
                // Fallback to legacy data
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                  {floorData.sections.map((sectionData, index) => renderSection(sectionData, index, floor.id, floor.name))}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: { xs: 4, sm: 6 },
                    px: { xs: 2, sm: 4 },
                    backgroundColor: surface,
                    borderRadius: '8px',
                    border: `1px dashed ${borderColor}`
                  }}
                >
                  <Typography variant="body2" sx={{ color: theme.gray?.[500] || textSecondary, fontWeight: 500, fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
                    No sections in this floor
                  </Typography>
                  {!isSteward && isManagementMode && (
                    <Button
                      startIcon={<PlusCircleIcon style={{ width: 16, height: 16 }} />}
                      onClick={() => {
                        setSelectedFloorForSection(floorKey);
                        setOpenSectionDialog(true);
                      }}
                      sx={{
                        display: { xs: isExpanded ? 'flex' : 'none', md: 'flex' }, // Show on mobile only when expanded, always show on desktop
                        mt: 2,
                        backgroundColor: `${theme.button?.secondary?.transparentBg || 'rgba(29, 78, 216, 0.1)'} !important`,
                        border: `1.5px solid ${theme.button?.secondary?.border || '#1D4ED8'} !important`,
                        color: `${theme.button?.secondary?.text || '#1D4ED8'} !important`,
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', sm: '0.813rem' },
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 0.5, sm: 0.75 },
                        boxShadow: 'none !important',
                        '&:hover': {
                          backgroundColor: `${theme.button?.secondary?.hover || '#DBEAFE'} !important`,
                          border: `1.5px solid ${theme.button?.secondary?.border || '#1D4ED8'} !important`,
                          boxShadow: '0 2px 4px rgba(29, 78, 216, 0.15) !important'
                        }
                      }}
                    >
                      Add Section
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', position: 'relative' }}>
        <Typography>Loading floor layout...</Typography>
        <Backdrop open={isSettlingBill} sx={{ color: 'var(--text-inverse)', zIndex: (theme) => theme.zIndex.modal + 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress color="inherit" />
            <Typography variant="body2">Settling bill…</Typography>
          </Box>
        </Backdrop>
      </Box>
    );
  }

  // Use optimized floor summaries when available, fallback to legacy floorLayout
  const useOptimized = floorSummaries.length > 0;
  const floors = useOptimized
    ? floorSummaries.map(f => `floor_${f.id}`)
    : Object.keys(floorLayout).filter(k => k.startsWith('floor_'));
  const visibleFloors = selectedFloorKey ? [selectedFloorKey] : floors;

  return (
    <Box sx={{
      ...(embedded ? { height: '100%', overflow: 'auto' } : { minHeight: '100vh' }),
      backgroundColor: theme.background,
    }}>
      {/* Toaster provided globally via ToastProvider */}

      {/* Transfer Mode Banner — full-bleed: negative margins cancel Layout's px-4/px-6/px-8 */}
      {transferMode?.active && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            color: 'white',
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 1.5, sm: 2 },
            mx: { xs: -2, sm: -3, lg: -4 },
            mt: { xs: -1, sm: -3 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1, sm: 0 },
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 20
          }}
        >
          <Box display="flex" alignItems="center" gap={1} sx={{ flexWrap: 'wrap', minWidth: 0 }}>
            <ArrowsRightLeftIcon style={{ width: 20, height: 20, flexShrink: 0 }} />
            <Typography fontWeight={600} sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
              Transfer Mode: Select target table for {transferMode.activeOrderCount} order(s) from &ldquo;{transferMode.sourceTableName}&rdquo;
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setTransferMode(null)}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              flexShrink: 0,
              alignSelf: { xs: 'flex-end', sm: 'center' },
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* Merge Mode Banner + floating action bar */}
      {mergeMode?.active && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            color: 'white',
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 1.5, sm: 2 },
            mx: { xs: -2, sm: -3, lg: -4 },
            mt: { xs: -1, sm: -3 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1, sm: 0 },
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 20
          }}
        >
          <Box display="flex" alignItems="center" gap={1} sx={{ flexWrap: 'wrap', minWidth: 0 }}>
            <LinkIcon style={{ width: 20, height: 20, flexShrink: 0 }} />
            <Typography fontWeight={600} sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
              Merge Mode: Tap tables to merge with &ldquo;{mergeMode.sourceTableName}&rdquo;
              {mergeMode.selectedTableIds.size > 0 && (
                <span style={{ marginLeft: 8, backgroundColor: 'rgba(255,255,255,0.25)', padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem' }}>
                  {mergeMode.selectedTableIds.size} selected
                </span>
              )}
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center" sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
            {mergeMode.selectedTableIds.size > 0 && (
              <Button
                variant="contained"
                size="small"
                onClick={handleExecuteMerge}
                disabled={isMerging}
                style={{
                  backgroundColor: 'var(--bg-surface)',
                  color: 'var(--status-warn-text)',
                  fontWeight: 700,
                  fontSize: '0.813rem',
                  textTransform: 'none',
                  borderRadius: 8,
                }}
              >
                {isMerging ? 'Merging...' : `Merge ${mergeMode.selectedTableIds.size + 1} Tables`}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => setMergeMode(null)}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                flexShrink: 0,
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {/* Waitlist seat/notify mode — banner over the live floor plan */}
      {waitlistMode?.active && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            color: 'white',
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 1.25, sm: 1.5 },
            mx: { xs: -2, sm: -3, lg: -4 },
            mt: { xs: -1, sm: -3 },
            position: 'sticky',
            top: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
            <Typography fontWeight={700} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} noWrap>
              {waitlistMode.action === 'seat' ? 'Seat walk-in' : 'Notify walk-in'}: {waitlistMode.customerName}
            </Typography>
            <Box
              sx={{
                backgroundColor: 'rgba(255,255,255,0.25)',
                px: 1,
                py: 0.25,
                borderRadius: 999,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              Party of {waitlistMode.partySize}
            </Box>
            <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' }, opacity: 0.9, display: { xs: 'none', sm: 'block' } }}>
              Tap a free table to {waitlistMode.action === 'seat' ? 'seat' : 'assign'}.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => { setWaitlistMode(null); navigate('/vendor/reservations'); }}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* Reservation Mode — Banner + Compact Form + Floating Submit */}
      {reservationMode?.active && (
        <>
          {/* Reservation band — aubergine to match the app shell (PageHeroBand /
              rail), not a foreign teal. Safe-area top so the title + Cancel clear
              the status-bar notch. */}
          <Box
            sx={{
              background: 'var(--band-bg)',
              color: 'white',
              px: { xs: 2, sm: 3, lg: 4 },
              pt: { xs: 'calc(env(safe-area-inset-top, 0px) + 10px)', sm: 1.5 },
              pb: { xs: 1, sm: 1.5 },
              mx: { xs: -2, sm: -3, lg: -4 },
              mt: { xs: -1, sm: -3 },
              position: 'sticky',
              top: 0,
              zIndex: 20,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarDaysIcon style={{ width: 20, height: 20, flexShrink: 0 }} />
                <Typography fontWeight={600} sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
                  {reservationMode.editingReservationId
                    ? `Edit Reservation${reservationMode.customerName ? ' — ' + reservationMode.customerName : ''}`
                    : 'Create Reservation — Tap a table to select'}
                  {reservationMode.selectedTableIds.size > 0 && (
                    <span style={{ marginLeft: 8, backgroundColor: 'rgba(255,255,255,0.25)', padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem' }}>
                      {reservationMode.selectedTableIds.size} selected
                    </span>
                  )}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => { setReservationMode(null); setReservationAvailableTables([]); setReservationSuggestions([]); }}
                sx={{
                  color: 'white', borderColor: 'rgba(255,255,255,0.5)', flexShrink: 0,
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                Cancel
              </Button>
            </Box>

            {/* Compact form row */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <TextField
                placeholder="Customer Name *"
                size="small"
                value={reservationMode.customerName}
                onChange={(e) => setReservationMode(prev => prev ? { ...prev, customerName: e.target.value } : null)}
                sx={{ flex: '1 1 140px', minWidth: 120, '& .MuiOutlinedInput-root': { bgcolor: 'white', color: 'var(--text-primary)', borderRadius: 1.5, '& fieldset': { borderColor: 'rgba(255,255,255,0.6)' }, '&:hover fieldset': { borderColor: 'white' }, '&.Mui-focused fieldset': { borderColor: 'var(--action)' } }, '& input::placeholder': { color: 'var(--text-tertiary)' } }}
              />
              <TextField
                placeholder="Phone *"
                size="small"
                value={reservationMode.customerPhone}
                onChange={(e) => setReservationMode(prev => prev ? { ...prev, customerPhone: e.target.value } : null)}
                sx={{ flex: '1 1 110px', minWidth: 100, '& .MuiOutlinedInput-root': { bgcolor: 'white', color: 'var(--text-primary)', borderRadius: 1.5, '& fieldset': { borderColor: 'rgba(255,255,255,0.6)' }, '&:hover fieldset': { borderColor: 'white' }, '&.Mui-focused fieldset': { borderColor: 'var(--action)' } }, '& input::placeholder': { color: 'var(--text-tertiary)' } }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'white', borderRadius: 1.5, px: 1.5, py: 0.5 }}>
                <IconButton size="small" sx={{ color: 'var(--action)', p: 0.25 }} onClick={() => setReservationMode(prev => prev ? { ...prev, partySize: Math.max(1, prev.partySize - 1) } : null)}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}>−</Typography>
                </IconButton>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', minWidth: 20, textAlign: 'center', color: 'var(--text-primary)' }}>
                  {reservationMode.partySize}
                </Typography>
                <IconButton size="small" sx={{ color: 'var(--action)', p: 0.25 }} onClick={() => setReservationMode(prev => prev ? { ...prev, partySize: Math.min(reservationSettings?.maxPartySize || 20, prev.partySize + 1) } : null)}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}>+</Typography>
                </IconButton>
                <Typography sx={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>guests</Typography>
              </Box>
              <TextField
                type="date"
                size="small"
                value={reservationMode.date}
                onChange={(e) => setReservationMode(prev => prev ? { ...prev, date: e.target.value } : null)}
                inputProps={{
                  min: todayLocalIso(),
                  ...(reservationSettings?.maxAdvanceBookingDays && {
                    max: toLocalIsoDate(new Date(Date.now() + reservationSettings.maxAdvanceBookingDays * 86400000)),
                  }),
                }}
                sx={{ flex: '0 0 150px', '& .MuiOutlinedInput-root': { bgcolor: 'white', color: 'var(--text-primary)', borderRadius: 1.5, '& fieldset': { borderColor: 'rgba(255,255,255,0.6)' }, '&:hover fieldset': { borderColor: 'white' }, '&.Mui-focused fieldset': { borderColor: 'var(--action)' } } }}
              />
              <FormControl size="small" sx={{ flex: '0 0 100px' }}>
                <Select
                  value={reservationMode.startTime}
                  onChange={(e) => setReservationMode(prev => prev ? { ...prev, startTime: e.target.value as string } : null)}
                  sx={{ bgcolor: 'white', color: 'var(--text-primary)', borderRadius: 1.5, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' }, '& .MuiSvgIcon-root': { color: 'var(--text-tertiary)' } }}
                >
                  {(() => {
                    const openH = parseInt(reservationSettings?.openingTime?.split(':')[0] || '10');
                    const lastH = parseInt(reservationSettings?.lastReservationTime?.split(':')[0] || '23');
                    const lastM = parseInt(reservationSettings?.lastReservationTime?.split(':')[1] || '00');
                    // For a same-day booking, slots earlier than now + the min-advance
                    // window are unbookable — grey them out so staff can't hit a 400.
                    const now = new Date();
                    const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                    const isToday = reservationMode.date === todayIso;
                    const minAdvanceMin = (reservationSettings?.minAdvanceBookingHours ?? 1) * 60;
                    const earliestMin = isToday ? (now.getHours() * 60 + now.getMinutes() + minAdvanceMin) : -1;
                    const slots: React.ReactNode[] = [];
                    for (let h = openH; h <= lastH; h++) {
                      for (const m of [0, 30]) {
                        if (h === lastH && m > lastM) break;
                        const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                        const disabled = (h * 60 + m) < earliestMin;
                        slots.push(<MenuItem key={val} value={val} disabled={disabled}>{val}{disabled ? ' (too soon)' : ''}</MenuItem>);
                      }
                    }
                    return slots;
                  })()}
                </Select>
              </FormControl>
            </Box>

            {/* Availability status bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, fontSize: '0.75rem', opacity: 0.9 }}>
              {reservationCheckLoading ? (
                <>
                  <CircularProgress size={12} sx={{ color: 'white' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: 'white' }}>Checking availability...</Typography>
                </>
              ) : reservationMode.availableTableIds.size === 0 ? (
                <Typography sx={{ fontSize: '0.75rem', color: '#FDE68A' }}> {/* basil-todo */}
                  No tables available for this time — try a different date or time
                </Typography>
              ) : (
                <>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#34D399', flexShrink: 0 }} /> {/* basil-todo */}
                  <Typography sx={{ fontSize: '0.75rem', color: 'white' }}>
                    {reservationMode.availableTableIds.size} table{reservationMode.availableTableIds.size !== 1 ? 's' : ''} available
                    {reservationMode.needsMerge && ' — select multiple to accommodate party'}
                  </Typography>
                </>
              )}
            </Box>

            {/* Recommendation cards (horizontal scroll) */}
            {reservationSuggestions.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { height: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 2 } }}>
                {reservationSuggestions.slice(0, 4).map((s: any, idx: number) => {
                  const tableNames = s.tables.map((t: any) => t.name).join(' + ');
                  const isSelected = s.tables.length === reservationMode.selectedTableIds.size && s.tables.every((t: any) => reservationMode.selectedTableIds.has(t.id));
                  return (
                    <Box
                      key={idx}
                      onClick={() => setReservationMode(prev => prev ? { ...prev, selectedTableIds: new Set(s.tables.map((t: any) => t.id)) } : null)}
                      sx={{
                        flexShrink: 0, minWidth: 150, px: 1.5, py: 1, borderRadius: 1.5,
                        bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)',
                        border: 1, borderColor: isSelected ? 'white' : 'rgba(255,255,255,0.2)',
                        cursor: 'pointer', transition: 'all 0.15s',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{tableNames}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.25 }}>
                        <Typography sx={{ fontSize: '0.7rem', opacity: 0.8 }}>{s.totalCapacity} seats</Typography>
                        <Box sx={{ px: 0.5, py: 0.1, borderRadius: 0.5, fontSize: '0.6rem', fontWeight: 700, bgcolor: idx === 0 && !s.merged ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)' }}>
                          {s.merged ? 'MERGE' : idx === 0 ? 'BEST FIT' : 'FITS'}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Floating bottom submit bar — anchored to right of the sidebar so it
              doesn't sit on top of the nav rail on desktop. */}
          {reservationMode.selectedTableIds.size > 0 && (
            <Box
              sx={{
                position: 'fixed',
                // Sit ABOVE the mobile bottom nav (~64px + safe area) so the
                // Create button and chips aren't hidden behind it; flush to the
                // bottom on desktop where there is no bottom nav.
                bottom: { xs: 'calc(env(safe-area-inset-bottom, 0px) + 62px)', md: 0 },
                left: { xs: 0, md: 'clamp(180px, 16vw, 220px)' },
                right: 0,
                bgcolor: 'white', borderTop: '2px solid var(--action)',
                px: { xs: 2, sm: 3 }, py: 1.5,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 2, zIndex: 1300,
                boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minWidth: 0 }}>
                {Array.from(reservationMode.selectedTableIds).map(id => {
                  const table = reservationAvailableTables.find((t: any) => t.id === id);
                  return (
                    <Chip
                      key={id}
                      label={`${table?.name || `Table ${id}`} (${table?.seatingCapacity || '?'} seats)`}
                      size="small"
                      onDelete={() => setReservationMode(prev => {
                        if (!prev) return null;
                        const next = new Set(prev.selectedTableIds);
                        next.delete(id);
                        return { ...prev, selectedTableIds: next };
                      })}
                      sx={{ bgcolor: 'color-mix(in srgb, var(--action) 8%, transparent)', borderColor: 'var(--action)', border: 1, fontWeight: 500, fontSize: '0.75rem' }}
                    />
                  );
                })}
                {(() => {
                  const totalCap = Array.from(reservationMode.selectedTableIds).reduce((sum, id) => {
                    const t = reservationAvailableTables.find((t: any) => t.id === id);
                    return sum + (t?.seatingCapacity || 0);
                  }, 0);
                  return (
                    <Typography variant="caption" sx={{ color: totalCap >= reservationMode.partySize ? '#059669' : '#D97706', fontWeight: 600, alignSelf: 'center', ml: 1 }}>
                      {totalCap}/{reservationMode.partySize} seats
                    </Typography>
                  );
                })()}
              </Box>
              {(() => {
                const selectedCapacity = Array.from(reservationMode.selectedTableIds).reduce((sum, id) => {
                  const t = reservationAvailableTables.find((t: any) => t.id === id);
                  return sum + (t?.seatingCapacity || 0);
                }, 0);
                const undersized = reservationMode.selectedTableIds.size > 0 && selectedCapacity < reservationMode.partySize;
                const missing: string[] = [];
                if (!reservationMode.customerName.trim()) missing.push('name');
                if (!reservationMode.customerPhone.trim()) missing.push('phone');
                const hint = undersized
                  ? `Seats ${selectedCapacity} < party ${reservationMode.partySize} — add a table`
                  : (missing.length ? `Add ${missing.join(' & ')} to continue` : null);
                const blocked = undersized || !reservationMode.customerName.trim() || !reservationMode.customerPhone.trim();
                return (
                  <>
                    {hint && (
                      <Typography variant="caption" sx={{ color: 'var(--status-warn-text)', fontWeight: 600, alignSelf: 'center', whiteSpace: 'nowrap' }}>
                        {hint}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      onClick={handleSubmitReservation}
                      // Block on in-flight OR an undersized merge (the backend rejects
                      // it too); name/phone still toast a helpful error on submit.
                      disabled={reservationSubmitting || undersized}
                      sx={{
                        bgcolor: 'var(--action)', '&:hover': { bgcolor: 'var(--action)' },
                        textTransform: 'none', fontWeight: 600, flexShrink: 0,
                        borderRadius: 2, px: 3,
                        opacity: blocked ? 0.6 : 1,
                      }}
                    >
                      {reservationSubmitting
                        ? (reservationMode.editingReservationId ? 'Saving...' : 'Creating...')
                        : (reservationMode.editingReservationId ? 'Save Changes' : 'Create Reservation')}
                    </Button>
                  </>
                );
              })()}
            </Box>
          )}
        </>
      )}

      {/* Reservation success confirmation */}
      <Dialog open={!!reservationSuccess} onClose={() => setReservationSuccess(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'var(--status-good-bg, #E6F4EA)', color: 'var(--status-good-text, #1E7E34)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>✓</Box>
          Reservation {reservationSuccess?.updated ? 'updated' : 'confirmed'}
        </DialogTitle>
        <DialogContent>
          {reservationSuccess && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '1.05rem' }}>{reservationSuccess.customerName}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                <span>👥 Party of {reservationSuccess.partySize}</span>
                <span>🍽 {reservationSuccess.tables}</span>
              </Box>
              <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                📅 {reservationSuccess.date} at {reservationSuccess.startTime}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReservationSuccess(null)} variant="contained" sx={{ bgcolor: 'var(--action)', '&:hover': { bgcolor: 'var(--action)' }, textTransform: 'none', fontWeight: 600 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* EPOS Table Selection Mode Banner */}
      {isEposSelectMode && !embedded && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
            color: 'white',
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 1.5, sm: 2 },
            mx: { xs: -2, sm: -3, lg: -4 },
            mt: { xs: -1, sm: -3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 20,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <TableCellsIcon style={{ width: 20, height: 20 }} />
            <Typography variant="body2" fontWeight={600}>
              Tap a table to start ordering
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/vendor/epos')}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              flexShrink: 0,
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* Header toolbar (vendors only) — full-bleed, compact single row */}
      {!isSteward && !embedded && (
        <Box
          sx={{
            mx: { xs: -2, sm: -3, lg: -4 },
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 0.75, sm: 1.5 },
            backgroundColor: surface,
            borderBottom: `1px solid ${borderColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            zIndex: 9,
          }}
        >
          {/* Left: back + title */}
          <Box display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
            <AppIconButton
              variant="subtle"
              tooltip="Back to Settings"
              icon={<ArrowLeftIcon style={{ width: 18, height: 18 }} />}
              size="small"
              sx={{ flexShrink: 0 }}
              onClick={() => navigate('/vendor/settings')}
            />
            <Typography
              noWrap
              sx={{
                fontWeight: 700,
                color: textPrimary,
                fontSize: { xs: '0.938rem', sm: '1.25rem' },
                lineHeight: 1.2
              }}
            >
              Tables
            </Typography>
          </Box>

          {/* Right: controls */}
          <Box display="flex" alignItems="center" gap={0.75} sx={{ flexShrink: 0 }}>
            {/* View toggle — pill style matching manage button */}
            <Box data-testid="view-mode-switcher" sx={{ display: 'flex', gap: '2px', backgroundColor: neutralLight, borderRadius: '8px', p: '2px', border: `1px solid ${borderColor}` }}>
              {([
                { value: 'grid' as const, icon: <ViewModuleIcon sx={{ fontSize: 16 }} />, label: 'Grid' },
                { value: 'list' as const, icon: <ViewListIcon sx={{ fontSize: 16 }} />, label: 'List' },
                { value: 'floorplan' as const, icon: <MapIcon sx={{ fontSize: 16 }} />, label: 'Plan' },
              ]).map(opt => (
                <Box
                  key={opt.value}
                  data-testid={`view-mode-${opt.value}`}
                  role="button"
                  aria-pressed={viewMode === opt.value}
                  onClick={() => {
                    setViewMode(opt.value);
                    if (opt.value === 'floorplan') {
                      const activeFloorId = selectedFloorKey ? parseInt(selectedFloorKey.replace('floor_', '')) : (floorSummaries[0]?.id || null);
                      if (activeFloorId) fetchFloorAssets(activeFloorId);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.375,
                    px: { xs: 0.75, sm: 1 },
                    py: 0.375,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: viewMode === opt.value ? surface : 'transparent',
                    boxShadow: viewMode === opt.value ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                    color: viewMode === opt.value ? textPrimary : neutral,
                    transition: 'all 0.15s',
                    '&:hover': {
                      backgroundColor: viewMode === opt.value ? surface : `${neutral}15`,
                      color: viewMode === opt.value ? textPrimary : textSecondary,
                    },
                    userSelect: 'none',
                  }}
                >
                  {opt.icon}
                  <Typography sx={{ fontSize: '0.688rem', fontWeight: 600, lineHeight: 1, display: { xs: 'none', sm: 'block' } }}>
                    {opt.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Management mode toggle */}
            <Tooltip title={isManagementMode ? 'Exit Management Mode' : 'Management Mode'}>
              <Box
                data-testid="management-mode-toggle"
                role="button"
                aria-pressed={isManagementMode}
                onClick={() => setIsManagementMode(!isManagementMode)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: isManagementMode ? `${primary}15` : neutralLight,
                  border: isManagementMode ? `1px solid ${primary}40` : `1px solid ${borderColor}`,
                  transition: 'all 0.15s',
                  '&:hover': { backgroundColor: isManagementMode ? `${primary}22` : `${neutral}15` },
                  height: 32,
                }}
              >
                <Cog6ToothIcon style={{ width: 15, height: 15, color: isManagementMode ? primary : neutral }} />
                <Typography sx={{
                  display: { xs: 'none', sm: 'block' },
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: isManagementMode ? primary : neutral,
                  whiteSpace: 'nowrap'
                }}>
                  Manage
                </Typography>
              </Box>
            </Tooltip>

            {/* Add Floor button — only in management mode */}
            {isManagementMode && (
              <>
                {/* Mobile: icon only */}
                <IconButton
                  onClick={() => setOpenFloorDialog(true)}
                  size="small"
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                    backgroundColor: `${primary} !important`,
                    color: 'white !important',
                    width: 32,
                    height: 32,
                    borderRadius: '8px !important',
                    '&:hover': { backgroundColor: `${primaryHover} !important` }
                  }}
                >
                  <AddIcon sx={{ fontSize: 18 }} />
                </IconButton>
                {/* Desktop: full button */}
                <Button
                  variant="contained"
                  startIcon={<BuildingOfficeIcon style={{ width: 16, height: 16 }} />}
                  onClick={() => setOpenFloorDialog(true)}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    backgroundColor: `${primary} !important`,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.813rem',
                    px: 2,
                    py: 0.75,
                    whiteSpace: 'nowrap',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: `${primaryHover} !important` }
                  }}
                >
                  Add Floor
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Floor / Section navigation pills */}
      {(isSteward || isVendor) && floors.length > 0 && (
        <Box sx={{
          mx: { xs: -2, sm: -3, lg: -4 },
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 0.75, sm: 1.25 },
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: surface,
          borderBottom: `1px solid ${borderColor}`,
        }}>
          <Box
            ref={sectionChipsContainerRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}
          >
            {showSectionNav ? (
              <>
                {/* Back button: return to floor mode */}
                <Chip
                  label={selectedFloorInfo?.floorNumber !== undefined && selectedFloorInfo?.floorNumber !== null
                    ? `F${selectedFloorInfo.floorNumber}`
                    : selectedFloorInfo?.name || 'Back'}
                  onClick={() => setSelectedFloorKey(null)}
                  size="small"
                  icon={<ArrowLeftIcon style={{ width: 14, height: 14 }} />}
                  sx={{
                    backgroundColor: neutralLight,
                    color: neutral,
                    border: `1.5px solid ${borderColor}`,
                    fontWeight: 700,
                    height: { xs: 28, sm: 32 },
                    fontSize: { xs: '0.75rem', sm: '0.813rem' },
                    '& .MuiChip-label': { px: { xs: 1, sm: 1.5 } },
                    '& .MuiChip-icon': { ml: { xs: 0.5, sm: 0.75 } },
                    '&:hover': { backgroundColor: `${neutral}15` },
                    transition: 'all 0.15s ease',
                    flexShrink: 0,
                  }}
                />
                {/* Section chips */}
                {visibleSections.map((sectionData, index) => {
                  const section = sectionData.section;
                  const isActive = activeSectionId === section.id;

                  return (
                    <Chip
                      key={section.id}
                      label={section.name}
                      data-section-chip={section.id}
                      onClick={() => scrollToSection(section.id)}
                      size="small"
                      sx={{
                        backgroundColor: isActive ? `${primary}18` : neutralLight,
                        color: isActive ? primary : neutral,
                        border: isActive ? `1.5px solid ${primary}40` : `1px solid ${borderColor}`,
                        fontWeight: isActive ? 700 : 600,
                        height: { xs: 28, sm: 32 },
                        fontSize: { xs: '0.75rem', sm: '0.813rem' },
                        '& .MuiChip-label': { px: { xs: 1, sm: 1.5 } },
                        '&:hover': {
                          backgroundColor: isActive ? `${primary}25` : `${neutral}12`,
                        },
                        transition: 'all 0.15s ease',
                        flexShrink: 0,
                      }}
                    />
                  );
                })}
              </>
            ) : (
              <>
                {/* Floor mode: "All" chip + floor chips (existing behavior) */}
                <Chip
                  label="All"
                  onClick={() => setSelectedFloorKey(null)}
                  size="small"
                  icon={<BuildingOfficeIcon style={{ width: 14, height: 14 }} />}
                  sx={{
                    backgroundColor: selectedFloorKey === null ? `${primary}33` : neutralLight,
                    color: selectedFloorKey === null ? primary : neutral,
                    border: selectedFloorKey === null ? `1.5px solid ${primary}50` : `1px solid ${borderColor}`,
                    fontWeight: selectedFloorKey === null ? 700 : 600,
                    height: { xs: 28, sm: 32 },
                    fontSize: { xs: '0.75rem', sm: '0.813rem' },
                    '& .MuiChip-label': { px: { xs: 1, sm: 1.5 } },
                    '& .MuiChip-icon': { ml: { xs: 0.5, sm: 0.75 } },
                    '&:hover': {
                      backgroundColor: selectedFloorKey === null ? `${primary}40` : neutralLight,
                    },
                    transition: 'all 0.15s ease'
                  }}
                />
                {floors.map((floorKey, index) => {
                  const floor = useOptimized
                    ? floorSummaries.find(f => `floor_${f.id}` === floorKey)
                    : floorLayout[floorKey]?.floor;

                  if (!floor) return null;

                  const selected = selectedFloorKey === floorKey;

                  return (
                    <Chip
                      key={floorKey}
                      // Two floors can share a floorNumber — this vendor has "6"
                      // and "Ground Floor" both on 0, so the bar shows "F0" twice
                      // with nothing to tell them apart. The F{n} label itself is
                      // contract: floor-layout-deep-journey asserts "chip F0
                      // visible at baseline" (A2) and drives A-Chips-F0 by that
                      // label, so relabelling would break the documented UI.
                      // The floor name is exposed on hover instead — additive,
                      // and matching the title= pattern used by this page's icon
                      // buttons. A real de-duplication belongs with the floor
                      // numbering itself, not the chip that renders it.
                      title={floor.name}
                      label={floor.floorNumber !== undefined && floor.floorNumber !== null ? `F${floor.floorNumber}` : floor.name}
                      onClick={() => {
                        if (selectedFloorKey === floorKey) {
                          setSelectedFloorKey(null);
                        } else {
                          setSelectedFloorKey(floorKey);
                          setExpandedFloors(prev => new Set(prev).add(floorKey));
                          const floorId = floorKey.replace('floor_', '');
                          loadFloorSections(floorId, true);
                        }
                      }}
                      size="small"
                      sx={{
                        backgroundColor: selected ? `${primary}18` : neutralLight,
                        color: selected ? primary : neutral,
                        border: selected ? `1.5px solid ${primary}40` : `1px solid ${borderColor}`,
                        fontWeight: selected ? 700 : 600,
                        height: { xs: 28, sm: 32 },
                        fontSize: { xs: '0.75rem', sm: '0.813rem' },
                        '& .MuiChip-label': { px: { xs: 1, sm: 1.5 } },
                        '&:hover': {
                          backgroundColor: selected ? `${primary}25` : `${neutral}12`,
                        },
                        transition: 'all 0.15s ease'
                      }}
                    />
                  );
                })}
              </>
            )}

            {/* Held Orders button — only when there are held orders */}
            {heldOrders.length > 0 && (
              <Chip
                label={`Held (${heldOrders.length})`}
                onClick={() => setShowHeldOrdersPicker(true)}
                size="small"
                icon={<PauseIcon style={{ width: 14, height: 14 }} />}
                sx={{
                  backgroundColor: 'var(--status-warn-bg)',
                  color: 'var(--status-warn-text)',
                  border: '1.5px solid var(--status-warn-text)',
                  fontWeight: 700,
                  height: { xs: 28, sm: 32 },
                  fontSize: { xs: '0.75rem', sm: '0.813rem' },
                  '& .MuiChip-label': { px: { xs: 1, sm: 1.5 } },
                  '& .MuiChip-icon': { ml: { xs: 0.5, sm: 0.75 }, color: 'var(--status-warn-text)' },
                  '&:hover': { backgroundColor: 'var(--status-warn-bg)' },
                  transition: 'all 0.15s ease',
                  flexShrink: 0,
                  ml: 'auto',
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Summary Dashboard - Quick Stats */}
      {floors.length > 0 && (
        <Box sx={{
          mx: { xs: -2, sm: -3, lg: -4 },
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 0, sm: 1.5 },
          backgroundColor: surface,
          borderBottom: { xs: 'none', sm: `1px solid ${borderColor}` }
        }}>
          {/* Mobile: compact inline stats strip */}
          <Box sx={{
            display: { xs: 'flex', sm: 'none' },
            alignItems: 'center',
            gap: 0.5,
            py: 0.75,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
          }}>
            {(() => {
              void floorDataVersion;
              let totalTables = floorSummaries.reduce((sum, f) => sum + f.tableCount, 0);
              let available = 0, occupied = 0, reserved = 0;
              // Skip deleted sections and tables — same rule visibleSections uses
              // (`cached.sections.filter(s => !s.section.isDeleted)`), which this
              // block was missing. It matters because the two halves of this bar
              // come from different sources: `Tables` is floorSummaries, whose
              // query excludes deleted floors/sections/tables, while these counts
              // walk cachedFloorData. On load the cache is filled by the optimized
              // loader and the two agree. After any create, fetchFloorLayout()
              // refills it from the deprecated /api/tables/layout, which returns
              // deleted sections too — 100 tables where the vendor has 80. That is
              // why Free jumped 79 -> 99 against a total of 80: more free tables
              // than tables.
              cachedFloorData.forEach((fd) => {
                fd.sections.forEach((sd: any) => {
                  if (sd.section?.isDeleted) return;
                  sd.tables.forEach((td: any) => {
                    if (td.table?.isDeleted) return;
                    if (td.isOccupied || (td.activeOrderCount && td.activeOrderCount > 0)) occupied++;
                    else if (td.status === 'reserved') reserved++;
                    else available++;
                  });
                });
              });
              const totalOrders = floorSummaries.reduce((sum, f) => sum + f.activeOrderCount, 0);
              const stats = [
                { label: 'Tables', value: totalTables, color: theme.primary || '#2563EB', bg: `${theme.primary || '#2563EB'}15` },
                { label: 'Free', value: available, color: theme.components?.status?.available?.text || '#059669', bg: theme.components?.status?.available?.bg || '#F0FDF4' },
                { label: 'Reserved', value: reserved, color: theme.components?.status?.reserved?.text || '#92400E', bg: theme.components?.status?.reserved?.bg || '#FEF3C7' },
                { label: 'Busy', value: occupied, color: theme.components?.status?.occupied?.text || '#DC2626', bg: theme.components?.status?.occupied?.bg || '#FEF2F2' },
                { label: 'Orders', value: totalOrders, color: theme.warning || '#D97706', bg: `${theme.warning || '#D97706'}15` },
              ];
              return stats.map(s => (
                <Box key={s.label} sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  backgroundColor: s.bg,
                  borderRadius: '6px',
                  flexShrink: 0,
                }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: s.color, lineHeight: 1 }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, color: s.color, opacity: 0.8, lineHeight: 1 }}>
                    {s.label}
                  </Typography>
                </Box>
              ));
            })()}
          </Box>
          {/* Desktop: compact inline stats */}
          <Box sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}>
            {(() => {
              void floorDataVersion;
              const totalTables = floorSummaries.reduce((sum, f) => sum + f.tableCount, 0);
              let available = 0, occupied = 0, reserved = 0;
              // Skip deleted sections and tables — same rule visibleSections uses
              // (`cached.sections.filter(s => !s.section.isDeleted)`), which this
              // block was missing. It matters because the two halves of this bar
              // come from different sources: `Tables` is floorSummaries, whose
              // query excludes deleted floors/sections/tables, while these counts
              // walk cachedFloorData. On load the cache is filled by the optimized
              // loader and the two agree. After any create, fetchFloorLayout()
              // refills it from the deprecated /api/tables/layout, which returns
              // deleted sections too — 100 tables where the vendor has 80. That is
              // why Free jumped 79 -> 99 against a total of 80: more free tables
              // than tables.
              cachedFloorData.forEach((fd) => {
                fd.sections.forEach((sd: any) => {
                  if (sd.section?.isDeleted) return;
                  sd.tables.forEach((td: any) => {
                    if (td.table?.isDeleted) return;
                    if (td.isOccupied || (td.activeOrderCount && td.activeOrderCount > 0)) occupied++;
                    else if (td.status === 'reserved') reserved++;
                    else available++;
                  });
                });
              });
              const totalOrders = floorSummaries.reduce((sum, f) => sum + f.activeOrderCount, 0);
              const items = [
                { label: 'Tables', value: totalTables, color: 'var(--text-secondary)', bg: '#F1F5F9' },
                { label: 'Free', value: available, color: 'var(--status-ok)', bg: '#F0FDF4' },
                { label: 'Reserved', value: reserved, color: 'var(--status-warn-text)', bg: '#FEF3C7' },
                { label: 'Busy', value: occupied, color: 'var(--status-bad)', bg: '#FEF2F2' },
                { label: 'Orders', value: totalOrders, color: 'var(--status-warn-text)', bg: '#FFFBEB' },
              ];
              return items.map(s => (
                <Box key={s.label} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 10px',
                  backgroundColor: s.bg,
                  borderRadius: 6,
                }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: s.color, lineHeight: 1 }}>
                    {s.value}
                  </span>
                  <span style={{ fontSize: '0.7rem', fontWeight: 500, color: s.color, opacity: 0.8, lineHeight: 1 }}>
                    {s.label}
                  </span>
                </Box>
              ));
            })()}
          </Box>
          
          {/* Legend removed — color-coded stat chips above are self-explanatory */}
        </Box>
      )}

      {/* Main Content */}
      {viewMode === 'floorplan' ? (
        <Box sx={{ p: { xs: 1.5, sm: 2 }, height: 'calc(100vh - 200px)', minHeight: 400 }}>
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}>
            <FloorPlanEditor
              tables={(() => {
                // Build TableData[] for the active floor from cached floor data
                const activeFloorKey = selectedFloorKey || (floorSummaries[0] ? `floor_${floorSummaries[0].id}` : null);
                if (!activeFloorKey) return [];
                const floorId = activeFloorKey.replace('floor_', '');
                const cached = cachedFloorData.get(floorId);
                if (!cached) return [];
                return cached.sections.flatMap(s =>
                  s.tables.map(t => ({
                    id: Number(t.table.id),
                    tableNumber: t.table.displayNumber || t.table.name,
                    capacity: t.table.capacity || 4,
                    sectionId: Number(s.section.id),
                    shape: t.table.shape || undefined,
                  }))
                );
              })()}
              sections={(() => {
                const activeFloorKey = selectedFloorKey || (floorSummaries[0] ? `floor_${floorSummaries[0].id}` : null);
                if (!activeFloorKey) return [];
                const floorId = activeFloorKey.replace('floor_', '');
                const cached = cachedFloorData.get(floorId);
                if (!cached) return [];
                return cached.sections.map(s => ({
                  id: Number(s.section.id),
                  name: s.section.name,
                }));
              })()}
              assets={floorAssets}
              floorId={(() => {
                const activeFloorKey = selectedFloorKey || (floorSummaries[0] ? `floor_${floorSummaries[0].id}` : null);
                return activeFloorKey ? parseInt(activeFloorKey.replace('floor_', '')) : 0;
              })()}
              tableGroupMap={(() => {
                // Convert string-keyed tableGroupMap to Map<number, GroupInfo>
                const map = new Map<number, { groupId: number }>();
                tableGroupMap.forEach((val, key) => {
                  map.set(Number(key), { groupId: val.groupId });
                });
                return map;
              })()}
              occupancyMap={(() => {
                // Build occupancy map from cached floor data
                const map = new Map<number, { isOccupied: boolean; orderCount: number }>();
                const activeFloorKey = selectedFloorKey || (floorSummaries[0] ? `floor_${floorSummaries[0].id}` : null);
                if (!activeFloorKey) return map;
                const floorId = activeFloorKey.replace('floor_', '');
                const cached = cachedFloorData.get(floorId);
                if (!cached) return map;
                cached.sections.forEach(s => {
                  s.tables.forEach(t => {
                    map.set(Number(t.table.id), {
                      isOccupied: t.isOccupied,
                      orderCount: t.activeOrders?.length || 0,
                    });
                  });
                });
                return map;
              })()}
              isManagementMode={isManagementMode}
              mergeMode={mergeMode ? {
                active: mergeMode.active,
                sourceTableId: mergeMode.sourceTableId,
                selectedTableIds: mergeMode.selectedTableIds,
              } : null}
              transferMode={transferMode ? {
                active: transferMode.active,
                sourceTableId: transferMode.sourceTableId,
              } : null}
              reservationMode={reservationMode ? {
                active: reservationMode.active,
                selectedTableIds: reservationMode.selectedTableIds,
                availableTableIds: reservationMode.availableTableIds,
                needsMerge: reservationMode.needsMerge,
              } : null}
              onQuickReserve={(table) => {
                const tableId = Number(table.id);
                enterReservationMode(isNaN(tableId) ? undefined : tableId);
              }}
              onTableClick={(table) => {
                // Find full table data from cached floor data to pass to handleTableClick
                const activeFloorKey = selectedFloorKey || (floorSummaries[0] ? `floor_${floorSummaries[0].id}` : null);
                if (!activeFloorKey) return;
                const floorId = activeFloorKey.replace('floor_', '');
                const cached = cachedFloorData.get(floorId);
                const floorSummary = floorSummaries.find(f => `floor_${f.id}` === activeFloorKey);
                if (!cached) return;
                for (const s of cached.sections) {
                  const tableEntry = s.tables.find(t => Number(t.table.id) === table.id);
                  if (tableEntry) {
                    handleTableClick({
                      table: tableEntry.table,
                      activeOrders: tableEntry.activeOrders,
                      isOccupied: tableEntry.isOccupied,
                      status: tableEntry.status,
                      floorName: floorSummary?.name || '',
                      sectionName: s.section.name,
                    });
                    break;
                  }
                }
              }}
              onSave={handleSavePositions}
            />
          </Suspense>
        </Box>
      ) : (
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        {floors.length > 0 ? (
          visibleFloors.map(floorKey => renderFloor(floorKey))
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: '300px', sm: '400px' },
              py: { xs: 4, sm: 8 },
              px: { xs: 3, sm: 4 },
              background: surface,
              borderRadius: { xs: '12px', sm: '16px' },
              border: `1px solid ${borderColor}`,
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}
          >
            <BuildingOfficeIcon style={{ width: 60, height: 60, color: '#cbd5e1' }} /> {/* basil-todo */}
            <Typography
              variant="h6"
              sx={{
                color: 'var(--text-tertiary)',
                mt: 2,
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                textAlign: 'center'
              }}
            >
              No floors created yet
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'var(--text-tertiary)',
                mt: 1,
                fontSize: { xs: '0.813rem', sm: '0.875rem' },
                textAlign: 'center'
              }}
            >
              {isSteward ? 'No floors available for viewing' : 'Start by creating your first floor'}
            </Typography>
            {!isSteward && (
              <Button
                variant="contained"
                startIcon={<PlusCircleIcon style={{ width: 18, height: 18 }} />}
                onClick={() => setOpenFloorDialog(true)}
                sx={{
                  mt: 3,
                  backgroundColor: '#64748b', /* basil-todo */
                  color: 'white',
                  fontWeight: 600,
                  fontSize: { xs: '0.813rem', sm: '0.875rem' },
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    backgroundColor: '#475569', /* basil-todo */
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                Create Your First Floor
              </Button>
            )}
          </Box>
        )}
      </Box>
      )}

      {/* Table Details Panel */}
      {selectedTable && (
        <Dialog
          open={!!selectedTable}
          onClose={closeTableDetails}
          maxWidth="xs"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              background: surface,
              border: isMobile ? 'none' : `1px solid ${borderColor}`,
              borderRadius: isMobile ? 0 : '12px',
              overflow: 'hidden',
              m: isMobile ? 0 : 2,
            }
          }}
        >
          <DialogTitle
            sx={{
              background: 'var(--bg-surface)',
              borderBottom: '1px solid var(--border-subtle)',
              padding: { xs: '14px 16px', sm: '16px 20px' },
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    fontSize: { xs: '1.05rem', sm: '1.15rem' },
                    lineHeight: 1.3
                  }}
                >
                  {showQrInDetails ? 'QR Code' : `Table ${selectedTable.table.displayNumber || selectedTable.table.name}`}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'var(--text-tertiary)',
                    fontWeight: 400,
                    fontSize: { xs: '0.8rem', sm: '0.85rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {[selectedTable.floorName, selectedTable.sectionName].filter(Boolean).join(' / ')}
                </Typography>
              </Box>
              <CloseButton onClick={closeTableDetails} size="md" />
            </Box>
          </DialogTitle>
          <DialogContent sx={{ padding: { xs: '12px 16px', sm: '16px 20px' }, background: 'var(--bg-surface)' }}>
            <AnimatePresence mode="wait">
              {!showQrInDetails ? (
                <motion.div
                  key="details"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <Box>
                    {/* Compact Table Info Toggle Bar */}
                    <Box
                      onClick={() => setShowTableInfo(!showTableInfo)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: { xs: 0.75, sm: 1 },
                        px: { xs: 1.25, sm: 1.5 },
                        mb: 1.5,
                        borderRadius: '10px',
                        background: selectedTable.isOccupied ? 'rgba(16, 185, 129, 0.06)' : '#F8FAFC',
                        border: selectedTable.isOccupied ? '1px solid rgba(16, 185, 129, 0.18)' : '1px solid #E2E8F0',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'background 0.15s',
                        '&:hover': { background: selectedTable.isOccupied ? 'rgba(16, 185, 129, 0.10)' : '#F1F5F9' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, minWidth: 0 }}>
                        {/* Status dot */}
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: selectedTable.isOccupied ? '#10B981' : '#94A3B8',
                          flexShrink: 0,
                        }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: selectedTable.isOccupied ? '#065F46' : '#475569',
                            fontSize: { xs: '0.8rem', sm: '0.85rem' }
                          }}
                        >
                          {getTableStatusText(selectedTable)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'var(--text-tertiary)',
                            fontSize: { xs: '0.75rem', sm: '0.8rem' }
                          }}
                        >
                          {'\u00B7'} {selectedTable.activeOrders?.length || selectedTable.activeOrderCount || 0} orders {'\u00B7'} {selectedTable.table.capacity || 4} seats
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                          flexShrink: 0,
                          color: 'var(--text-tertiary)',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                        }}
                      >
                        <ChevronDownIcon
                          style={{
                            width: 16,
                            height: 16,
                            color: 'var(--text-tertiary)',
                            transition: 'transform 0.2s',
                            transform: showTableInfo ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Collapsible Stats + QR Section */}
                    <Collapse in={showTableInfo}>
                      <Box sx={{ mb: 1.5 }}>
                        {/* Stats Row */}
                        <Box
                          sx={{
                            display: 'flex',
                            gap: { xs: 1, sm: 1.5 },
                            mb: 1.5
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              p: { xs: 1, sm: 1.5 },
                              borderRadius: { xs: '6px', sm: '8px' },
                              background: selectedTable.status === 'reserved' ? '#FFFBEB' : '#f0f9ff',
                              border: selectedTable.status === 'reserved' ? '1px solid #FDE68A' : '1px solid #e0f2fe',
                              textAlign: 'center'
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: selectedTable.status === 'reserved' ? '#92400E' : '#0369a1',
                                fontWeight: 600,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                display: 'block'
                              }}
                            >
                              STATUS
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: selectedTable.status === 'reserved' ? '#B45309' : '#0c4a6e',
                                fontSize: { xs: '0.813rem', sm: '0.9rem' }
                              }}
                            >
                              {getTableStatusText(selectedTable)}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              flex: 1,
                              p: { xs: 1, sm: 1.5 },
                              borderRadius: { xs: '6px', sm: '8px' },
                              background: 'var(--status-warn-bg)',
                              border: '1px solid var(--status-warn-border)',
                              textAlign: 'center'
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'var(--status-warn-text)',
                                fontWeight: 600,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                display: 'block'
                              }}
                            >
                              ORDERS
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: 'var(--status-warn-text)',
                                fontSize: { xs: '0.813rem', sm: '0.9rem' }
                              }}
                            >
                              {selectedTable.activeOrders?.length || selectedTable.activeOrderCount || 0}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              flex: 1,
                              p: { xs: 1, sm: 1.5 },
                              borderRadius: { xs: '6px', sm: '8px' },
                              background: 'var(--accent-alt-bg)',
                              border: '1px solid #e9d5ff', /* basil-todo */
                              textAlign: 'center'
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#6b21a8', /* basil-todo */
                                fontWeight: 600,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                display: 'block'
                              }}
                            >
                              SEATS
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: '#581c87', /* basil-todo */
                                fontSize: { xs: '0.813rem', sm: '0.9rem' }
                              }}
                            >
                              {selectedTable.table.capacity || 4}
                            </Typography>
                          </Box>
                        </Box>

                        {/* QR Code Section */}
                        <Box
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            borderRadius: { xs: '6px', sm: '8px' },
                            background: 'var(--status-ok-bg)',
                            border: '1px solid #dcfce7', /* basil-todo */
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1
                          }}
                        >
                          <Box flex={1}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'var(--status-ok-text)',
                                fontWeight: 600,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                display: 'block'
                              }}
                            >
                              QR CODE
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: selectedTable.table.qrCode ? '#166534' : '#dc2626',
                                fontWeight: 500,
                                fontSize: { xs: '0.813rem', sm: '0.875rem' }
                              }}
                            >
                              {selectedTable.table.qrCode ? `${selectedTable.table.qrCode.code}` : 'Not Generated'}
                            </Typography>
                          </Box>
                          {selectedTable.table.qrCode ? (
                            <Button
                              onClick={(e) => { e.stopPropagation(); if (selectedTable.table.qrCode) { handleViewQrCode(selectedTable.table.qrCode); } }}
                              size="small"
                              variant="contained"
                              sx={{
                                backgroundColor: '#64748b !important', /* basil-todo */
                                color: 'white !important',
                                minWidth: 'auto',
                                px: { xs: 0.75, sm: 1 },
                                py: { xs: 0.25, sm: 0.5 },
                                '&:hover': {
                                  backgroundColor: '#475569 !important' /* basil-todo */
                                },
                                '&.MuiButton-root': {
                                  backgroundColor: '#64748b !important' /* basil-todo */
                                }
                              }}
                            >
                              <QrCodeIcon style={{ width: 18, height: 18, color: 'white' }} />
                            </Button>
                          ) : (
                            !isSteward && (
                              <Tooltip title="Generate a QR code for customers to scan and order from this table">
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="warning"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeTableDetails();
                                    handleOpenQrDialog(selectedTable.table);
                                  }}
                                  sx={{
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                    py: { xs: 0.25, sm: 0.5 },
                                    px: { xs: 1, sm: 1.5 }
                                  }}
                                >
                                  Generate
                                </Button>
                              </Tooltip>
                            )
                          )}
                        </Box>
                      </Box>
                    </Collapse>

                    {/* Reservations Section */}
                    {selectedTable.reservations && selectedTable.reservations.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          mb: 1
                        }}>
                          <Typography sx={{ fontSize: '1rem' }}>📅</Typography>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--status-warn-text)' }}>
                            Reservations ({selectedTable.reservations.length})
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {selectedTable.reservations
                            .sort((a, b) => a.startTime.localeCompare(b.startTime))
                            .map((reservation) => (
                              <Box
                                key={reservation.id}
                                sx={{
                                  p: 1.5,
                                  borderRadius: '8px',
                                  border: `1px solid ${theme.warning || '#D97706'}40`,
                                  borderLeft: `4px solid ${theme.warning || '#F59E0B'}`,
                                  backgroundColor: `${theme.warning || '#D97706'}15`
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box sx={{
                                      px: 0.75,
                                      py: 0.25,
                                      borderRadius: '6px',
                                      backgroundColor: reservation.status === 'CONFIRMED'
                                        ? (theme.components?.status?.reserved?.bg || '#FEF3C7')
                                        : reservation.status === 'SEATED'
                                          ? (theme.components?.status?.available?.bg || '#D1FAE5')
                                          : '#FED7AA',
                                      border: `1px solid ${reservation.status === 'CONFIRMED'
                                        ? (theme.components?.status?.reserved?.border || theme.warning || '#F59E0B')
                                        : reservation.status === 'SEATED'
                                          ? (theme.components?.status?.available?.border || '#10B981')
                                          : '#FB923C'}`
                                    }}>
                                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: reservation.status === 'CONFIRMED' ? (theme.components?.status?.reserved?.text || '#92400E') : reservation.status === 'SEATED' ? (theme.components?.status?.available?.text || '#065F46') : '#9A3412', textTransform: 'uppercase' }}>
                                        {reservation.status}
                                      </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontWeight: 500 }}>
                                      {new Date(`2000-01-01T${reservation.startTime}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} – {new Date(`2000-01-01T${reservation.endTime}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                    </Typography>
                                  </Box>
                                  {/* Touch-friendly action buttons based on status */}
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {reservation.status === 'PENDING' && (
                                      <Button size="small" variant="contained"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            await api.patch(`/api/reservations/${reservation.id}/status`, { status: 'CONFIRMED' });
                                            toast.success(`${reservation.customerName} confirmed`);
                                            fetchFloorLayout(true);
                                          } catch { toast.error('Failed to confirm'); }
                                        }}
                                        sx={{ fontSize: '0.6rem', py: 0.25, px: 0.75, minWidth: 'auto', minHeight: 24, backgroundColor: 'var(--action) !important', color: 'white !important', textTransform: 'none', '&:hover': { backgroundColor: 'var(--action) !important' } }}
                                      >Confirm</Button>
                                    )}
                                    {(reservation.status === 'PENDING' || reservation.status === 'CONFIRMED') && (
                                      <Button size="small" variant="contained"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            await api.patch(`/api/reservations/${reservation.id}/status`, { status: 'SEATED' });
                                            toast.success(`${reservation.customerName} seated`);
                                            fetchFloorLayout(true);
                                          } catch { toast.error('Failed to seat'); }
                                        }}
                                        sx={{ fontSize: '0.6rem', py: 0.25, px: 0.75, minWidth: 'auto', minHeight: 24, backgroundColor: 'var(--action) !important', color: 'white !important', textTransform: 'none', '&:hover': { backgroundColor: 'var(--action) !important' } }}
                                      >Seat</Button>
                                    )}
                                    {reservation.status === 'SEATED' && (
                                      <Button size="small" variant="contained"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          const ok = await confirm({
                                            title: 'Complete Reservation',
                                            message: `Mark ${reservation.customerName}'s reservation as completed?`,
                                            confirmText: 'Complete',
                                          });
                                          if (!ok) return;
                                          try {
                                            await api.patch(`/api/reservations/${reservation.id}/status`, { status: 'COMPLETED' });
                                            toast.success(`${reservation.customerName} completed`);
                                            fetchFloorLayout(true);
                                          } catch { toast.error('Failed to complete'); }
                                        }}
                                        sx={{ fontSize: '0.6rem', py: 0.25, px: 0.75, minWidth: 'auto', minHeight: 24, backgroundColor: '#6B7280 !important', color: 'white !important', textTransform: 'none', '&:hover': { backgroundColor: '#4B5563 !important' } }} /* basil-todo */
                                      >Complete</Button>
                                    )}
                                    {(reservation.status === 'PENDING' || reservation.status === 'CONFIRMED') && (
                                      <Button size="small" variant="outlined"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          const ok = await confirm({
                                            title: 'Cancel Reservation',
                                            message: `Cancel ${reservation.customerName}'s reservation for ${reservation.partySize} guests? This cannot be undone.`,
                                            confirmText: 'Cancel Reservation',
                                            confirmColor: 'error',
                                          });
                                          if (!ok) return;
                                          try {
                                            await api.patch(`/api/reservations/${reservation.id}/status`, { status: 'CANCELLED' });
                                            toast.success(`Reservation cancelled`);
                                            fetchFloorLayout(true);
                                          } catch { toast.error('Failed to cancel'); }
                                        }}
                                        sx={{ fontSize: '0.6rem', py: 0.25, px: 0.75, minWidth: 'auto', minHeight: 24, borderColor: 'var(--status-bad)', color: 'var(--status-bad)', textTransform: 'none', '&:hover': { backgroundColor: 'var(--status-bad-bg)', borderColor: 'var(--status-bad)' } }}
                                      >Cancel</Button>
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                                    {reservation.customerName}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                                    · 👥 {reservation.partySize} guests
                                  </Typography>
                                </Box>
                                {reservation.specialRequests && (
                                  <Typography sx={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', fontStyle: 'italic', mt: 0.5 }}>
                                    "{reservation.specialRequests}"
                                  </Typography>
                                )}
                              </Box>
                            ))}
                        </Box>
                      </Box>
                    )}

                    {/* Active Orders Section */}
                    <Box>
                      <Box
                        sx={{
                          borderRadius: { xs: '6px', sm: '8px' },
                          border: '1px solid var(--border-subtle)',
                          overflow: 'hidden',
                          minHeight: { xs: '100px', sm: '120px' },
                          position: 'relative'
                        }}
                      >
                        <TableOrderHistory
                          tableId={parseInt(selectedTable.table.id)}
                          theme={{
                            palette: {
                              mode: 'light',
                              primary: { main: primary },
                              text: { secondary: textSecondary },
                              background: { paper: surface }
                            }
                          }}
                          activeOnly={true}
                          noPadding={true}
                          isSteward={isSteward || isVendor}
                          onModifyOrder={(orderId) => {
                            setSelectedOrderForModification(orderId);
                            setShowOrderModification(true);
                            closeTableDetails();
                          }}
                          onCancelOrder={handleCancelOrder}
                          onServeOrder={handleServeOrder}
                          cancellingOrderId={cancellingOrderId}
                          servingOrderId={servingOrderId}
                          refreshTrigger={orderHistoryRefreshTrigger}
                        />
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              ) : (
                <motion.div
                  key="qrcode"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                >
                  <Box sx={{ textAlign: 'center', py: { xs: 2, sm: 0 } }}>
                    {isLoadingQrImage ? (
                      <Box sx={{ py: 4 }}>
                        <Typography sx={{ color: 'var(--text-tertiary)', fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
                          Loading QR code...
                        </Typography>
                      </Box>
                    ) : qrImageUrl && (
                      <>
                        <Box
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            backgroundColor: 'white',
                            borderRadius: { xs: '6px', sm: '8px' },
                            display: 'inline-block',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            border: '1px solid var(--border-subtle)',
                            mb: 2
                          }}
                        >
                          <img
                            src={qrImageUrl}
                            alt="QR Code"
                            style={{
                              maxWidth: isMobile ? '160px' : '200px',
                              height: 'auto',
                              display: 'block'
                            }}
                          />
                        </Box>
                        <Box display="flex" gap={1.5} justifyContent="center" flexWrap="wrap">
                          <Button
                            variant="contained"
                            onClick={() => setShowFancyQrModal(true)}
                            size="small"
                            sx={{
                              background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent || theme.primary} 100%) !important`,
                              color: 'white !important',
                              px: { xs: 1.5, sm: 2 },
                              py: { xs: 0.75, sm: 1 },
                              fontSize: { xs: '0.75rem', sm: '0.8rem' },
                              fontWeight: 600,
                              textTransform: 'none',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1) !important',
                              '&:hover': {
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15) !important',
                                transform: 'translateY(-1px)'
                              }
                            }}
                          >
                            ✨ Fancy QR
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleDownloadQr}
                            size="small"
                            sx={{
                              backgroundColor: `${theme.primary}22 !important`,
                              color: `${theme.primary} !important`,
                              border: `1px solid ${theme.primary} !important`,
                              px: { xs: 1.5, sm: 2 },
                              py: { xs: 0.75, sm: 1 },
                              fontSize: { xs: '0.75rem', sm: '0.8rem' },
                              fontWeight: 600,
                              textTransform: 'none',
                              boxShadow: 'none !important',
                              '&:hover': {
                                backgroundColor: `${theme.primary}44 !important`,
                                boxShadow: 'none !important'
                              }
                            }}
                          >
                            Download
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={handlePrintQr}
                            size="small"
                            sx={{
                              px: { xs: 1.5, sm: 2 },
                              py: { xs: 0.75, sm: 1 },
                              fontSize: { xs: '0.75rem', sm: '0.8rem' },
                              borderColor: `${theme.primary} !important`,
                              color: `${theme.primary} !important`,
                              backgroundColor: `${theme.primary}22 !important`,
                              fontWeight: 600,
                              textTransform: 'none',
                              '&:hover': {
                                borderColor: `${theme.primary} !important`,
                                backgroundColor: `${theme.primary}44 !important`
                              }
                            }}
                          >
                            Print
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </DialogContent>
          {/* Merge group info — inside content area, not in action bar */}
          {selectedTable && tableGroupMap.has(String(selectedTable.table.id)) && !showQrInDetails && (
            <Box style={{
              margin: '0 16px 8px',
              padding: '6px 10px',
              background: 'var(--status-warn-bg)',
              borderRadius: 8,
              border: '1px solid var(--status-warn)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <LinkIcon style={{ width: 14, height: 14, color: 'var(--status-warn-text)', flexShrink: 0 }} />
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--status-warn-text)' }}>
                Merged with: {tableGroupMap.get(String(selectedTable.table.id))?.tableNames.filter(
                  n => n !== (selectedTable.table.displayNumber || selectedTable.table.name)
                ).join(', ')}
              </span>
            </Box>
          )}
          <DialogActions
            style={{
              padding: '8px 12px',
              background: 'var(--bg-surface)',
              borderTop: '1px solid var(--border-subtle)',
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
            }}
          >
            {!showQrInDetails && selectedTable && (
              <Box style={{ display: 'flex', gap: 6, width: '100%', alignItems: 'center' }}>
                {/* PRIMARY: Order */}
                <Button
                  variant="contained"
                  startIcon={<ShoppingCartIcon style={{ width: 14, height: 14 }} />}
                  onClick={async () => {
                    if (!(await confirmReservationOrder(selectedTable))) return;
                    setOrderTableId(selectedTable.table.id);
                    setOrderTableName(selectedTable.table.name);
                    setTableDataForOrder(selectedTable);
                    setSelectedTable(null);
                    setShowOrderForm(true);
                  }}
                  size="small"
                  style={{
                    backgroundColor: '#475569', /* basil-todo */
                    color: 'var(--text-inverse)',
                    padding: '6px 10px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 8,
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Order{(() => {
                    const orderCount = selectedTable.activeOrders?.length || selectedTable.activeOrderCount || 0;
                    return orderCount > 0 ? ` ${orderCount}` : '';
                  })()}
                </Button>

                {/* PRIMARY: View Bill */}
                {hasActiveOrders(selectedTable) && (
                  <Button
                    variant="contained"
                    startIcon={isLoadingBill ? <CircularProgress size={12} style={{ color: 'white' }} /> : <DocumentTextIcon style={{ width: 14, height: 14, color: 'white' }} />}
                    onClick={async () => {
                      setIsLoadingBill(true);
                      try {
                        // The bill is about to be shown to the guest — make sure
                        // any live offer is already on it.
                        await ensureOffersOnBill(
                          selectedTable.activeOrders?.[0]?.orderLineId);

                        let primaryOrderId = getPrimaryOrderId(selectedTable);
                        const isInMergeGroup = tableGroupMap.has(String(selectedTable.table.id));

                        if (!primaryOrderId && (selectedTable.activeOrderCount && selectedTable.activeOrderCount > 0 || selectedTable.isOccupied)) {
                          try {
                            // If merged, fetch orders from ALL tables in the group
                            const fetchedOrders = isInMergeGroup
                              ? await fetchMergedGroupOrders(String(selectedTable.table.id))
                              : await api.get(`/api/tables/${selectedTable.table.id}/orders`, {
                                  params: { status: ['PENDING', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED'] }
                                }).then(r => r.data || []);
                            if (fetchedOrders.length > 0) primaryOrderId = fetchedOrders[0].id;
                          } catch (fetchError) {
                            toast.error('Failed to fetch table orders');
                            setIsLoadingBill(false);
                            return;
                          }
                        }
                        if (primaryOrderId) {
                          setPendingReturnTable(selectedTable);
                          closeTableDetails();
                          setSelectedOrderForInvoice(primaryOrderId);
                          setShowInvoiceModal(true);
                        } else {
                          toast.error('No orders available to view bill');
                        }
                      } finally {
                        setIsLoadingBill(false);
                      }
                    }}
                    disabled={isLoadingBill}
                    size="small"
                    style={{
                      backgroundColor: 'var(--action)',
                      color: 'var(--text-inverse)',
                      padding: '6px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 8,
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isLoadingBill ? 'Loading...' : 'Bill'}
                  </Button>
                )}

                {/* PRIMARY: Settle */}
                {hasActiveOrders(selectedTable) && (
                  <Button
                    variant="contained"
                    startIcon={isSettlingBill ? <CircularProgress size={12} style={{ color: 'white' }} /> : <BanknotesIcon style={{ width: 14, height: 14 }} />}
                    onClick={async () => {
                      // Last chance before money moves. settle-bill computes the
                      // total server-side, so applying here is safe — but it must
                      // happen BEFORE that call, not inside it.
                      await ensureOffersOnBill(
                        selectedTable.activeOrders?.[0]?.orderLineId);
                      setPendingSettleTable(selectedTable);
                      setShowSettleConfirmDialog(true);
                    }}
                    disabled={isSettlingBill}
                    size="small"
                    style={{
                      backgroundColor: 'var(--action)',
                      color: 'var(--text-inverse)',
                      padding: '6px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 8,
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isSettlingBill ? '...' : 'Settle'}
                  </Button>
                )}

                {/* SECONDARY: More menu */}
                <Button
                  onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                  variant="outlined"
                  size="small"
                  endIcon={<EllipsisVerticalIcon style={{ width: 14, height: 14 }} />}
                  style={{
                    backgroundColor: 'var(--bg-surface)',
                    border: '1.5px solid var(--border-default)',
                    borderRadius: 8,
                    color: 'var(--text-secondary)',
                    padding: '6px 10px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    marginLeft: 'auto',
                  }}
                >
                  More
                </Button>
                <Menu
                  anchorEl={moreMenuAnchor}
                  open={Boolean(moreMenuAnchor)}
                  onClose={() => setMoreMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  slotProps={{
                    paper: {
                      sx: {
                        borderRadius: '12px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                        minWidth: 200,
                        py: 0.5,
                      }
                    }
                  }}
                >
                  {/* Discount — vendor only, needs active orders */}
                  {!isSteward && hasActiveOrders(selectedTable) && (
                    <MenuItem onClick={() => { setMoreMenuAnchor(null); handleOpenDiscountDialog(selectedTable); }}
                      sx={{ py: 1.25, px: 2, gap: 1.5, borderRadius: '8px', mx: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28, color: 'var(--status-warn)' }}>
                        <ReceiptPercentIcon style={{ width: 18, height: 18 }} />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Discount
                      </ListItemText>
                    </MenuItem>
                  )}

                  {/* Split Bill — needs active orders */}
                  {hasActiveOrders(selectedTable) && (
                    <MenuItem onClick={() => { setMoreMenuAnchor(null); handleOpenSplitBill(selectedTable); }}
                      sx={{ py: 1.25, px: 2, gap: 1.5, borderRadius: '8px', mx: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28, color: 'var(--accent-alt)' }}>
                        <BanknotesIcon style={{ width: 18, height: 18 }} />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Split Bill
                      </ListItemText>
                    </MenuItem>
                  )}

                  {/* Transfer — needs active orders */}
                  {hasActiveOrders(selectedTable) && (
                    <MenuItem onClick={() => {
                      setMoreMenuAnchor(null);
                      const orderCount = selectedTable.activeOrders?.length || selectedTable.activeOrderCount || 0;
                      setTransferSourceTable({
                        id: selectedTable.table.id,
                        name: selectedTable.table.name,
                        displayNumber: selectedTable.table.displayNumber,
                        activeOrderCount: orderCount
                      });
                      setSelectedTable(null);
                      setShowTransferDialog(true);
                    }}
                      sx={{ py: 1.25, px: 2, gap: 1.5, borderRadius: '8px', mx: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28, color: 'var(--accent-alt)' }}>
                        <ArrowsRightLeftIcon style={{ width: 18, height: 18 }} />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Transfer
                      </ListItemText>
                    </MenuItem>
                  )}

                  {hasActiveOrders(selectedTable) && <Divider sx={{ my: 0.5 }} />}

                  {/* Reserve */}
                  <MenuItem onClick={() => {
                    setMoreMenuAnchor(null);
                    const tableId = parseInt(selectedTable.table.id, 10);
                    setSelectedTable(null);
                    enterReservationMode(isNaN(tableId) ? undefined : tableId);
                  }}
                    sx={{ py: 1.25, px: 2, gap: 1.5, borderRadius: '8px', mx: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28, color: 'var(--status-warn)' }}>
                      <CalendarDaysIcon style={{ width: 18, height: 18 }} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      Reserve
                    </ListItemText>
                  </MenuItem>

                  {/* Merge / Unmerge — vendor only */}
                  {!isSteward && (() => {
                    const isInGroup = tableGroupMap.has(String(selectedTable.table.id));
                    return isInGroup ? (
                      <MenuItem onClick={() => { setMoreMenuAnchor(null); handleUnmergeTable(selectedTable.table.id); }}
                        sx={{ py: 1.25, px: 2, gap: 1.5, borderRadius: '8px', mx: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 28, color: 'var(--status-warn)' }}>
                          <ScissorsIcon style={{ width: 18, height: 18 }} />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
                          Unmerge Tables
                        </ListItemText>
                      </MenuItem>
                    ) : (
                      <MenuItem onClick={() => {
                        setMoreMenuAnchor(null);
                        // Enter merge mode — close table details, show banner, let user tap tables
                        const tableName = selectedTable.table.displayNumber || selectedTable.table.name;
                        setSelectedTable(null);
                        setMergeMode({
                          active: true,
                          sourceTableId: String(selectedTable.table.id),
                          sourceTableName: tableName,
                          selectedTableIds: new Set(),
                        });
                      }}
                        sx={{ py: 1.25, px: 2, gap: 1.5, borderRadius: '8px', mx: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 28, color: 'var(--status-warn)' }}>
                          <LinkIcon style={{ width: 18, height: 18 }} />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
                          Merge Tables
                        </ListItemText>
                      </MenuItem>
                    );
                  })()}

                  {/* Block / Unblock — vendor only */}
                  {!isSteward && (
                    <MenuItem
                      onClick={async () => {
                        setMoreMenuAnchor(null);
                        const isBlocked = selectedTable.table.isActive === false;
                        const tableName = selectedTable.table.displayNumber || selectedTable.table.name;
                        const ok = await confirm({
                          title: isBlocked ? 'Unblock Table' : 'Block Table',
                          message: isBlocked
                            ? `Unblock ${tableName}? It will become available for seating.`
                            : `Block ${tableName}? It will be marked as unavailable.`,
                          confirmText: isBlocked ? 'Unblock' : 'Block',
                          confirmColor: isBlocked ? 'success' : 'warning',
                        });
                        if (!ok) return;
                        try {
                          await api.patch(`/api/tables/${selectedTable.table.id}/block`, {
                            isActive: isBlocked,
                          });
                          toast.success(`Table ${isBlocked ? 'unblocked' : 'blocked'}`);
                          setSelectedTable(null);
                          cachedFloorData.clear();
                          fetchFloorSummaries();
                        } catch {
                          toast.error(`Failed to ${isBlocked ? 'unblock' : 'block'} table`);
                        }
                      }}
                      sx={{ py: 1.25, px: 2, gap: 1.5, borderRadius: '8px', mx: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 28, color: selectedTable.table.isActive === false ? '#10B981' : '#EF4444' }}>
                        {selectedTable.table.isActive === false ? (
                          <LockOpenIcon style={{ width: 18, height: 18 }} />
                        ) : (
                          <NoSymbolIcon style={{ width: 18, height: 18 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {selectedTable.table.isActive === false ? 'Unblock Table' : 'Block Table'}
                      </ListItemText>
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            )}
            {/* Group indicator badge moved above DialogActions */}
            {showQrInDetails && (
              <Button
                onClick={() => setShowQrInDetails(false)}
                size="small"
                variant="text"
                sx={{
                  color: `${theme.primary} !important`,
                  backgroundColor: 'transparent !important',
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.75, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: `${theme.primary}15 !important`
                  }
                }}
              >
                Back
              </Button>
            )}
            {false && (
              <Button onClick={closeTableDetails}>Close</Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Dialogs */}
      <Dialog
        open={openFloorDialog}
        onClose={() => {
          setOpenFloorDialog(false);
          setFloorName('');
          setFloorNumber(0);
        }}
        PaperProps={{
          sx: {
            background: 'white',
            border: '1px solid var(--border-subtle)',
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderBottom: '1px solid var(--border-subtle)',
            padding: '16px 20px',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: 'var(--text-primary)'
          }}
        >
          Create New Floor
        </DialogTitle>
        <DialogContent sx={{ padding: '20px', background: 'white' }}>
          <TextField
            fullWidth
            label="Floor Name"
            value={floorName}
            onChange={(e) => setFloorName(e.target.value)}
            margin="normal"
            placeholder="e.g., Ground Floor, First Floor"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'var(--border-default)'
                }
              }
            }}
          />
          <TextField
            fullWidth
            label="Floor Number"
            type="number"
            value={floorNumber === '' ? '' : floorNumber}
            onChange={(e) => {
              const value = e.target.value;
              setFloorNumber(value === '' ? '' : value);
            }}
            onBlur={(e) => {
              const value = e.target.value;
              if (value !== '') {
                const num = parseInt(value);
                if (!isNaN(num)) {
                  if (num < 0) {
                    setFloorNumber(0);
                  } else if (num > 100) {
                    setFloorNumber(100);
                  } else {
                    setFloorNumber(num);
                  }
                }
              }
            }}
            margin="normal"
            placeholder="0 for ground floor, 1 for first floor, etc."
            helperText={(() => {
              if (floorNumber === '' || floorNumber === null) {
                return "Required: Enter a number between 0 and 100";
              }
              const floorNum = typeof floorNumber === 'string' ? parseInt(floorNumber) : floorNumber;
              if (isNaN(floorNum)) {
                return "⚠️ Please enter a valid number";
              }
              if (floorNum < 0 || floorNum > 100) {
                return "⚠️ Floor number must be between 0 and 100";
              }
              const existingFloorNumbers = Object.values(floorLayout).map(({ floor }) => floor.floorNumber);
              const isDuplicate = existingFloorNumbers.includes(floorNum);
              if (isDuplicate) {
                const existingFloor = Object.values(floorLayout).find(
                  ({ floor }) => floor.floorNumber === floorNum
                );
                return `⚠️ Floor number ${floorNum} already used by "${existingFloor?.floor.name}"`;
              }
              return "Floor number helps maintain order and uniqueness";
            })()}
            error={(() => {
              if (floorNumber === '' || floorNumber === null) return false;
              const floorNum = typeof floorNumber === 'string' ? parseInt(floorNumber) : floorNumber;
              if (isNaN(floorNum) || floorNum < 0 || floorNum > 100) return true;
              return Object.values(floorLayout).some(({ floor }) => floor.floorNumber === floorNum);
            })()}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'var(--border-default)'
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '12px 20px', background: 'var(--bg-sunken)', borderTop: '1px solid var(--border-subtle)', gap: '8px', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: { xs: 'stretch', sm: 'flex-end' }, '& > :not(:first-of-type)': { ml: { xs: 0, sm: 1 } } }}>
          <Button
            onClick={() => {
              setOpenFloorDialog(false);
              setFloorName('');
              setFloorNumber(0);
            }}
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: 'transparent !important',
              border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
              color: (theme.gray?.[700] || '#374151') + ' !important',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              '&:hover': {
                backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateFloor}
            variant="contained"
            size="small"
            sx={{
              backgroundColor: theme.primary + ' !important',
              border: 'none !important',
              color: 'var(--text-inverse) !important',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              boxShadow: 'none !important',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              '&:hover': {
                backgroundColor: (theme.primaryHover || theme.primary) + ' !important',
                boxShadow: 'none !important'
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openSectionDialog}
        onClose={() => setOpenSectionDialog(false)}
        PaperProps={{
          sx: {
            background: 'white',
            border: '1px solid var(--border-subtle)',
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderBottom: '1px solid var(--border-subtle)',
            padding: '16px 20px',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: 'var(--text-primary)'
          }}
        >
          Create New Section
        </DialogTitle>
        <DialogContent sx={{ padding: '20px', background: 'white' }}>
          <TextField
            fullWidth
            label="Section Name"
            value={sectionName}
            onChange={(e) => setSectionName(e.target.value)}
            margin="normal"
            placeholder="e.g., Main Dining, Private Area"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'var(--border-default)'
                }
              }
            }}
          />
          {/* Grid columns and rows hidden for future use */}
          {/* <Grid container spacing={2} sx={{ mt: 1, display: 'none' }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Grid Columns"
                value={sectionColumns}
                onChange={(e) => setSectionColumns(parseInt(e.target.value) || 4)}
                inputProps={{ min: 1, max: 8 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'var(--border-default)'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Grid Rows"
                value={sectionRows}
                onChange={(e) => setSectionRows(parseInt(e.target.value) || 4)}
                inputProps={{ min: 1, max: 10 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'var(--border-default)'
                    }
                  }
                }}
              />
            </Grid>
          </Grid> */}
        </DialogContent>
        <DialogActions sx={{ padding: '12px 20px', background: 'var(--bg-sunken)', borderTop: '1px solid var(--border-subtle)', gap: '8px', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: { xs: 'stretch', sm: 'center' }, '& > :not(:first-of-type)': { ml: { xs: 0, sm: 1 } } }}>
          <Button
            onClick={() => setOpenSectionDialog(false)}
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: 'transparent !important',
              border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
              color: (theme.gray?.[700] || '#374151') + ' !important',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              '&:hover': {
                backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateSection}
            variant="contained"
            size="small"
            sx={{
              backgroundColor: theme.primary + ' !important',
              border: 'none !important',
              color: 'var(--text-inverse) !important',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              boxShadow: 'none !important',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              '&:hover': {
                backgroundColor: (theme.primaryHover || theme.primary) + ' !important',
                boxShadow: 'none !important'
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Section Settings (UI-only) */}
      <Dialog
        open={openSectionSettings}
        onClose={() => setOpenSectionSettings(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Section Settings
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Section Name"
              value={selectedSectionForSettings?.name || ''}
              InputProps={{ readOnly: true }}
              helperText="Read-only (no backend changes)"
              fullWidth
            />
            <TextField
              label="Grid Columns"
              value={selectedSectionForSettings?.gridColumns ?? ''}
              InputProps={{ readOnly: true }}
              helperText="Current columns (read-only)"
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Tables in Section"
                value={selectedSectionForSettings?.tablesCount ?? 0}
                InputProps={{ readOnly: true }}
                fullWidth
              />
              <TextField
                label="Active Orders"
                value={selectedSectionForSettings?.ordersCount ?? 0}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Box>

            {/* Read-only info only; action and editable controls removed */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenSectionSettings(false)}
            sx={{
              backgroundColor: `${theme.button?.secondary?.transparentBg || 'rgba(29, 78, 216, 0.1)'} !important`,
              color: `${theme.button?.secondary?.text || '#1D4ED8'} !important`,
              fontWeight: 600,
              px: 3,
              py: 1.25,
              borderRadius: '10px !important',
              border: 'none !important',
              boxShadow: 'none !important',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: `${theme.button?.secondary?.hover || '#DBEAFE'} !important`,
                boxShadow: 'none !important'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openTableDialog}
        onClose={() => setOpenTableDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'white',
            border: '1px solid var(--border-subtle)',
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderBottom: '1px solid var(--border-subtle)',
            padding: '16px 20px',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: 'var(--text-primary)'
          }}
        >
          Create Tables
        </DialogTitle>
        <DialogContent sx={{ padding: '20px', background: 'white' }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Table Prefix"
                value={tablePrefix}
                onChange={(e) => setTablePrefix(e.target.value)}
                placeholder="e.g., T, Table"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'var(--border-default)'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Number of Tables"
                value={tablesToCreate === '' ? '' : tablesToCreate}
                onChange={(e) => {
                  const value = e.target.value;
                  setTablesToCreate(value === '' ? '' : value);
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value !== '') {
                    const num = parseInt(value);
                    if (!isNaN(num)) {
                      if (num < 1) {
                        setTablesToCreate(1);
                      } else if (num > 100) {
                        setTablesToCreate(100);
                      } else {
                        setTablesToCreate(num);
                      }
                    }
                  }
                }}
                placeholder="1-100"
                helperText={(() => {
                  if (tablesToCreate === '' || tablesToCreate === null) {
                    return "Required: Enter a number between 1 and 100";
                  }
                  const numTables = typeof tablesToCreate === 'string' ? parseInt(tablesToCreate) : tablesToCreate;
                  if (isNaN(numTables)) {
                    return "⚠️ Please enter a valid number";
                  }
                  if (numTables < 1 || numTables > 100) {
                    return "⚠️ Must be between 1 and 100";
                  }
                  return "Number of tables to create in this section";
                })()}
                error={(() => {
                  if (tablesToCreate === '' || tablesToCreate === null) return false;
                  const numTables = typeof tablesToCreate === 'string' ? parseInt(tablesToCreate) : tablesToCreate;
                  return isNaN(numTables) || numTables < 1 || numTables > 100;
                })()}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'var(--border-default)'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Capacity"
                value={tableCapacity === '' ? '' : tableCapacity}
                // Was `parseInt(e.target.value) || 4`. Clearing the box makes the
                // value '' for an instant; parseInt('') is NaN, and NaN || 4 is 4,
                // so the field refilled with 4 before the next keystroke landed —
                // typing "12" produced "412". Keep '' while editing and clamp on
                // blur, the same shape Number of Tables uses below.
                onChange={(e) => {
                  const value = e.target.value;
                  setTableCapacity(value === '' ? '' : value);
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setTableCapacity(4);
                    return;
                  }
                  const num = parseInt(value);
                  if (isNaN(num)) setTableCapacity(4);
                  else if (num < 1) setTableCapacity(1);
                  else if (num > 20) setTableCapacity(20);
                  else setTableCapacity(num);
                }}
                inputProps={{ min: 1, max: 20 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'var(--border-default)'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Shape</InputLabel>
                <Select
                  value={tableShape}
                  onChange={(e) => setTableShape(e.target.value as 'square' | 'round' | 'rectangle')}
                  label="Shape"
                  sx={{
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--border-default)'
                    }
                  }}
                >
                  <MenuItem value="square">Square</MenuItem>
                  <MenuItem value="round">Round</MenuItem>
                  <MenuItem value="rectangle">Rectangle</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Typography
            variant="caption"
            sx={{
              mt: 2,
              display: 'block',
              color: 'var(--text-tertiary)',
              backgroundColor: 'var(--bg-sunken)',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border-subtle)'
            }}
          >
            Tables will be created with IDs like: FLOOR-SECTION-{tablePrefix}1, FLOOR-SECTION-{tablePrefix}2, etc.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              mt: 1,
              color: 'var(--status-ok)',
              backgroundColor: 'var(--status-ok-bg)',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid var(--status-ok-border)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <QrCodeIcon style={{ width: '14px', height: '14px' }} />
            QR codes will be automatically generated for each table upon creation.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            padding: '12px 20px',
            background: 'var(--bg-sunken)',
            borderTop: '1px solid var(--border-subtle)',
            gap: '8px',
            // Mobile responsiveness: stack actions vertically on small screens
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            '& > :not(:first-of-type)': { ml: { xs: 0, sm: 1 } }
          }}
        >
          <Button
            onClick={() => setOpenTableDialog(false)}
            sx={{
              backgroundColor: `${theme.button?.utility?.bg || '#F3F4F6'} !important`,
              border: `1.5px solid ${theme.gray?.[300] || '#D1D5DB'} !important`,
              color: `${theme.button?.utility?.text || '#6b7280'} !important`,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: `${theme.button?.utility?.hover || '#E5E7EB'} !important`,
                border: `1.5px solid ${theme.gray?.[300] || '#D1D5DB'} !important`
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateTables}
            startIcon={<QrCodeIcon style={{ width: '16px', height: '16px' }} />}
            sx={{
              backgroundColor: `${theme.button?.success?.transparentBg || 'rgba(16, 185, 129, 0.13)'} !important`,
              border: `1.5px solid ${theme.success || '#10B981'} !important`,
              color: `${theme.success || '#059669'} !important`,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              position: 'relative',
              boxShadow: 'none !important',
              '& .MuiButton-startIcon': {
                // Keep text perfectly centered on mobile by not affecting flow
                mr: { xs: 0, sm: 1 },
                position: { xs: 'absolute', sm: 'static' },
                left: { xs: 12, sm: 'auto' }
              },
              '&:hover': {
                backgroundColor: 'rgba(16, 185, 129, 0.2) !important',
                border: `1.5px solid ${theme.success || '#10B981'} !important`,
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.15) !important'
              }
            }}
          >
            Create Tables & QR Codes
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Generation Dialog */}
      <Dialog
        open={openQrDialog}
        onClose={() => setOpenQrDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'white',
            border: '1px solid var(--border-subtle)',
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderBottom: '1px solid var(--border-subtle)',
            padding: '16px 20px'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              color: 'var(--text-primary)'
            }}
          >
            Generate QR Code for {selectedTableForQr?.name}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '20px', background: 'white' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="QR Code Name"
              value={qrCodeName}
              onChange={(e) => setQrCodeName(e.target.value)}
              fullWidth
              helperText="A descriptive name for this QR code"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'var(--border-default)'
                  }
                }
              }}
            />
            <TextField
              label="QR Code Data"
              value={qrCodeData}
              onChange={(e) => setQrCodeData(e.target.value)}
              fullWidth
              required
              helperText="The data to encode in the QR code (e.g., table:tableId)"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'var(--border-default)'
                  }
                }
              }}
            />
            {selectedTableForQr && (
              <Box sx={{
                p: 2,
                bgcolor: 'var(--bg-sunken)',
                borderRadius: '8px',
                border: '1px solid var(--border-subtle)'
              }}>
                <Typography variant="body2" sx={{ color: 'var(--text-tertiary)', fontWeight: 600, mb: 1 }}>
                  Table Information:
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                  Table: {selectedTableForQr.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                  ID: {selectedTableForQr.id}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '12px 20px', background: 'var(--bg-sunken)', borderTop: '1px solid var(--border-subtle)', gap: '8px', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, '& > :not(:first-of-type)': { ml: 0 } }}>
          <Button
            onClick={() => {
              setOpenQrDialog(false);
              setQrCodeName('');
              setQrCodeData('');
              setSelectedTableForQr(null);
            }}
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: 'transparent !important',
              border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
              color: (theme.gray?.[700] || '#374151') + ' !important',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              '&:hover': {
                backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateQrCode}
            variant="contained"
            size="small"
            disabled={!qrCodeData.trim()}
            sx={{
              backgroundColor: theme.primary + ' !important',
              border: 'none !important',
              color: 'var(--text-inverse) !important',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              boxShadow: 'none !important',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              '&:hover': {
                backgroundColor: (theme.primaryHover || theme.primary) + ' !important',
                boxShadow: 'none !important'
              },
              '&:disabled': {
                backgroundColor: 'var(--bg-sunken) !important',
                color: 'var(--text-tertiary) !important',
                border: '1px solid var(--border-subtle) !important'
              }
            }}
          >
            Generate QR Code
          </Button>
        </DialogActions>
      </Dialog>

      {/* Steward Order Modification Dialog */}
      {showOrderModification && selectedOrderForModification && (
        <StewardOrderModification
          open={showOrderModification}
          onClose={() => {
            setShowOrderModification(false);
            setSelectedOrderForModification(null);
            fetchFloorLayout(true); // Refresh data, preserve expanded state
          }}
          orderId={selectedOrderForModification}
          onOrderUpdated={() => {
            fetchFloorLayout(true); // Preserve expanded state
            toast.success('Order updated successfully');
          }}
        />
      )}

      {/* Vendor Order Form Modal */}
      {showOrderForm && orderTableId && orderTableName && (
        <ImprovedVendorOrderFormModal
          isOpen={showOrderForm}
          onClose={() => {
            setShowOrderForm(false);
            setOrderTableId(null);
            setOrderTableName(null);
            setResumeHeldOrder(null);
          }}
          tableId={orderTableId}
          tableName={orderTableName}
          resumeHeldOrder={resumeHeldOrder}
          onHeldOrderChange={() => { refreshHeldOrders(); setResumeHeldOrder(null); }}
          onOrderPlaced={(newOrder) => {
            console.log('[Visual2DLayoutManager] onOrderPlaced called with:', newOrder);
            console.log('[Visual2DLayoutManager] tableDataForOrder is:', tableDataForOrder);

            // Close the order form first
            setShowOrderForm(false);
            setOrderTableId(null);
            setOrderTableName(null);

            // If we have the table data and got the new order data, update the layout and reopen modal
            if (tableDataForOrder && newOrder) {
              console.log('[Visual2DLayoutManager] Both tableDataForOrder and newOrder exist, proceeding with update');

              // Add the new order to the activeOrders array and update occupied status
              const updatedTableData = {
                ...tableDataForOrder,
                activeOrders: [...(tableDataForOrder.activeOrders || []), newOrder],
                isOccupied: true,  // Mark table as occupied
                status: 'occupied'  // Update status
              };

              // Use flushSync to ensure state updates happen synchronously
              flushSync(() => {
                // Update the floor layout state with proper immutability
                setFloorLayout(prevLayout => {
                  const newLayout = JSON.parse(JSON.stringify(prevLayout)); // Deep clone
                  Object.keys(newLayout).forEach(floorKey => {
                    const floorData = newLayout[floorKey];
                    floorData.sections?.forEach((sectionData: any) => {
                      const tableIndex = sectionData.tables?.findIndex((t: any) => t.table.id === tableDataForOrder.table.id);
                      if (tableIndex !== undefined && tableIndex !== -1) {
                        sectionData.tables[tableIndex] = updatedTableData;
                      }
                    });
                  });
                  return newLayout;
                });

                // Open the table details modal with updated data
                setSelectedTable(updatedTableData);

                console.log('[Visual2DLayoutManager] State updated, opening table details modal with order:', newOrder);
              });

              // Refresh floor summaries to update the order counts at the top
              fetchFloorSummaries();

              // Clear the saved table data
              setTableDataForOrder(null);
            } else {
              console.warn('[Visual2DLayoutManager] Cannot update table details:', {
                hasTableDataForOrder: !!tableDataForOrder,
                hasNewOrder: !!newOrder,
                tableDataForOrder,
                newOrder
              });

              // Fallback: refresh the layout to show the new order
              console.log('[Visual2DLayoutManager] Falling back to fetchFloorLayout');
              fetchFloorLayout(true);
              fetchFloorSummaries();  // Also refresh summaries
              setTableDataForOrder(null);
            }
          }}
        />
      )}

      {/* Held Orders Picker */}
      {showHeldOrdersPicker && (
        <React.Suspense fallback={null}>
          <LazyHeldOrdersPicker
            heldOrders={heldOrders}
            onResume={(order) => {
              setShowHeldOrdersPicker(false);
              setResumeHeldOrder(order);
              setOrderTableId(order.tableId !== null ? String(order.tableId) : 'TAKEAWAY');
              setOrderTableName(order.tableName);
              setShowOrderForm(true);
            }}
            onDelete={(id) => {
              deleteHeldOrderFromStore(id);
              refreshHeldOrders();
            }}
            onClose={() => setShowHeldOrdersPicker(false)}
          />
        </React.Suspense>
      )}

      {/* Invoice Modal — only render when open to avoid wasted re-renders */}
      {showInvoiceModal && (
        <InvoiceModal
          isOpen={showInvoiceModal}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedOrderForInvoice(null);
            if (pendingReturnTable) {
              setSelectedTable(pendingReturnTable);
              setPendingReturnTable(null);
            }
          }}
          orderId={selectedOrderForInvoice}
          orderDisplayId={(selectedTable?.activeOrders?.find(order => order.id === selectedOrderForInvoice) as any)?.customOrderId ||
            (selectedTable?.completedOrders?.find(order => order.id === selectedOrderForInvoice) as any)?.customOrderId}
          tableName={(() => {
            const tbl = pendingReturnTable || selectedTable;
            const name = tbl?.table?.displayNumber || tbl?.table?.name;
            if (!name) return undefined;
            const groupInfo = tableGroupMap.get(String(tbl?.table?.id));
            return groupInfo ? `${name} (Merged: ${groupInfo.tableNames.join(', ')})` : name;
          })()}
        />
      )}

      {/* Confirm order action dialog */}
      <Dialog open={confirmOrderAction.open} onClose={() => setConfirmOrderAction({ open: false, type: null, orderId: null })}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {confirmOrderAction.type === 'cancel' ? 'Cancel Order' : 'Mark as Served'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.95rem' }}>
            {confirmOrderAction.type === 'cancel'
              ? 'Are you sure you want to cancel this order?'
              : 'Are you sure you want to mark this order as served?'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 1, flexDirection: { xs: 'row' }, justifyContent: 'flex-end', px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmOrderAction({ open: false, type: null, orderId: null })}
            size="small"
            sx={{
              px: 2.5,
              py: 0.75,
              minWidth: 90,
              backgroundColor: `${theme.primary}22 !important`,
              color: `${theme.primary} !important`,
              border: `1px solid ${theme.primary} !important`,
              boxShadow: 'none !important',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: `${theme.primary}44 !important`
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={executeConfirmedOrderAction}
            size="small"
            sx={{
              px: 2.75,
              py: 0.75,
              minWidth: 90,
              backgroundColor: `${theme.accent}22 !important`,
              color: `${theme.accent} !important`,
              border: `1px solid ${theme.accent} !important`,
              boxShadow: 'none !important',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: `${theme.accent}44 !important`
              }
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global Backdrop during settlement */}
      <Backdrop
        open={isSettlingBill}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 2,
          backgroundColor: 'rgba(0,0,0,0.25)'
        }}
      >
        <Paper elevation={3} sx={{ p: 2.5, px: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Preparing invoice…</Typography>
          </Box>
        </Paper>
      </Backdrop>

      {/* Edit Floor Dialog */}
      <Dialog
        open={openEditFloorDialog}
        onClose={() => {
          setOpenEditFloorDialog(false);
          setEditingFloor(null);
          setFloorName('');
          setFloorNumber(0);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderBottom: '1px solid var(--border-subtle)',
            fontWeight: 700,
            fontSize: '1.25rem',
            color: 'var(--text-primary)'
          }}
        >
          Edit Floor
        </DialogTitle>
        <DialogContent sx={{ padding: '20px', background: 'white' }}>
          <TextField
            fullWidth
            label="Floor Name"
            value={floorName}
            onChange={(e) => setFloorName(e.target.value)}
            variant="outlined"
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Floor Number (Optional)"
            type="number"
            value={floorNumber || ''}
            onChange={(e) => setFloorNumber(parseInt(e.target.value) || 0)}
            variant="outlined"
            helperText="Leave blank or 0 if not applicable"
          />
        </DialogContent>
        <DialogActions sx={{ padding: '12px 20px', background: 'var(--bg-sunken)', borderTop: '1px solid var(--border-subtle)', gap: '8px', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: { xs: 'stretch', sm: 'flex-end' }, '& > :not(:first-of-type)': { ml: { xs: 0, sm: 1 } } }}>
          <Button
            onClick={() => {
              setOpenEditFloorDialog(false);
              setEditingFloor(null);
              setFloorName('');
              setFloorNumber(0);
            }}
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: 'transparent !important',
              border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
              color: (theme.gray?.[700] || '#374151') + ' !important',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              '&:hover': {
                backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateFloor}
            disabled={!floorName.trim()}
            variant="contained"
            size="small"
            sx={{
              backgroundColor: theme.primary + ' !important',
              border: 'none !important',
              color: 'var(--text-inverse) !important',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              boxShadow: 'none !important',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              '&:hover': {
                backgroundColor: (theme.primaryHover || theme.primary) + ' !important',
                boxShadow: 'none !important'
              },
              '&:disabled': {
                backgroundColor: 'var(--bg-sunken) !important',
                color: 'var(--text-tertiary) !important',
                border: '1px solid var(--border-subtle) !important'
              }
            }}
          >
            Update Floor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Section Dialog */}
      <Dialog
        open={openEditSectionDialog}
        onClose={() => {
          setOpenEditSectionDialog(false);
          setEditingSection(null);
          setSectionName('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderBottom: '1px solid var(--border-subtle)',
            fontWeight: 700,
            fontSize: '1.25rem',
            color: 'var(--text-primary)'
          }}
        >
          Edit Section
        </DialogTitle>
        <DialogContent sx={{ padding: '20px', background: 'white' }}>
          <TextField
            fullWidth
            label="Section Name"
            value={sectionName}
            onChange={(e) => setSectionName(e.target.value)}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '12px 20px', background: 'var(--bg-sunken)', borderTop: '1px solid var(--border-subtle)', gap: '8px', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: { xs: 'stretch', sm: 'flex-end' }, '& > :not(:first-of-type)': { ml: { xs: 0, sm: 1 } } }}>
          <Button
            onClick={() => {
              setOpenEditSectionDialog(false);
              setEditingSection(null);
              setSectionName('');
            }}
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: 'transparent !important',
              border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
              color: (theme.gray?.[700] || '#374151') + ' !important',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              '&:hover': {
                backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateSection}
            disabled={!sectionName.trim()}
            sx={{
              backgroundColor: `${theme.primary} !important`,
              color: 'var(--text-inverse) !important',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              px: 3,
              py: 1.25,
              borderRadius: '10px !important',
              justifyContent: 'center',
              border: 'none !important',
              boxShadow: 'none !important',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: `${theme.primaryHover || theme.primary} !important`,
                boxShadow: 'none !important'
              },
              '&:disabled': {
                backgroundColor: 'var(--bg-sunken) !important',
                color: 'var(--text-tertiary) !important'
              }
            }}
          >
            Update Section
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Table Dialog */}
      <Dialog
        open={openEditTableDialog}
        onClose={() => {
          setOpenEditTableDialog(false);
          setEditingTable(null);
          setTableName('');
          setTableDisplayNumber('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderBottom: '1px solid var(--border-subtle)',
            fontWeight: 700,
            fontSize: '1.25rem',
            color: 'var(--text-primary)'
          }}
        >
          Edit Table
        </DialogTitle>
        <DialogContent sx={{ padding: '20px', background: 'white' }}>
          <TextField
            fullWidth
            label="Table Name (System ID)"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            variant="outlined"
            sx={{ mt: 1 }}
            helperText="Internal identifier (e.g., 6-AC-T1)"
          />
          <TextField
            fullWidth
            label="Display Detail"
            value={tableDisplayNumber}
            onChange={(e) => setTableDisplayNumber(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
            placeholder="e.g., 1, 2, 3"
            helperText="Clean table detail shown to customers (e.g., 1, 2, 3)"
          />
        </DialogContent>
        <DialogActions sx={{ padding: '12px 20px', background: 'var(--bg-sunken)', borderTop: '1px solid var(--border-subtle)', gap: '8px', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: { xs: 'stretch', sm: 'flex-end' }, '& > :not(:first-of-type)': { ml: { xs: 0, sm: 1 } } }}>
          <Button
            onClick={() => {
              setOpenEditTableDialog(false);
              setEditingTable(null);
              setTableName('');
              setTableDisplayNumber('');
            }}
            variant="outlined"
            size="small"
            sx={{
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              backgroundColor: 'transparent !important',
              color: (theme.gray?.[700] || '#374151') + ' !important',
              border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
              minWidth: { xs: 'auto', sm: 'fit-content' },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              fontWeight: 500,
              '&:hover': {
                backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateTable}
            disabled={!tableName.trim()}
            variant="contained"
            size="small"
            sx={{
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              justifyContent: 'center',
              backgroundColor: theme.primary + ' !important',
              color: 'var(--text-inverse) !important',
              border: 'none !important',
              minWidth: { xs: 'auto', sm: 'fit-content' },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              fontWeight: 500,
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: (theme.primaryHover || theme.primary) + ' !important',
                boxShadow: 'none !important'
              },
              '&:disabled': {
                backgroundColor: 'var(--bg-sunken) !important',
                color: 'var(--text-tertiary) !important',
                border: '1px solid var(--border-subtle) !important'
              }
            }}
          >
            Update Table
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteConfirmDialog}
        onClose={() => {
          setOpenDeleteConfirmDialog(false);
          setDeletingEntity(null);
          setDeleteConfirmText('');
          setShowCascadeOption(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #fef2f2 0%, #fefefe 100%)',
            borderBottom: '1px solid #fee2e2', /* basil-todo */
            fontWeight: 600,
            fontSize: '1.125rem',
            color: 'var(--status-bad)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 2.5,
            px: 3
          }}
        >
          <WarningAmberIcon sx={{ color: 'var(--status-bad)', fontSize: '1.5rem' }} />
          Delete {deletingEntity?.type?.charAt(0).toUpperCase() + deletingEntity?.type?.slice(1)}
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', background: 'white' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" sx={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
              Are you sure you want to delete <strong>{deletingEntity?.name}</strong>?
            </Typography>

            {deletingEntity?.hasPendingOrders && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Pending Orders Found</AlertTitle>
                This {deletingEntity.type} has pending orders. Deleting it will cascade delete all related entities and orders.
              </Alert>
            )}

            {showCascadeOption && deletingEntity?.hasPendingOrders && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  To confirm cascade deletion, please type <strong>"delete"</strong> below:
                </Typography>
                <TextField
                  fullWidth
                  label='Type "delete" to confirm'
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  variant="outlined"
                  error={deleteConfirmText.length > 0 && deleteConfirmText.toLowerCase() !== 'delete'}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 20px', background: 'var(--bg-sunken)', borderTop: '1px solid var(--border-subtle)', gap: '12px', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
          <Button
            onClick={() => {
              setOpenDeleteConfirmDialog(false);
              setDeletingEntity(null);
              setDeleteConfirmText('');
              setShowCascadeOption(false);
            }}
            variant="outlined"
            sx={{
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 44,
              px: 4,
              py: 1.5,
              justifyContent: 'center',
              textTransform: 'none !important',
              fontSize: '0.875rem',
              borderRadius: '8px',
              fontWeight: '600 !important',
              borderWidth: '1.5px !important',
              borderStyle: 'solid !important',
              borderColor: 'var(--border-default) !important',
              color: 'var(--text-tertiary) !important',
              backgroundColor: 'white !important',
              boxShadow: 'none !important',
              '&:hover': {
                borderColor: 'var(--border-strong) !important',
                backgroundColor: 'var(--bg-sunken) !important',
                boxShadow: 'none !important',
                color: 'var(--text-secondary) !important'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deletingEntity?.hasPendingOrders && deleteConfirmText.toLowerCase() !== 'delete'}
            variant="contained"
            sx={{
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 44,
              px: 4,
              py: 1.5,
              justifyContent: 'center',
              textTransform: 'none !important',
              fontSize: '0.875rem',
              borderRadius: '8px',
              fontWeight: 600,
              backgroundColor: 'rgba(239, 68, 68, 0.1) !important',
              color: 'var(--status-bad) !important',
              borderWidth: '1.5px !important',
              borderStyle: 'solid !important',
              borderColor: 'rgba(239, 68, 68, 0.3) !important',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.15) !important',
                borderColor: 'rgba(239, 68, 68, 0.4) !important',
                boxShadow: 'none !important'
              },
              '&:disabled': {
                backgroundColor: 'var(--bg-sunken) !important',
                color: 'var(--text-tertiary) !important',
                borderColor: 'var(--border-subtle) !important'
              }
            }}
          >
            Delete {deletingEntity?.type?.charAt(0).toUpperCase() + deletingEntity?.type?.slice(1)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={openRestoreDialog}
        onClose={() => {
          setOpenRestoreDialog(false);
          setRestoringEntity(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)',
            borderBottom: '1px solid var(--status-ok-border)',
            fontWeight: 700,
            fontSize: '1.25rem',
            color: 'var(--status-ok)',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <ArrowPathIcon style={{ width: 24, height: 24 }} />
          Restore {restoringEntity?.type?.charAt(0).toUpperCase() + restoringEntity?.type?.slice(1)}
        </DialogTitle>
        <DialogContent sx={{ padding: '20px', background: 'white' }}>
          <Typography variant="body1">
            Are you sure you want to restore <strong>{restoringEntity?.name}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            This will make it visible and usable again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '12px 20px', background: 'var(--status-ok-bg)', borderTop: '1px solid var(--status-ok-border)', gap: '8px' }}>
          <Button
            onClick={() => {
              setOpenRestoreDialog(false);
              setRestoringEntity(null);
            }}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmRestore}
            sx={{
              backgroundColor: success,
              '&:hover': {
                backgroundColor: `${success}cc`
              }
            }}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settle Bill Confirmation Dialog */}
      <Dialog
        open={showSettleConfirmDialog}
        onClose={() => {
          setShowSettleConfirmDialog(false);
          setPendingSettleTable(null);
          setSelectedPaymentMethod('');
          setShowPaymentMethodError(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)',
            borderBottom: '1px solid var(--status-ok-border)',
            fontWeight: 700,
            fontSize: '1.25rem',
            color: 'var(--status-ok)',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <BanknotesIcon style={{ width: 24, height: 24 }} />
          Settle Bill
        </DialogTitle>
        <DialogContent sx={{ padding: '20px', background: 'white' }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to settle the bill for <strong>{pendingSettleTable?.table?.name}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            This will mark all active orders as completed and generate the final invoice.
          </Typography>

          {/* Error Alert - Shows when user tries to settle without selecting payment */}
          {showPaymentMethodError && (
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 2, 
                borderRadius: '8px',
                '& .MuiAlert-icon': { alignItems: 'center' }
              }}
              onClose={() => setShowPaymentMethodError(false)}
            >
              <AlertTitle sx={{ fontWeight: 600 }}>Payment Method Required</AlertTitle>
              Please select a payment method below before settling the bill.
            </Alert>
          )}

          {/* Payment Method Selection */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'var(--text-secondary)' }}>
            Select Payment Method
          </Typography>
          <Box 
            sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1,
              p: 1.5,
              borderRadius: '8px',
              border: '2px solid',
              borderColor: showPaymentMethodError ? '#f59e0b' : (selectedPaymentMethod ? '#10b981' : '#e5e7eb'),
              backgroundColor: showPaymentMethodError ? '#fffbeb' : (selectedPaymentMethod ? '#f0fdf4' : '#f9fafb'),
              transition: 'all 0.2s ease',
              animation: showPaymentMethodError ? 'shake 0.5s ease-in-out' : 'none',
              '@keyframes shake': {
                '0%, 100%': { transform: 'translateX(0)' },
                '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-4px)' },
                '20%, 40%, 60%, 80%': { transform: 'translateX(4px)' }
              }
            }}
          >
            {PAYMENT_METHODS.map((method) => (
              <Chip
                key={method.value}
                label={`${method.icon} ${method.label}`}
                onClick={() => {
                  setSelectedPaymentMethod(method.value);
                  setShowPaymentMethodError(false);
                }}
                variant={selectedPaymentMethod === method.value ? 'filled' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  fontWeight: selectedPaymentMethod === method.value ? 600 : 400,
                  backgroundColor: selectedPaymentMethod === method.value ? '#10b981' : 'white',
                  color: selectedPaymentMethod === method.value ? 'white' : '#374151',
                  borderColor: selectedPaymentMethod === method.value ? '#10b981' : '#d1d5db',
                  '&:hover': {
                    backgroundColor: selectedPaymentMethod === method.value ? '#059669' : '#f3f4f6',
                    transform: 'scale(1.02)'
                  },
                  transition: 'all 0.15s ease'
                }}
              />
            ))}
          </Box>
          {selectedPaymentMethod && (
            <Typography variant="caption" sx={{ color: 'var(--status-ok)', mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              ✓ {PAYMENT_METHODS.find(m => m.value === selectedPaymentMethod)?.label} selected
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '12px 20px', background: 'var(--status-ok-bg)', borderTop: '1px solid var(--status-ok-border)', gap: '8px' }}>
          <Button
            onClick={() => {
              setShowSettleConfirmDialog(false);
              setPendingSettleTable(null);
              setSelectedPaymentMethod('');
              setShowPaymentMethodError(false);
            }}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!selectedPaymentMethod) {
                setShowPaymentMethodError(true);
                return;
              }
              setShowSettleConfirmDialog(false);
              setShowPaymentMethodError(false);
              if (pendingSettleTable) {
                handleSettleBill(pendingSettleTable, selectedPaymentMethod);
              }
              setPendingSettleTable(null);
              setSelectedPaymentMethod('');
            }}
            sx={{
              backgroundColor: 'var(--action)',
              '&:hover': {
                backgroundColor: 'var(--action)'
              }
            }}
          >
            Confirm & Settle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discount Dialog */}
      <Dialog
        open={showDiscountDialog}
        onClose={() => {
          setShowDiscountDialog(false);
          setDiscountTable(null);
          setDiscountType('PERCENTAGE');
          setDiscountValue('');
          setDiscountReason('');
          setCouponCode('');
          setCurrentDiscount(null);
          setBillOffers(null);
          setBillLoyalty(null);
          setCouponCheck(null);
        }}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #ffffff 100%)',
            borderBottom: '1px solid var(--status-warn)',
            fontWeight: 700,
            fontSize: '1.25rem',
            color: 'var(--status-warn-text)',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <span style={{ fontSize: '24px' }}>%</span>
          Apply Discount - {discountTable?.table?.name}
        </DialogTitle>
        <DialogContent sx={{ padding: '20px', background: 'white' }}>
          {/* Current Discount Display */}
          {currentDiscount && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleRemoveDiscount}
                >
                  Remove
                </Button>
              }
            >
              <AlertTitle>Current Discount Applied</AlertTitle>
              {currentDiscount.type === 'PERCENTAGE' ? `${currentDiscount.value}%` : `${CURRENCY.symbol}${currentDiscount.value.toFixed(2)}`} discount
              ({CURRENCY.symbol}{currentDiscount.amount.toFixed(2)} off)
              <br />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Reason: {currentDiscount.reason}
              </Typography>
            </Alert>
          )}

          {/* An offer the restaurant is running that fits this bill. Shown above
              the manual fields because it is the answer most of the time, and
              staff should not have to work out the percentage themselves. */}
          {billOffers?.available && !currentDiscount && (
            <Box
              data-testid="bill-offer-available"
              sx={{
                mb: 2, p: 2, borderRadius: '10px',
                border: '1px solid var(--action)',
                backgroundColor: 'var(--bg-selected)',
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: 'var(--action)', fontSize: '0.9rem' }}>
                  {billOffers.names.join(' + ')}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  {CURRENCY.symbol}{billOffers.discountAmount.toFixed(2)} off this bill
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                data-testid="apply-bill-offer"
                disabled={applyingOffer}
                onClick={handleApplyOffer}
                sx={{
                  backgroundColor: 'var(--action)',
                  '&:hover': { backgroundColor: 'var(--action-hover, var(--action))' },
                  textTransform: 'none', fontWeight: 650,
                }}
              >
                {applyingOffer ? 'Applying…' : 'Apply offer'}
              </Button>
            </Box>
          )}

          {/* What this guest has built up over previous visits. Information to
              say out loud, not a button — nothing here changes the bill, and a
              visit is only counted when the bill is settled. */}
          {billLoyalty && billLoyalty.standings.length > 0 && (
            <Box
              data-testid="bill-loyalty"
              sx={{
                mb: 2, p: 2, borderRadius: '10px',
                background: 'var(--bg-sunken)',
                display: 'flex', flexDirection: 'column', gap: 1.5,
              }}
            >
              <Typography sx={{
                fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'var(--text-secondary)',
              }}>
                This guest has earned
              </Typography>

              {billLoyalty.standings.map((s) => {
                // Money is written differently in Bristol and in Hyderabad, so
                // only the spend goals go through the currency symbol.
                const show = (n: number) => s.unit === 'spend'
                  ? `${CURRENCY.symbol}${n.toFixed(2)}`
                  : `${n}`;
                const money = (n: number) => `${CURRENCY.symbol}${n.toFixed(2)}`;
                const current = billLoyalty.currentDiscount;
                const busy = claimingReward === s.promotionId;

                // Three shapes, three sentences, three things a button can do.
                const isBalance = s.kind === 'BALANCE';
                const isTier = s.kind === 'TIER';
                const worth = isBalance ? (s.balanceWorth ?? 0) : (s.rewardValue ?? 0);
                // A tier needs no button — it is already on the bill. A balance
                // is spendable whenever there is something on it.
                const givable = !isTier && worth > 0
                  && (isBalance || (s.claimable && !s.blockedReason));

                const line = isTier
                  ? (s.tierHeld ? 'Member' : `${show(s.have)} of ${show(s.target ?? 0)}`)
                  : isBalance
                    ? `${show(s.have)} ${s.unit} — worth ${money(worth)}`
                    : s.claimable
                      ? 'Reward ready'
                      : s.target === null
                        ? `${show(s.have)} ${s.unit}`
                        : `${show(s.have)} of ${show(s.target)} ${s.unit === 'spend' ? '' : s.unit}`.trim()
                          + (s.remaining && s.remaining > 0 ? ` — ${show(s.remaining)} to go` : '');

                return (
                  <Box key={s.promotionId} data-testid="loyalty-standing"
                       data-reward-id={s.promotionId}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 650, color: 'var(--text-primary)' }}>
                        {s.name}
                      </Typography>
                      {/* A STATUS, never an action.
                          This read as the thing to press: bold saffron on the
                          right, the same colour as the button below it, so the
                          eye goes there first and clicking does nothing.
                          Saffron is now reserved for the button, and this is
                          set as a quiet label — uppercase and letter-spaced —
                          so it reads as a state. When there is no button (a
                          tier, or a reward whose dish is missing) it keeps the
                          emphasis, because then it IS the message. */}
                      <Typography sx={{
                        fontSize: givable ? '0.7rem' : '0.8rem',
                        whiteSpace: 'nowrap',
                        textTransform: givable ? 'uppercase' : 'none',
                        letterSpacing: givable ? '0.06em' : 'normal',
                        fontWeight: givable ? 600 : ((s.claimable || isBalance || s.tierHeld) ? 700 : 400),
                        alignSelf: 'center',
                        color: givable
                          ? 'var(--text-secondary)'
                          : ((s.claimable || (isBalance && worth > 0) || s.tierHeld)
                            ? 'var(--action)' : 'var(--text-secondary)'),
                      }}>
                        {line}
                      </Typography>
                    </Box>

                    {/* A bar only where there is something to fill. A balance
                        has no finish line, and a tier already held is not a
                        journey any more. */}
                    {s.target !== null && !s.claimable && !isBalance && !s.tierHeld && (
                      <Box sx={{
                        mt: 0.75, height: 4, borderRadius: 2,
                        background: 'var(--border, rgba(0,0,0,0.12))', overflow: 'hidden',
                      }}>
                        <Box sx={{
                          width: `${s.percent}%`, height: '100%',
                          background: 'var(--action)',
                        }} />
                      </Box>
                    )}

                    {/* A tier is already on the bill — there is nothing to press
                        and nothing to decide, so say what it is doing. */}
                    {isTier && s.tierHeld && (
                      <Typography sx={{ mt: 0.5, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                        Applies to every bill on its own — nothing to press
                      </Typography>
                    )}

                    {/* Earned, but not givable on this bill as it stands. Worded
                        as the thing to DO — "add a Vegetarian Pasta and it is
                        free" — because a server who reads a refusal says nothing
                        and the guest goes home without the reward they earned. */}
                    {s.claimable && s.blockedReason && (
                      <Typography sx={{
                        mt: 0.5, fontSize: '0.78rem', fontWeight: 600,
                        color: 'var(--text-primary)',
                      }}>
                        {s.blockedReason}
                      </Typography>
                    )}

                    {/* Both figures, side by side, so the guest can choose. The
                        bill carries one discount — whichever they pick, the other
                        comes off. Nothing is swapped without them saying so. */}
                    {givable && current && (
                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                          <Typography sx={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                            On the bill now — {current.reason}
                          </Typography>
                          <Typography sx={{
                            fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums',
                            fontWeight: current.amount >= worth ? 700 : 400,
                            color: 'var(--text-secondary)',
                          }}>
                            −{money(current.amount)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                          <Typography sx={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                            {isBalance ? 'Their balance' : 'This reward'} — {isBalance ? `${show(s.have)} ${s.unit}` : s.rewardLabel}
                          </Typography>
                          <Typography sx={{
                            fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums',
                            fontWeight: worth > current.amount ? 700 : 400,
                            color: worth > current.amount ? 'var(--action)' : 'var(--text-secondary)',
                          }}>
                            −{money(worth)}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {givable && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          size="medium"
                          data-testid="give-reward"
                          disabled={busy}
                          // Wide enough to be unmistakably the thing to press.
                          // A small button beside a bold coloured status is how
                          // somebody ends up clicking the status.
                          fullWidth={!current}
                          onClick={() => (isBalance
                            ? handleSpendBalance(s.promotionId, !!current)
                            : handleGiveReward(s.promotionId, !!current))}
                          sx={{
                            backgroundColor: 'var(--action)',
                            '&:hover': { backgroundColor: 'var(--action-hover, var(--action))' },
                            textTransform: 'none', fontWeight: 650,
                          }}
                        >
                          {busy ? (isBalance ? 'Spending…' : 'Giving…')
                            : isBalance
                              ? (current ? `Spend their balance instead — ${money(worth)}`
                                         : `Spend their balance — ${money(worth)}`)
                              : (current ? `Use the reward instead — ${money(worth)}`
                                         : `Give the reward — ${money(worth)}`)}
                        </Button>
                        {current && (
                          <Button
                            variant="outlined"
                            size="small"
                            data-testid="keep-current-discount"
                            disabled={busy}
                            onClick={() => setShowDiscountDialog(false)}
                            sx={{
                              borderColor: 'var(--border, rgba(0,0,0,0.2))',
                              color: 'var(--text-secondary)',
                              textTransform: 'none', fontWeight: 600,
                            }}
                          >
                            Keep the discount
                          </Button>
                        )}
                      </Box>
                    )}

                    {/* The reassurance staff need to press the button at all. */}
                    {givable && (
                      <Typography sx={{ mt: 0.5, fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        {isBalance
                          ? (current
                            ? 'Only as much as this bill can take; the rest stays theirs. Either choice can be undone.'
                            : 'Only as much as this bill can take — the rest stays on their balance.')
                          : current
                            ? 'The guest keeps their card if they’d rather save it. Either choice can be undone.'
                            : 'Removing the discount later puts the visits back on their card.'}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Total Amount Display */}
          <Box sx={{
            background: 'var(--bg-sunken)',
            p: 2,
            borderRadius: '8px',
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Table Total:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>
              {CURRENCY.symbol}{(discountTable?.totalAmount || 0).toFixed(2)}
            </Typography>
          </Box>

          {/* Discount Type Selection */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'var(--text-secondary)' }}>
            Discount Type
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {DISCOUNT_TYPES.map((type) => (
              <Chip
                key={type.value}
                label={`${type.icon} ${type.label}`}
                onClick={() => {
                  setDiscountType(type.value as 'PERCENTAGE' | 'FIXED' | 'COUPON');
                  setDiscountValue('');
                  setCouponCode('');
                }}
                variant={discountType === type.value ? 'filled' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  fontWeight: discountType === type.value ? 600 : 400,
                  backgroundColor: discountType === type.value ? '#f59e0b' : 'transparent',
                  color: discountType === type.value ? 'white' : '#374151',
                  borderColor: discountType === type.value ? '#f59e0b' : '#d1d5db',
                  '&:hover': {
                    backgroundColor: discountType === type.value ? '#d97706' : '#f3f4f6'
                  }
                }}
              />
            ))}
          </Box>

          {/* Coupon Code Field (only for COUPON type) */}
          {discountType === 'COUPON' && (
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Coupon Code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code"
                inputProps={{ style: { textTransform: 'uppercase' } }}
                helperText={checkingCoupon ? 'Checking…' : undefined}
              />

              {/* What the server says this code is worth. Staff never type the
                  amount for a real coupon — the offer decides it. */}
              {couponCheck?.valid && (
                <Box
                  data-testid="coupon-valid"
                  sx={{
                    mt: 1.5, p: 1.75, borderRadius: '10px',
                    border: '1px solid var(--action)',
                    backgroundColor: 'var(--bg-selected)',
                    display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: 'var(--action)', fontSize: '0.9rem' }}>
                      {couponCheck.offerName}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {CURRENCY.symbol}{couponCheck.discountAmount.toFixed(2)} off this bill
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    data-testid="apply-coupon"
                    disabled={applyingCoupon}
                    onClick={handleApplyCoupon}
                    sx={{
                      backgroundColor: 'var(--action)',
                      textTransform: 'none', fontWeight: 650,
                    }}
                  >
                    {applyingCoupon ? 'Applying…' : 'Apply coupon'}
                  </Button>
                </Box>
              )}

              {couponCheck && !couponCheck.valid && (
                <Typography
                  data-testid="coupon-invalid"
                  sx={{ mt: 1, fontSize: '0.82rem', color: 'var(--status-error-text, #b3261e)' }}
                >
                  {couponCheck.message}
                </Typography>
              )}
            </Box>
          )}

          {/* Discount Value Field */}
          <TextField
            fullWidth
            label={discountType === 'PERCENTAGE' ? 'Discount Percentage' : 'Discount Amount'}
            value={discountValue}
            onChange={(e) => {
              const val = e.target.value;
              // Only allow numbers and decimal point
              if (/^\d*\.?\d*$/.test(val)) {
                // For percentage, cap at 100
                if (discountType === 'PERCENTAGE' && parseFloat(val) > 100) {
                  setDiscountValue('100');
                } else {
                  setDiscountValue(val);
                }
              }
            }}
            type="text"
            inputMode="decimal"
            sx={{ mb: 2 }}
            placeholder={discountType === 'PERCENTAGE' ? 'Enter percentage (0-100)' : 'Enter amount'}
            InputProps={{
              startAdornment: discountType !== 'PERCENTAGE' ? (
                <Typography sx={{ mr: 1, color: 'text.secondary' }}>{CURRENCY.symbol}</Typography>
              ) : undefined,
              endAdornment: discountType === 'PERCENTAGE' ? (
                <Typography sx={{ ml: 1, color: 'text.secondary' }}>%</Typography>
              ) : undefined
            }}
            error={discountType === 'PERCENTAGE' && parseFloat(discountValue) > 100}
            helperText={discountType === 'PERCENTAGE' && parseFloat(discountValue) > 100 ? 'Percentage cannot exceed 100%' : ''}
          />

          {/* Discount Preview — server-computed, identical to the final bill */}
          {discountValue && parseFloat(discountValue) > 0 && discountPreview && (
            <Box sx={{
              background: 'var(--status-warn-bg)',
              p: 2,
              borderRadius: '8px',
              mb: 2,
              border: '1px solid var(--status-warn)'
            }}>
              {([
                ['Items subtotal', discountPreview.subtotal, false],
                [`Discount${discountType === 'PERCENTAGE' ? ` (${discountValue}%)` : ''}`, -discountPreview.sessionDiscountAmount, false],
              ] as [string, number, boolean][])
                .concat(discountPreview.vatAmount == null
                  ? ([
                      ...(discountPreview.cgstAmount > 0 ? [[`CGST`, discountPreview.cgstAmount, false]] : []),
                      ...(discountPreview.sgstAmount > 0 ? [[`SGST`, discountPreview.sgstAmount, false]] : []),
                      ...(discountPreview.igstAmount > 0 ? [[`IGST`, discountPreview.igstAmount, false]] : []),
                    ] as [string, number, boolean][])
                  : [])
                .concat(discountPreview.serviceChargeAmount > 0
                  ? ([[`Service charge`, discountPreview.serviceChargeAmount, false]] as [string, number, boolean][])
                  : [])
                .concat(discountPreview.roundOff !== 0
                  ? ([[`Round off`, discountPreview.roundOff, false]] as [string, number, boolean][])
                  : [])
                .map(([label, amount]) => (
                  <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: (amount as number) < 0 ? '#d97706' : 'text.primary' }}>
                      {(amount as number) < 0 ? '-' : ''}{CURRENCY.symbol}{Math.abs(amount as number).toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px dashed var(--status-warn)' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Final Amount{discountPreview.vatAmount != null ? ` (incl. VAT ${CURRENCY.symbol}${discountPreview.vatAmount.toFixed(2)})` : ''}:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--status-ok)' }}>
                  {CURRENCY.symbol}{discountPreview.finalAmount.toFixed(2)}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                Exactly as the final bill will show — discount on items, taxes on the discounted amount.
              </Typography>
            </Box>
          )}

          {/* Mandatory Reason Field */}
          <TextField
            fullWidth
            label="Reason for Discount *"
            value={discountReason}
            onChange={(e) => setDiscountReason(e.target.value)}
            multiline
            rows={2}
            required
            placeholder="Enter the reason for applying this discount (mandatory)"
            error={discountReason.trim() === '' && discountValue !== ''}
            helperText="Reason is mandatory for all discounts"
          />
        </DialogContent>
        <DialogActions sx={{
          padding: '12px 20px', background: 'var(--status-warn-bg)',
          borderTop: '1px solid var(--status-warn)', gap: '8px',
          display: 'flex', flexWrap: 'wrap',
        }}>
          {/* Why the button below cannot be pressed. Without this it is simply
              inert: you press it, nothing happens, and nothing says why. */}
          {manualDiscountBlockers() && (
            <Typography
              data-testid="discount-blockers"
              sx={{ flex: 1, minWidth: 200, fontSize: '0.8rem', color: 'var(--text-secondary)' }}
            >
              {manualDiscountBlockers()}
            </Typography>
          )}
          <Button
            onClick={() => {
              setShowDiscountDialog(false);
              setDiscountTable(null);
              setDiscountType('PERCENTAGE');
              setDiscountValue('');
              setDiscountReason('');
              setCouponCode('');
              setCurrentDiscount(null);
            }}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyDiscount}
            disabled={
              isApplyingDiscount ||
              !discountValue ||
              parseFloat(discountValue) <= 0 ||
              discountReason.trim() === '' ||
              (discountType === 'COUPON' && !couponCode.trim())
            }
            sx={{
              backgroundColor: 'var(--status-warn)',
              '&:hover': {
                backgroundColor: 'var(--status-warn-text)'
              },
              // Muted, so a button that cannot be pressed does not look
              // identical to one that can. The previous #fcd34d was all but
              // indistinguishable from the enabled colour, which is why pressing
              // it and getting nothing was so confusing.
              '&.Mui-disabled': {
                backgroundColor: 'var(--bg-sunken)',
                color: 'var(--text-tertiary)'
              }
            }}
          >
            {isApplyingDiscount ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                Applying...
              </>
            ) : (
              'Apply Discount'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table Transfer Dialog - Now just navigates to table layout */}
      <TableTransferDialog
        open={showTransferDialog}
        onClose={() => {
          setShowTransferDialog(false);
          setTransferSourceTable(null);
        }}
        sourceTable={transferSourceTable || { id: '', name: '' }}
      />

      {/* Transfer Confirmation Dialog - Shown when selecting target table in transfer mode */}
      <Dialog
        open={transferConfirmation?.show || false}
        onClose={() => !isTransferring && setTransferConfirmation(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            color: 'white',
            fontWeight: 700,
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2
          }}
        >
          <ArrowsRightLeftIcon style={{ width: 24, height: 24 }} />
          Confirm Transfer
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Transfer <strong>{transferMode?.activeOrderCount || 0} order(s)</strong> from:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              mb: 2,
              py: 2,
              px: 3,
              backgroundColor: 'var(--bg-sunken)',
              borderRadius: '12px',
              border: '1px solid var(--border-subtle)'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">From</Typography>
              <Typography variant="h6" fontWeight={700} color="primary">
                {transferMode?.sourceTableName || 'Unknown'}
              </Typography>
            </Box>
            <ArrowsRightLeftIcon style={{ width: 24, height: 24, color: 'var(--text-tertiary)' }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">To</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: 'var(--status-ok)' }}>
                {transferConfirmation?.targetTableName || 'Unknown'}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            This action will move all active orders to the new table.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setTransferConfirmation(null)}
            disabled={isTransferring}
            sx={{
              color: 'var(--text-tertiary)',
              fontWeight: 500,
              textTransform: 'none',
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmedTransfer}
            disabled={isTransferring}
            sx={{
              backgroundColor: 'var(--action) !important',
              color: 'white !important',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              minWidth: 120,
              '&:hover': {
                backgroundColor: 'var(--action) !important'
              },
              '&.Mui-disabled': {
                backgroundColor: '#93c5fd !important', /* basil-todo */
                color: 'white !important'
              }
            }}
          >
            {isTransferring ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1, color: 'white' }} />
                Transferring...
              </>
            ) : (
              'Confirm Transfer'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reservation Occupied Table Confirmation Dialog */}
      <Dialog
        open={!!reservationOccupiedConfirm}
        onClose={() => setReservationOccupiedConfirm(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            color: 'white', fontWeight: 700, fontSize: '1.1rem',
            display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
          }}
        >
          Table Currently In Use
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            <strong>{reservationOccupiedConfirm?.tableName}</strong> is currently occupied with active orders.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can still reserve it — the current guests are expected to leave before the reservation time. Proceed?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setReservationOccupiedConfirm(null)}
            sx={{ color: 'var(--text-tertiary)', fontWeight: 500, textTransform: 'none', px: 3 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (reservationOccupiedConfirm) {
                // Use the inline helper from handleTableClick context — replicate selection logic
                setReservationMode(prev => {
                  if (!prev) return prev;
                  const next = new Set(prev.selectedTableIds);
                  if (!prev.needsMerge && next.size > 0) {
                    next.clear();
                  }
                  next.add(reservationOccupiedConfirm.tableId);
                  return { ...prev, selectedTableIds: next };
                });
              }
              setReservationOccupiedConfirm(null);
            }}
            sx={{
              backgroundColor: 'var(--action) !important', color: 'white !important',
              fontWeight: 600, textTransform: 'none', px: 3,
              '&:hover': { backgroundColor: 'var(--action) !important' },
            }}
          >
            Reserve Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* TableMergeDialog removed — merge now uses floor-plan selection mode */}

      {/* Split Bill Dialog — only mount when needed */}
      {showSplitBillDialog && <SplitBillDialog
        open={showSplitBillDialog}
        onClose={() => { setShowSplitBillDialog(false); setSplitBillData(null); }}
        tableName={splitBillData?.tableName || ''}
        totalAmount={splitBillData?.totalAmount || 0}
        orderIds={splitBillData?.orderIds || []}
        onSettleComplete={(result) => {
          if (result.fullyPaid) {
            // Refresh floor layout
            cachedFloorData.clear();
            fetchFloorSummaries();
          }
        }}
      />}

      {/* Loading Overlay - Shown during transfer to block clicks */}
      {isTransferring && transferMode?.active && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)'
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Transferring Orders...
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
            Please wait while orders are being transferred
          </Typography>
        </Box>
      )}

      {/* Transfer Instruction Popup - Fades away after entering transfer mode */}
      <AnimatePresence>
        {showTransferInstruction && transferMode?.active && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1500,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                borderRadius: '20px',
                padding: { xs: '24px 24px', sm: '32px 48px' },
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
                textAlign: 'center',
                maxWidth: '360px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}
              >
                <TableCellsIcon style={{ width: 36, height: 36, color: 'white' }} />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  mb: 1
                }}
              >
                Select Target Table
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  mb: 2
                }}
              >
                Tap on any table to transfer {transferMode?.activeOrderCount || 0} order(s)
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  borderRadius: '12px',
                  padding: '8px 16px'
                }}
              >
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  From:
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                  {transferMode?.sourceTableName}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog
        open={openPermanentDeleteDialog}
        onClose={() => {
          setOpenPermanentDeleteDialog(false);
          setPermanentDeletingEntity(null);
          setPermanentDeleteConfirmText('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
            borderBottom: '1px solid var(--status-bad)',
            fontWeight: 700,
            fontSize: '1.25rem',
            color: 'var(--text-inverse)',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <WarningAmberIcon sx={{ color: '#fef2f2' }} /> {/* basil-todo */}
          Permanently Delete {permanentDeletingEntity?.type?.charAt(0).toUpperCase() + permanentDeletingEntity?.type?.slice(1)}
        </DialogTitle>
        <DialogContent sx={{ padding: '20px', background: 'white' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="error" sx={{ mb: 1 }}>
              <AlertTitle>⚠️ This action is irreversible!</AlertTitle>
              Permanently deleting <strong>{permanentDeletingEntity?.name}</strong> cannot be undone.
              All data will be lost forever.
            </Alert>

            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              To confirm permanent deletion, please type <strong>"delete"</strong> below:
            </Typography>
            <TextField
              fullWidth
              label='Type "delete" to confirm'
              value={permanentDeleteConfirmText}
              onChange={(e) => setPermanentDeleteConfirmText(e.target.value)}
              variant="outlined"
              error={permanentDeleteConfirmText.length > 0 && permanentDeleteConfirmText.toLowerCase() !== 'delete'}
              helperText={permanentDeleteConfirmText.length > 0 && permanentDeleteConfirmText.toLowerCase() !== 'delete' ? 'Please type "delete" exactly' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '12px 20px', background: 'var(--status-bad-bg)', borderTop: '1px solid var(--status-bad)', gap: '8px' }}>
          <Button
            onClick={() => {
              setOpenPermanentDeleteDialog(false);
              setPermanentDeletingEntity(null);
              setPermanentDeleteConfirmText('');
            }}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmPermanentDelete}
            disabled={permanentDeleteConfirmText.toLowerCase() !== 'delete'}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Missing QR Code Dialog */}
      <Dialog
        open={showQrMissingDialog}
        onClose={() => {
          setShowQrMissingDialog(false);
          setMissingQrDetails(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon sx={{ color: 'warning.main' }} />
            QR Code Not Found
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              The QR code <strong>{missingQrDetails?.code}</strong> for {missingQrDetails?.table?.name || 'this table'} could not be found.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This can happen if:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                The QR code was deleted from the system
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                The QR code reference exists but the image wasn't generated
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                There was a synchronization issue
              </Typography>
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>How to fix this</AlertTitle>
              You can generate a new QR code for this table by clicking the button below.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: { xs: 'stretch', sm: 'flex-end' }, '& > :not(:first-of-type)': { ml: { xs: 0, sm: 1 } } }}>
          <Button
            onClick={() => {
              setShowQrMissingDialog(false);
              setMissingQrDetails(null);
            }}
            color="inherit"
            sx={{ whiteSpace: 'nowrap', width: { xs: '100%', sm: 'auto' }, minHeight: 48, justifyContent: 'center' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setShowQrMissingDialog(false);
              if (missingQrDetails?.table) {
                handleOpenQrDialog(missingQrDetails.table);
              }
              setMissingQrDetails(null);
            }}
            disabled={!missingQrDetails?.table}
            sx={{ whiteSpace: 'nowrap', width: { xs: '100%', sm: 'auto' }, minHeight: 48, justifyContent: 'center' }}
          >
            Generate New QR Code
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fancy QR Code Modal */}
      <FancyQrCodeModal
        isOpen={showFancyQrModal}
        onClose={() => setShowFancyQrModal(false)}
        qrUrl={selectedTable?.table?.qrCode?.code ? `${window.location.origin}/menu?qr=${selectedTable.table.qrCode.code}` : ''}
        qrName={`QR Code - ${selectedTable?.table?.displayNumber || selectedTable?.table?.name || 'Table'}`}
        vendorName={user?.username || 'Vendor'}
        tableName={selectedTable?.table?.displayNumber || selectedTable?.table?.name || 'Table'}
        floorName={selectedTable?.floorName}
        sectionName={selectedTable?.sectionName}
      />

      {/* Quick Reservation Modal from Floor Plan */}
      {showReservationModal && (
        <ReservationProvider>
          <ReservationModal
            isOpen={showReservationModal}
            onClose={() => {
              setShowReservationModal(false);
              setReservationTableId(undefined);
              fetchFloorLayout(true);
            }}
            preselectedTableId={reservationTableId}
          />
        </ReservationProvider>
      )}

    </Box>
  );
};

export default Visual2DLayoutManager;